export const BLOG_ROOT_PATH = "/blog";
export const BLOG_SITE_TITLE = "Latted Blog";
export const BLOG_SITE_DESCRIPTION =
  "Practical AI video tutorials, creator reviews, and workflow notes for teams building with modern models on Latted.";

const LATTED_TEAM = "Latted Team";
const DEFAULT_WORKFLOW_COVER = {
  coverImagePath: "/blog/covers/latted-workflows-light.svg",
  coverImageDarkPath: "/blog/covers/latted-workflows-dark.svg",
  coverImageAlt:
    "Abstract timeline-inspired cover art with layered tracks and workflow blocks",
};

function createBlogPost(post) {
  return {
    authorName: LATTED_TEAM,
    readingTimeMinutes: 7,
    updatedAt: post.publishedAt,
    tags: [],
    keywords: [],
    faqItems: [],
    ...DEFAULT_WORKFLOW_COVER,
    ...post,
  };
}

export const BLOG_POSTS = [
  createBlogPost({
    slug: "create-reusable-ai-video-characters-from-name-look-and-personality",
    title:
      "How to Create Reusable AI Video Characters From a Name, Look, and Personality",
    seoTitle:
      "How to Create Reusable AI Video Characters From a Name, Look, and Personality in 2026",
    description:
      "Learn how Latted's character workflow turns a name, physical appearance, and personality into a reusable AI-video persona that can be shared across future projects and automations.",
    excerpt:
      "A practical guide to creating reusable character records instead of rebuilding the same persona in every prompt.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 7,
    tags: ["Characters", "AI Video", "Latted", "Teams", "Workflow"],
    keywords: [
      "create AI video character",
      "reusable character workflow",
      "Latted characters",
      "AI character appearance and personality",
      "shared video personas",
    ],
    mdxPublicPath:
      "/blog/create-reusable-ai-video-characters-from-name-look-and-personality.mdx",
    llmSummary:
      "Explains how Latted lets teams create reusable character records from a name, appearance, and personality so recurring personas stay consistent across future work.",
    faqItems: [
      {
        question: "What makes a character reusable in an AI-video workflow?",
        answer:
          "A character becomes reusable when its key identity details are saved in a structured record instead of being rewritten in every new prompt.",
      },
      {
        question: "What should go in the appearance field?",
        answer:
          "The appearance field should capture the visual traits that need to stay stable, such as clothing, hair, age range, and other recognizable features.",
      },
      {
        question: "What should go in the personality field?",
        answer:
          "The personality field should describe the tone, energy, and behavioral traits that affect how the persona should feel on screen.",
      },
      {
        question: "Do I need a final image before creating the character?",
        answer:
          "No. In Latted you can create the character record from descriptive fields first and refine or attach stronger visual references later.",
      },
      {
        question: "Where do I create reusable characters in Latted?",
        answer:
          "You create them from Latted's characters workflow, where the character record starts with a name, appearance, and personality.",
      },
    ],
  }),
  createBlogPost({
    slug: "add-your-own-script-after-ai-ugc-idea-generation",
    title: "Why You Should Add Your Own Script After AI UGC Idea Generation",
    seoTitle:
      "Why You Should Add Your Own Script After AI UGC Idea Generation in 2026",
    description:
      "Learn how Latted's UGC workflow lets teams keep AI-generated ideas and also add a custom title and script when a stronger human-written ad direction deserves to enter the same review flow.",
    excerpt:
      "A practical guide to combining AI ideation breadth with human editorial control in one UGC workflow.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 6,
    tags: ["UGC Ads", "Ideation", "AI Video", "Latted", "Workflow"],
    keywords: [
      "custom UGC script after ideation",
      "AI UGC ideas plus custom script",
      "Latted UGC workflow",
      "add your own ad script",
      "UGC creative testing workflow",
    ],
    mdxPublicPath: "/blog/add-your-own-script-after-ai-ugc-idea-generation.mdx",
    llmSummary:
      "Explains how Latted lets teams add a custom script after AI UGC ideation so human-written campaign direction can be compared beside generated concepts.",
    faqItems: [
      {
        question: "Why add a custom script after AI ideation instead of before?",
        answer:
          "Because seeing the generated option set first makes it easier to decide whether a human-written direction should enter the same test set.",
      },
      {
        question: "Does a custom script replace the generated ideas?",
        answer:
          "No. It complements them by letting the team compare deliberate human direction beside AI-generated concepts.",
      },
      {
        question: "When is a custom script better than an AI-generated one?",
        answer:
          "It is usually better when the campaign has strict messaging needs, legal constraints, or a proven hook that should not depend on model variation.",
      },
      {
        question: "Is this only useful for large marketing teams?",
        answer:
          "No. Founders and solo creators benefit too whenever they want AI breadth while still testing one specific script they already believe in.",
      },
      {
        question: "Where do I add my own script in Latted?",
        answer:
          "You add it in Latted's UGC workflow after the initial idea set is generated so it can enter the same review and generation flow.",
      },
    ],
  }),
  createBlogPost({
    slug: "resume-ugc-generation-after-sign-in-without-losing-selected-ideas",
    title:
      "Why UGC Generation Should Resume After Sign-In Without Losing Your Selected Ideas",
    seoTitle:
      "Why UGC Generation Should Resume After Sign-In Without Losing Selected Ideas in 2026",
    description:
      "See why Latted preserves selected UGC ideas through authentication so users can move from ideation to generation without rebuilding their choices after sign-in.",
    excerpt:
      "A practical guide to auth-aware continuation for ideation-first UGC workflows.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 6,
    tags: ["UGC Ads", "Authentication", "AI Video", "Latted", "Workflow"],
    keywords: [
      "resume UGC generation after sign in",
      "auth aware UGC workflow",
      "selected ideas after login",
      "Latted UGC sign in flow",
      "UGC idea selection continuity",
    ],
    mdxPublicPath:
      "/blog/resume-ugc-generation-after-sign-in-without-losing-selected-ideas.mdx",
    llmSummary:
      "Explains how Latted preserves selected UGC ideas through authentication so users can sign in and continue generation without redoing the selection step.",
    faqItems: [
      {
        question: "What is auth-aware continuation in a UGC workflow?",
        answer:
          "It means the product preserves the selected ideas and intended action through sign-in so generation can continue afterward without rebuilding the choices.",
      },
      {
        question: "Why does selected-idea state matter so much?",
        answer:
          "Because selecting ideas is the point where editorial judgment turns a brainstorm into a production decision, so losing that state means losing real work.",
      },
      {
        question: "Should authentication happen earlier instead?",
        answer:
          "Not always. Letting users explore first can reduce friction as long as the product preserves the work they already did when sign-in becomes necessary.",
      },
      {
        question: "Is this only useful when several ideas are selected?",
        answer:
          "No. It helps even with one selected idea because any broken handoff between decision and execution weakens trust in the workflow.",
      },
      {
        question: "Where does this matter in Latted?",
        answer:
          "It matters in Latted's UGC workflow, where users can review several candidate ideas and then move selected concepts into generated projects.",
      },
    ],
  }),
  createBlogPost({
    slug: "resume-studio-video-submission-after-sign-in-without-losing-your-recording",
    title:
      "Why Studio Video Submission Should Resume After Sign-In Without Losing Your Recording",
    seoTitle:
      "Why Studio Video Submission Should Resume After Sign-In Without Losing Your Recording in 2026",
    description:
      "Learn how Latted preserves an uploaded or recorded talking-head clip and its style prompt through authentication so Studio submissions can continue without re-recording the take.",
    excerpt:
      "A practical guide to auth-aware continuity in talking-head and webcam-based AI-video workflows.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 6,
    tags: ["Studio Workflow", "Authentication", "Talking Head", "AI Video", "Latted"],
    keywords: [
      "resume studio submission after sign in",
      "talking head recording after login",
      "Latted studio auth workflow",
      "preserve recorded video through sign in",
      "auth aware media submission",
    ],
    mdxPublicPath:
      "/blog/resume-studio-video-submission-after-sign-in-without-losing-your-recording.mdx",
    llmSummary:
      "Explains how Latted preserves a recorded or uploaded Studio clip plus its style direction through sign-in so users do not lose an accepted take before submission.",
    faqItems: [
      {
        question: "Why should a Studio workflow preserve my recording through sign-in?",
        answer:
          "Because recording a usable take takes effort, and the product should protect that progress instead of forcing the user to recreate it.",
      },
      {
        question: "Is this only important for in-browser recording?",
        answer:
          "No. It matters for uploaded files too, though it feels especially important for browser-recorded footage because a live capture just happened.",
      },
      {
        question: "What else should survive sign-in besides the video file?",
        answer:
          "The chosen style direction should survive too so the transformation intent remains intact after authentication.",
      },
      {
        question: "Does preserving the take mean it submits automatically without review?",
        answer:
          "No. It means the workflow can continue from the same accepted state after sign-in instead of losing the take the user already approved.",
      },
      {
        question: "Where does this matter in Latted?",
        answer:
          "It matters in Latted's Studio workflow, where users can record or upload talking-head footage and then submit it into a styled video project.",
      },
    ],
  }),
  createBlogPost({
    slug: "try-brain-explorer-before-creating-an-account",
    title:
      "Why Letting Users Try Brain Explorer Before Sign-Up Improves Product Evaluation",
    seoTitle:
      "Why Letting Users Try Brain Explorer Before Sign-Up Improves Product Evaluation in 2026",
    description:
      "Learn why Latted's Brain Explorer is easier to evaluate when users can run one real analysis before creating an account, instead of being forced to commit before they see their own results.",
    excerpt:
      "A practical argument for value-first evaluation in AI video and text analysis products.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 6,
    tags: ["Brain Explorer", "Product Evaluation", "AI Video", "Latted", "Analysis"],
    keywords: [
      "try Brain Explorer before sign up",
      "free first analysis",
      "Latted Brain Explorer evaluation",
      "value first product onboarding",
      "analyze video before account",
    ],
    mdxPublicPath: "/blog/try-brain-explorer-before-creating-an-account.mdx",
    llmSummary:
      "Explains why Latted Brain Explorer is easier to trust when users can run one real video or text analysis before creating an account for repeated use.",
    faqItems: [
      {
        question: "Why let users try Brain Explorer before sign-up?",
        answer:
          "Because analysis products are easier to trust after one real result than before any hands-on use, and the first useful analysis proves value quickly.",
      },
      {
        question: "Does one free analysis replace account creation entirely?",
        answer:
          "No. It simply gives users enough direct product experience to decide whether continuing into sign-up is worthwhile.",
      },
      {
        question: "What should a first Brain Explorer session prove?",
        answer:
          "It should prove that the product can handle the user's input and return analysis that feels interpretable and useful for real decisions.",
      },
      {
        question: "Is this only important for new users?",
        answer:
          "It is mainly a new-user evaluation feature, but it also reflects a healthier product philosophy where value comes before commitment.",
      },
      {
        question: "Where can I try this in Latted?",
        answer:
          "You can start from Latted's Brain Explorer, which supports analysis workflows for both video and text inputs.",
      },
    ],
  }),
  createBlogPost({
    slug: "highlighted-text-playback-for-script-review-in-brain-explorer",
    title:
      "How Highlighted Text Playback Helps Teams Review Video Scripts Before Production",
    seoTitle:
      "How Highlighted Text Playback Helps Teams Review Video Scripts Before Production in 2026",
    description:
      "See how Latted Brain Explorer's highlighted text playback makes script analysis easier to interpret by connecting time-based signals to specific words and phrases before production begins.",
    excerpt:
      "A practical guide to time-aware script analysis for hooks, pacing, transitions, and CTA review.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 6,
    tags: ["Brain Explorer", "Text Analysis", "Pre-Production", "AI Video", "Latted"],
    keywords: [
      "highlighted text playback",
      "script review before production",
      "Latted Brain Explorer text analysis",
      "time aware script analysis",
      "analyze script pacing",
    ],
    mdxPublicPath:
      "/blog/highlighted-text-playback-for-script-review-in-brain-explorer.mdx",
    llmSummary:
      "Explains how Latted Brain Explorer uses highlighted text playback to connect script analysis signals to specific moments in the copy before a video is produced.",
    faqItems: [
      {
        question: "What is highlighted text playback in a script-analysis workflow?",
        answer:
          "It is a review mode where the script advances visually over time so users can connect analysis signals to specific words or phrases.",
      },
      {
        question: "Why is this useful before making the video?",
        answer:
          "Because it helps identify weak hooks, slow transitions, and unclear endings while the script is still easy to revise.",
      },
      {
        question: "Is a high-level score enough for script revision?",
        answer:
          "Usually no. A high-level score may show that something is weak, but time-aware text review helps reveal where the weakness probably begins.",
      },
      {
        question: "Who should use this kind of analysis?",
        answer:
          "Anyone writing scripts before production can benefit, especially product marketers, creators, educators, and teams comparing several drafts.",
      },
      {
        question: "Where can I use this in Latted?",
        answer:
          "You can use it in Latted's Brain Explorer, which supports text analysis as part of the broader pre-production workflow.",
      },
    ],
  }),
  createBlogPost({
    slug: "upload-a-new-reference-asset-inside-the-automation-builder",
    title: "How to Upload a New Reference Asset Without Leaving the Automation Builder",
    seoTitle:
      "How to Upload a New Reference Asset Without Leaving the Automation Builder in 2026",
    description:
      "Learn how Latted's automation builder lets teams upload a missing reference asset directly from the attachments flow and immediately use it in the same automation setup.",
    excerpt:
      "A practical guide to fixing missing automation context without abandoning the builder.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 6,
    tags: ["Automations", "Assets", "AI Video", "Latted", "Workflow"],
    keywords: [
      "upload asset inside automation builder",
      "automation attachment upload",
      "Latted automation asset upload",
      "inline asset upload workflow",
      "reference assets for automations",
    ],
    mdxPublicPath:
      "/blog/upload-a-new-reference-asset-inside-the-automation-builder.mdx",
    llmSummary:
      "Explains how Latted lets teams upload a missing reference asset directly inside the automation builder so setup can continue without switching to another workspace first.",
    faqItems: [
      {
        question: "Why upload an asset inside the automation builder instead of beforehand?",
        answer:
          "Because the need for a new reference often becomes obvious during setup, and inline upload lets the user solve that gap immediately without abandoning the automation.",
      },
      {
        question: "What kinds of assets are most useful to attach?",
        answer:
          "Product images, screenshots, logos, and other reusable visual references are usually the most valuable because they ground recurring output.",
      },
      {
        question: "Does inline upload replace a shared asset library?",
        answer:
          "No. It strengthens the shared asset system by creating the reusable asset at the moment it becomes necessary.",
      },
      {
        question: "Is this only useful for large teams?",
        answer:
          "No. Solo operators benefit too because context switching is costly even when one person owns the whole automation workflow.",
      },
      {
        question: "Where can I do this in Latted?",
        answer:
          "You can do it inside Latted's automation builder, where the attachments flow supports asset selection as part of automation setup.",
      },
    ],
  }),
  createBlogPost({
    slug: "search-assets-and-characters-before-running-a-latted-automation",
    title:
      "Why Searchable Asset and Character Pickers Matter Before You Run an Automation",
    seoTitle:
      "Why Searchable Asset and Character Pickers Matter Before You Run an Automation in 2026",
    description:
      "Learn how Latted's searchable automation pickers help teams find the right assets and characters before ideation or generation runs begin.",
    excerpt:
      "A practical guide to faster, more accurate automation setup as reusable libraries grow.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 6,
    tags: ["Automations", "Assets", "Characters", "AI Video", "Latted"],
    keywords: [
      "search asset picker automation",
      "search character picker automation",
      "Latted automation attachments",
      "automation setup with reusable references",
      "searchable automation picker",
    ],
    mdxPublicPath:
      "/blog/search-assets-and-characters-before-running-a-latted-automation.mdx",
    llmSummary:
      "Explains how Latted's searchable asset and character pickers make automation setup faster and more accurate once reusable libraries begin to grow.",
    faqItems: [
      {
        question: "Why do automation attachment pickers need search?",
        answer:
          "Because reusable libraries become hard to navigate by scrolling alone, and search turns attachment from a memory test into a retrieval task.",
      },
      {
        question: "What happens when attachment search is missing?",
        answer:
          "Setup gets slower, attachment accuracy drops, and teams are more likely to skip useful references or choose the wrong ones.",
      },
      {
        question: "Does search only matter for very large libraries?",
        answer:
          "No. It becomes useful as soon as the library grows beyond what people can remember reliably.",
      },
      {
        question: "How can teams make picker search work better?",
        answer:
          "Use clear names, keep records current, and avoid unnecessary duplication so the library itself stays understandable.",
      },
      {
        question: "Where does this matter in Latted?",
        answer:
          "It matters in Latted's automation builder, where reusable assets and characters can be attached before the automation runs.",
      },
    ],
  }),
  createBlogPost({
    slug: "visible-attachment-chips-before-an-automation-ideation-run",
    title: "Why Visible Attachment Chips Matter Before an Automation Ideation Run",
    seoTitle:
      "Why Visible Attachment Chips Matter Before an Automation Ideation Run in 2026",
    description:
      "See how Latted's visible asset and character chips make automation context easier to audit before ideation begins, so operators can verify or remove attached references without reopening the modal.",
    excerpt:
      "A practical guide to making automation context inspectable before the agent starts brainstorming.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 6,
    tags: ["Automations", "Editor UX", "AI Video", "Latted", "Workflow"],
    keywords: [
      "attachment chips automation",
      "visible automation context",
      "Latted automation attachments",
      "pre run context audit",
      "asset and character chips",
    ],
    mdxPublicPath:
      "/blog/visible-attachment-chips-before-an-automation-ideation-run.mdx",
    llmSummary:
      "Explains how Latted shows attached assets and characters as visible chips below the automation prompt so operators can audit context before starting an ideation run.",
    faqItems: [
      {
        question: "What are attachment chips in an automation workflow?",
        answer:
          "They are visible labels that show which assets and characters are currently attached so the operator can review that context before starting a run.",
      },
      {
        question: "Why not keep attachment state only inside the modal?",
        answer:
          "Because the modal is mainly for selection, while visible chips make the final context easier to verify without reopening configuration.",
      },
      {
        question: "Do attachment chips improve the automation output directly?",
        answer:
          "Not by themselves. They improve setup quality by making it easier to catch wrong or missing context before the run begins.",
      },
      {
        question: "Who benefits most from visible chips?",
        answer:
          "Operators managing several recurring workflows benefit the most because they need fast confidence checks before each run.",
      },
      {
        question: "Where does this happen in Latted?",
        answer:
          "It matters in Latted's automation builder, where attached assets and characters can appear directly below the main prompt area.",
      },
    ],
  }),
  createBlogPost({
    slug: "let-ai-asset-generation-continue-in-the-background",
    title: "Why AI Asset Generation Should Keep Running After You Close the Create View",
    seoTitle:
      "Why AI Asset Generation Should Keep Running After You Close the Create View in 2026",
    description:
      "Learn why Latted lets reusable asset generation continue in the background so teams can keep moving through larger workflows instead of waiting inside a single create panel.",
    excerpt:
      "A practical guide to background asset generation for faster multi-step creative work.",
    publishedAt: "2026-04-17",
    readingTimeMinutes: 6,
    tags: ["Assets", "Background Tasks", "AI Video", "Latted", "Workflow"],
    keywords: [
      "background asset generation",
      "continue asset generation after close",
      "Latted asset background task",
      "AI asset generation workflow",
      "reusable asset creation in background",
    ],
    mdxPublicPath: "/blog/let-ai-asset-generation-continue-in-the-background.mdx",
    llmSummary:
      "Explains how Latted lets reusable asset generation continue in the background after the create view is closed so teams can keep working while the task finishes.",
    faqItems: [
      {
        question: "What does it mean for asset generation to continue in the background?",
        answer:
          "It means the asset creation task keeps running even after the active creation view is closed, so the user can keep working elsewhere while the result finishes.",
      },
      {
        question: "Why is this better than waiting in one screen?",
        answer:
          "Because waiting in one screen blocks other useful work, while background continuation keeps the workflow moving.",
      },
      {
        question: "Does background continuation mean the task becomes invisible?",
        answer:
          "It should not. Good background workflows also need clear task tracking so the user can tell the work is still running and know when it finishes.",
      },
      {
        question: "Who benefits most from background asset generation?",
        answer:
          "Teams and frequent creators benefit the most because they often manage several creative tasks at once, but it helps anyone who does not want to babysit a generation panel.",
      },
      {
        question: "Where does this matter in Latted?",
        answer:
          "It matters in Latted's assets workflow, where reusable assets can be generated for organization-wide use across projects and automations.",
      },
    ],
  }),
  createBlogPost({
    slug: "fullscreen-b-roll-overlay-clips-on-ai-video-timeline",
    title: "How Full-Screen B-Roll Overlay Clips Work on an AI Video Timeline",
    seoTitle:
      "How Full-Screen B-Roll Overlay Clips Work on an AI Video Timeline in 2026",
    description:
      "Learn how Latted's overlay clip workflow lets editors add, move, trim, and remove full-screen B-roll above the base cut without rebuilding the underlying scene.",
    excerpt:
      "A practical guide to timeline overlays for product footage, cutaways, and supporting visuals.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 6,
    tags: ["Timeline Editing", "B-Roll", "AI Video", "Latted", "Workflow"],
    keywords: [
      "overlay clips AI video",
      "B-roll overlay timeline",
      "Latted overlay clips",
      "full screen overlay editor",
      "AI video B-roll workflow",
    ],
    mdxPublicPath: "/blog/fullscreen-b-roll-overlay-clips-on-ai-video-timeline.mdx",
    llmSummary:
      "Explains how Latted uses overlay clips as editable timeline objects so teams can add, move, trim, and remove full-screen B-roll without rebuilding the base video.",
    faqItems: [
      {
        question: "What is a full-screen overlay clip in an AI-video editor?",
        answer:
          "It is a clip that temporarily sits above the base video layer so you can show another visual without rebuilding the underlying cut.",
      },
      {
        question: "Why use an overlay instead of regenerating the scene?",
        answer:
          "Because an overlay changes only the supporting visual layer, which is usually faster and safer than redoing the whole scene.",
      },
      {
        question: "Can overlay clips come from existing project videos?",
        answer:
          "Yes. In Latted, overlay clips can be created from project videos that already exist in the timeline.",
      },
      {
        question: "Can you upload your own overlay footage?",
        answer:
          "Yes. Latted supports uploading video files as overlay clips so outside footage can be added directly to the project.",
      },
      {
        question: "When should I create a new scene instead of an overlay?",
        answer:
          "Create a new scene when the story structure or pacing needs to change. Use an overlay when the base scene works and only the top visual layer needs improvement.",
      },
    ],
  }),
  createBlogPost({
    slug: "zapcap-burn-in-captions-and-style-controls-in-the-editor",
    title: "How Burn-In Captions and Style Controls Work in Latted's Video Editor",
    seoTitle:
      "How Burn-In Captions and Style Controls Work in Latted's Video Editor in 2026",
    description:
      "See how Latted lets teams add or remove burn-in captions per clip and control size, weight, position, outline, shadow, word count, and other styling details from the editor.",
    excerpt:
      "A practical guide to adding readable, brand-aware burn-in captions without leaving the video editor.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 7,
    tags: ["Captions", "Video Editing", "AI Video", "Latted", "Workflow"],
    keywords: [
      "burn in captions AI video",
      "caption style controls video editor",
      "Latted captions workflow",
      "video caption styling",
      "AI video caption editor",
    ],
    mdxPublicPath:
      "/blog/zapcap-burn-in-captions-and-style-controls-in-the-editor.mdx",
    llmSummary:
      "Shows how Latted handles burn-in captions as a per-video editor action with style controls for size, weight, position, outline, shadow, word count, and other visual settings.",
    faqItems: [
      {
        question: "What are burn-in captions?",
        answer:
          "They are captions rendered directly into the video image so viewers always see them, even when a platform does not load external subtitle files.",
      },
      {
        question: "Why use burn-in captions instead of subtitle files?",
        answer:
          "Burn-in captions keep the text visible in reposts, social feeds, and native playback environments where optional subtitle tracks may not show.",
      },
      {
        question: "What styling controls matter most first?",
        answer:
          "Start with size, contrast, position, and words displayed at once. Those settings usually matter more than decorative choices.",
      },
      {
        question: "Should every clip in a project use captions?",
        answer:
          "No. Captions are most useful on speech-led clips. B-roll, music-led moments, and some motion-graphics sequences may not need them.",
      },
      {
        question: "Where do you manage caption styling in Latted?",
        answer:
          "You manage it from the video properties workflow, where captions can be turned on or off and adjusted for each video clip.",
      },
    ],
  }),
  createBlogPost({
    slug: "extract-audio-from-ai-video-clips-into-your-timeline",
    title:
      "How to Extract Audio From a Generated Video and Keep Editing It in the Timeline",
    seoTitle:
      "How to Extract Audio From a Generated Video and Keep Editing It in the Timeline in 2026",
    description:
      "Learn why Latted's extract-audio workflow is useful when a generated clip already has usable dialogue, ambience, or music that you want to edit separately on the timeline.",
    excerpt:
      "A practical guide to turning embedded video audio into a separate timeline clip you can keep shaping.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 6,
    tags: ["Audio Workflow", "Timeline Editing", "AI Video", "Latted", "Workflow"],
    keywords: [
      "extract audio from video timeline",
      "AI video audio extraction",
      "Latted extract audio",
      "separate audio from video editor",
      "timeline audio workflow",
    ],
    mdxPublicPath: "/blog/extract-audio-from-ai-video-clips-into-your-timeline.mdx",
    llmSummary:
      "Explains how Latted can extract audio from a generated video so the sound becomes its own timeline clip that can be moved, trimmed, balanced, and reused.",
    faqItems: [
      {
        question: "What does audio extraction do in a video editor?",
        answer:
          "It pulls the sound out of a video clip and turns it into a separate timeline element that can be edited independently.",
      },
      {
        question: "Why extract audio instead of leaving it attached to the video?",
        answer:
          "Because separate audio is easier to trim, rebalance, reuse, and combine with other timeline edits than audio that remains locked inside one video clip.",
      },
      {
        question: "What kinds of sound are worth extracting?",
        answer:
          "Dialogue, ambience, music, and other useful audio already present in a generated clip are all good candidates when you want to keep them while revising the visuals.",
      },
      {
        question: "When is this especially helpful?",
        answer:
          "It is especially helpful when the video needs visual revisions but the audio track is already working well enough to preserve and refine.",
      },
      {
        question: "Where does the extracted audio go in Latted?",
        answer:
          "It becomes its own audio clip on the project timeline so it can be edited like other audio elements in the project.",
      },
    ],
  }),
  createBlogPost({
    slug: "agent-project-eligibility-and-auto-reload-gating-for-agent-flows",
    title:
      "Why Agent Project Creation Can Require Auto-Reload Settings in Latted",
    seoTitle:
      "Why Agent Project Creation Can Require Auto-Reload Settings in Latted in 2026",
    description:
      "Understand how Latted handles agent-project eligibility, why the first project can be free, and why continued agentic creation can prompt an auto-reload setup flow instead of failing mid-run.",
    excerpt:
      "A practical guide to the auto-reload setup gate that protects long-running agent projects from avoidable billing interruptions.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 6,
    tags: ["Billing", "Agent Workflow", "AI Video", "Latted", "Workflow"],
    keywords: [
      "agent auto reload gate",
      "Latted agent eligibility",
      "AI video auto reload settings",
      "agent project billing workflow",
      "auto reload for AI agents",
    ],
    mdxPublicPath:
      "/blog/agent-project-eligibility-and-auto-reload-gating-for-agent-flows.mdx",
    llmSummary:
      "Explains why Latted can require auto-reload settings before continued agent-driven project creation, including the first-free-project message, threshold and reload amount choices, and the blank-project fallback.",
    faqItems: [
      {
        question: "Why would agent project creation require auto-reload?",
        answer:
          "Because agent-driven workflows can continue generating while they run, and Latted uses auto-reload settings to reduce the chance of a long job stopping for low balance.",
      },
      {
        question: "Does Latted still let you try the agent first?",
        answer:
          "Yes. The in-app flow explicitly states that the first project is free before continued full agentic creation requires auto-reload.",
      },
      {
        question: "What settings do you choose in the auto-reload flow?",
        answer:
          "You choose a low-balance threshold and a reload amount so the account knows when and how much to top up during future agent work.",
      },
      {
        question: "Why gate the workflow before the run instead of after?",
        answer:
          "Because it is safer to set spending rules before a long-running agent job starts than to let the job fail in the middle of work.",
      },
      {
        question: "What if you do not want to enable auto-reload?",
        answer:
          "Latted also presents a blank-project path so you can continue creating without the same agent-specific billing setup.",
      },
    ],
  }),
  createBlogPost({
    slug: "drag-to-reorder-ai-video-shots-in-the-timeline",
    title: "How to Drag-to-Reorder AI Video Shots on Your Timeline",
    seoTitle:
      "How to Drag-to-Reorder AI Video Shots on Your Timeline in 2026",
    description:
      "See why persistent timeline reordering matters when the cut is good but the sequence is wrong, and how Latted keeps the new order as part of the project state.",
    excerpt:
      "A practical guide to fixing pacing and story flow by reordering shots instead of rebuilding them.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 6,
    tags: ["Timeline Editing", "Story Structure", "AI Video", "Latted", "Workflow"],
    keywords: [
      "reorder AI video timeline",
      "drag reorder video shots",
      "Latted timeline reorder",
      "AI video pacing edits",
      "video sequence editing workflow",
    ],
    mdxPublicPath: "/blog/drag-to-reorder-ai-video-shots-in-the-timeline.mdx",
    llmSummary:
      "Explains how Latted lets teams drag-to-reorder video shots on the timeline and persist that order as part of the project instead of forcing clip regeneration.",
    faqItems: [
      {
        question: "What is timeline reordering?",
        answer:
          "It is changing the playback order of clips in a project while keeping the clips themselves intact.",
      },
      {
        question: "Why reorder clips instead of regenerating them?",
        answer:
          "Because many editorial problems come from sequence, not clip quality. Reordering is often faster and less destructive than rebuilding the shots.",
      },
      {
        question: "What kinds of problems can reordering fix?",
        answer:
          "It can fix pacing, setup-before-payoff issues, confusing transitions, weak openings, and stories that reveal the right information in the wrong order.",
      },
      {
        question: "Why does persistent order matter?",
        answer:
          "Because teams need the new sequence to behave like the real project state, not a temporary preview that disappears later.",
      },
      {
        question: "When should you regenerate instead of reorder?",
        answer:
          "Regenerate when the individual shot itself is wrong. Reorder when the clip is usable but belongs in a different place in the sequence.",
      },
    ],
  }),
  createBlogPost({
    slug: "per-video-type-generation-drafts-in-the-ai-video-editor",
    title: "Why Per-Video-Type Draft State Makes an AI Video Editor Faster to Use",
    seoTitle:
      "Why Per-Video-Type Draft State Makes an AI Video Editor Faster to Use in 2026",
    description:
      "Learn how Latted preserves draft inputs across text-to-video, image-to-video, transition, reference-to-video, motion-control, video-to-video, and avatar workflows so mode switches do not wipe your work.",
    excerpt:
      "A practical guide to draft persistence across several AI video generation modes inside one editor.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 7,
    tags: ["Editor UX", "Draft State", "AI Video", "Latted", "Workflow"],
    keywords: [
      "per video type draft state",
      "AI video editor draft persistence",
      "Latted draft workflow",
      "switch video modes without losing work",
      "generation draft state AI video",
    ],
    mdxPublicPath:
      "/blog/per-video-type-generation-drafts-in-the-ai-video-editor.mdx",
    llmSummary:
      "Explains how Latted preserves common and per-mode draft inputs across text-to-video, image-to-video, transition, reference-to-video, motion-control, video-to-video, and avatar workflows.",
    faqItems: [
      {
        question: "What is per-video-type draft state?",
        answer:
          "It is saved in-progress input for each generation mode, together with shared fields that should persist when you move between modes.",
      },
      {
        question: "Why is draft persistence important in an AI video editor?",
        answer:
          "Because editors often compare several creation modes before deciding. If switching modes erases their work, experimentation becomes much slower.",
      },
      {
        question: "What kinds of fields should stay common across modes?",
        answer:
          "Prompt, model choice, duration, aspect ratio, and other shared generation settings are strong candidates to persist across mode changes.",
      },
      {
        question: "What kinds of fields should stay specific to one mode?",
        answer:
          "Inputs like reference keyframes, transition endpoints, motion settings, or a source video usually belong to the specific workflow that needs them.",
      },
      {
        question: "Who benefits most from this kind of editor behavior?",
        answer:
          "Both advanced users and newer users benefit because it makes exploration safer and reduces the cost of trying a different path.",
      },
    ],
  }),
  createBlogPost({
    slug: "organization-asset-detail-page-edit-and-sub-assets",
    title:
      "Why Latted's Organization Asset Detail Page Matters for Reusable Visual References",
    seoTitle:
      "Why Latted's Organization Asset Detail Page Matters for Reusable Visual References in 2026",
    description:
      "See how Latted turns an organization asset into an editable workspace for naming, prompting, previewing changes, and branching reusable sub-assets instead of treating it like a static file record.",
    excerpt:
      "A practical guide to using the asset detail page as a real editing surface for reusable references.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 7,
    tags: ["Assets", "Teams", "AI Video", "Latted", "Workflow"],
    keywords: [
      "organization asset detail page",
      "Latted reusable assets",
      "preview and commit asset edits",
      "sub assets workflow",
      "AI video reference management",
    ],
    mdxPublicPath: "/blog/organization-asset-detail-page-edit-and-sub-assets.mdx",
    llmSummary:
      "Explains how Latted turns a single organization asset into an editable workspace with inline naming and prompt edits, preview-first image changes, and sub-asset branching.",
    faqItems: [
      {
        question: "What is an asset detail workspace?",
        answer:
          "It is a page where one reusable asset can be inspected, renamed, updated, previewed, and branched into variations instead of behaving like a static file record.",
      },
      {
        question: "Why is a list of assets not enough?",
        answer:
          "Because reusable references often need refinement over time, and list views are good for discovery but weak for actual editing decisions.",
      },
      {
        question: "Why does preview-before-commit matter for assets?",
        answer:
          "It lets teams inspect a proposed change before replacing the current reusable reference, which reduces accidental drift.",
      },
      {
        question: "When should you create a sub-asset?",
        answer:
          "Create a sub-asset when you need a controlled variation of the main reference without overwriting the approved original.",
      },
      {
        question: "Who benefits most from an asset detail page?",
        answer:
          "Teams that reuse product shots, brand references, or character visuals across several projects benefit the most because they need continuity and controlled iteration.",
      },
    ],
  }),
  createBlogPost({
    slug: "batch-lip-sync-multiple-clips-to-one-voiceover-in-ai-video",
    title: "How to Lip Sync Multiple Video Clips to One Voiceover in Latted",
    seoTitle:
      "How to Lip Sync Multiple Video Clips to One Voiceover in Latted in 2026",
    description:
      "Learn why multi-clip lip sync is useful when one approved voiceover should drive several shots, and how Latted keeps that workflow inside the editor instead of turning it into several separate jobs.",
    excerpt:
      "A practical guide to applying one approved voiceover across several selected video clips in one editor workflow.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 6,
    tags: ["Lip Sync", "Audio Workflow", "AI Video", "Latted", "Workflow"],
    keywords: [
      "multi clip lip sync",
      "lip sync several video clips",
      "Latted lip sync workflow",
      "AI video voiceover lip sync",
      "batch lip sync editor",
    ],
    mdxPublicPath:
      "/blog/batch-lip-sync-multiple-clips-to-one-voiceover-in-ai-video.mdx",
    llmSummary:
      "Explains how Latted can apply one voiceover across several selected video clips in a single lip-sync workflow instead of forcing editors to handle each shot separately.",
    faqItems: [
      {
        question: "What is multi-clip lip sync?",
        answer:
          "It is a workflow where one voice track is applied across several selected video clips so the speaking motion can align with the same approved narration.",
      },
      {
        question: "Why is this better than lip syncing each clip separately?",
        answer:
          "Because narration is often approved at the sequence level, and handling several shots in one workflow is faster and more consistent than treating each clip as an isolated job.",
      },
      {
        question: "When is one voiceover shared across several clips?",
        answer:
          "It happens when a sequence uses several angles or related shots but still follows one approved narration track.",
      },
      {
        question: "Should every clip in a project be lip synced to the same voiceover?",
        answer:
          "No. This works best when the selected clips belong to the same spoken sequence and should all follow the same narration.",
      },
      {
        question: "Where does this workflow belong in production?",
        answer:
          "It belongs after the voiceover is approved and before final export, when the team is aligning visuals more tightly to the chosen audio performance.",
      },
    ],
  }),
  createBlogPost({
    slug: "ugc-product-brief-idea-count-and-captions-toggle",
    title:
      "Why Latted's UGC Workflow Starts With a Product Brief, Idea Count, and Captions Toggle",
    seoTitle:
      "Why Latted's UGC Workflow Starts With a Product Brief, Idea Count, and Captions Toggle in 2026",
    description:
      "Learn why Latted's UGC page asks for character inputs, product media, a product description, an idea count, and a captions choice before generation starts.",
    excerpt:
      "A practical guide to the ideation-first UGC workflow that narrows the best ad direction before full video generation.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 7,
    tags: ["UGC Ads", "Ideation", "AI Video", "Latted", "Workflow"],
    keywords: [
      "UGC product brief workflow",
      "generate several UGC ad ideas",
      "Latted UGC page",
      "captions toggle UGC ads",
      "AI UGC ideation workflow",
    ],
    mdxPublicPath: "/blog/ugc-product-brief-idea-count-and-captions-toggle.mdx",
    llmSummary:
      "Explains why Latted structures its UGC workflow around character inputs, product files, a product description, an idea count, and a captions toggle before full video generation begins.",
    faqItems: [
      {
        question: "Why start UGC video creation with a product brief instead of a blank prompt?",
        answer:
          "Because a product brief gives the system clearer commercial context and usually leads to better ad ideas than an underspecified generic prompt.",
      },
      {
        question: "Why generate several ideas before creating videos?",
        answer:
          "Because ad performance depends on angle selection, and it is cheaper to compare concepts before spending on full production.",
      },
      {
        question: "Why ask about captions before generation?",
        answer:
          "Because captions affect how speech-led videos are meant to land in feeds, and many teams want that decision included from the start.",
      },
      {
        question: "Can you use either a default creator or your own uploaded character?",
        answer:
          "Yes. Latted supports both default creator choices and custom uploaded character media as part of the UGC setup.",
      },
      {
        question: "Who benefits most from an ideation-first UGC flow?",
        answer:
          "Teams producing several ad angles for the same product benefit the most because they need to narrow concepts before committing to renders.",
      },
    ],
  }),
  createBlogPost({
    slug: "programmatic-sfx-and-music-generation-from-the-ai-video-editor",
    title:
      "How Sound Effects and Music Generation Fit Directly Into Latted's Video Editor",
    seoTitle:
      "How Sound Effects and Music Generation Fit Directly Into Latted's Video Editor in 2026",
    description:
      "See how Latted treats generated sound effects and music as timeline clips tied to a target video and timestamp, which makes audio generation part of editing instead of a separate toolchain.",
    excerpt:
      "A practical guide to generating sound effects and music inside the editor where timing and context already exist.",
    publishedAt: "2026-04-01",
    readingTimeMinutes: 7,
    tags: ["Audio Workflow", "Sound Design", "AI Video", "Latted", "Workflow"],
    keywords: [
      "generate sound effects in video editor",
      "generate music in timeline",
      "Latted audio generation",
      "AI video sound design workflow",
      "timeline native music generation",
    ],
    mdxPublicPath:
      "/blog/programmatic-sfx-and-music-generation-from-the-ai-video-editor.mdx",
    llmSummary:
      "Explains how Latted generates sound effects and music as timeline clips tied to a target video and timestamp so audio decisions happen in context instead of outside the editor.",
    faqItems: [
      {
        question: "What is timeline-native audio generation?",
        answer:
          "It is creating sound effects or music as clips that already know where they belong in the edit instead of generating them in a separate tool and importing them later.",
      },
      {
        question: "Why is this better than using a separate audio tool first?",
        answer:
          "Because timing, duration, and context are clearer inside the timeline, which makes the generated result more useful on the first pass.",
      },
      {
        question: "What is the difference between sound effects and music in this workflow?",
        answer:
          "Sound effects usually support specific moments or actions, while music supports the broader emotional and pacing arc of the sequence.",
      },
      {
        question: "Can generated audio be revised later?",
        answer:
          "Yes. Latted supports regeneration and continued clip-level audio editing instead of treating the first generated result as final.",
      },
      {
        question: "When should a team upload audio instead of generating it?",
        answer:
          "Upload audio when you already have an approved track. Generate it when the project still needs a new sound layer designed around the current cut.",
      },
    ],
  }),
  createBlogPost({
    slug: "shared-ai-asset-library-for-video-teams",
    title:
      "How a Shared AI Asset Library Speeds Up Video Production Across Projects",
    seoTitle:
      "How a Shared AI Asset Library Speeds Up Video Production Across Projects in 2026",
    description:
      "Learn why Latted's shared asset workflow helps teams reuse approved references across projects instead of rebuilding the same product, character, and brand context from scratch.",
    excerpt:
      "A practical guide to using a shared AI asset library to speed up repeatable video production across many projects.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 6,
    tags: ["Assets", "AI Video", "Latted", "Teams", "Workflow"],
    keywords: [
      "shared AI asset library",
      "Latted assets workflow",
      "reusable AI video assets",
      "organization asset library",
      "AI video team asset management",
    ],
    mdxPublicPath: "/blog/shared-ai-asset-library-for-video-teams.mdx",
    llmSummary:
      "Explains how Latted's shared asset workflow helps teams reuse approved references across projects instead of re-uploading the same visual context for each new video.",
    faqItems: [
      {
        question: "What is a shared AI asset library?",
        answer:
          "It is a reusable collection of images and references that multiple projects can access so teams do not have to recreate the same visual material from scratch.",
      },
      {
        question: "Why is a shared asset library useful for AI-video teams?",
        answer:
          "Because it reduces repeated setup work and helps teams preserve the same products, characters, and references across many projects.",
      },
      {
        question: "What should go into a shared asset library first?",
        answer:
          "Start with the assets that recur the most, such as hero product images, recurring characters, logos, and approved reference visuals.",
      },
      {
        question: "Is a shared asset library only helpful for large organizations?",
        answer:
          "No. It also helps small teams and solo creators once they reuse the same visual material across several projects.",
      },
      {
        question: "Where can teams manage shared assets in Latted?",
        answer:
          "They can manage them from Latted's assets workflow, where reusable organization-level references can be uploaded, generated, searched, and reused.",
      },
    ],
  }),
  createBlogPost({
    slug: "sub-assets-for-ai-video-asset-variations",
    title:
      "How Sub-Assets Help Teams Create Variations Without Losing the Original Asset",
    seoTitle:
      "How Sub-Assets Help Teams Create Variations Without Losing the Original Asset in 2026",
    description:
      "See why Latted's parent-and-variation asset workflow is useful when one approved reference needs alternate looks, angles, or campaign-specific versions without overwriting the original.",
    excerpt:
      "A guide to using sub-assets so teams can branch reusable references into controlled variations without losing the approved base asset.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 6,
    tags: ["Assets", "AI Video", "Latted", "Variations", "Workflow"],
    keywords: [
      "sub-assets AI video",
      "asset variations Latted",
      "parent asset workflow",
      "AI asset versioning",
      "reusable asset variations",
    ],
    mdxPublicPath: "/blog/sub-assets-for-ai-video-asset-variations.mdx",
    llmSummary:
      "Shows how Latted's sub-asset workflow helps teams create controlled variations of reusable references without overwriting the original approved asset.",
    faqItems: [
      {
        question: "What is a sub-asset in an AI-video workflow?",
        answer:
          "It is a variation of a main reusable asset that keeps the original intact while adding a new version for a specific need.",
      },
      {
        question: "Why use a sub-asset instead of editing the original?",
        answer:
          "Because many projects need alternate versions without turning the approved base asset into a constantly changing target.",
      },
      {
        question: "What kinds of changes belong in a sub-asset?",
        answer:
          "Changes like alternate angles, lighting, styling, crops, or campaign-specific versions are strong candidates because they preserve the same underlying identity.",
      },
      {
        question: "When should I create a brand new asset instead?",
        answer:
          "Create a new asset when the visual should no longer inherit the identity or purpose of the original reference.",
      },
      {
        question: "Where can I manage sub-assets in Latted?",
        answer:
          "You can manage them from the asset detail workflow, where a parent asset can branch into controlled variations instead of being replaced outright.",
      },
    ],
  }),
  createBlogPost({
    slug: "enhance-prompts-before-generating-ai-assets",
    title:
      "Why Prompt Enhancement Helps Teams Generate Better Reusable AI Assets",
    seoTitle:
      "Why Prompt Enhancement Helps Teams Generate Better Reusable AI Assets in 2026",
    description:
      "Learn why Latted's prompt-enhancement step is useful when teams have a rough asset idea but need a clearer generation brief before turning it into a reusable reference.",
    excerpt:
      "A practical guide to prompt enhancement for reusable AI assets, where better first-pass prompts create stronger long-term references.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 6,
    tags: ["Assets", "AI Image", "Latted", "Prompting", "Workflow"],
    keywords: [
      "asset prompt enhancement",
      "AI asset generation prompt",
      "Latted enhance prompt",
      "better reusable AI assets",
      "AI image prompt enhancement",
    ],
    mdxPublicPath: "/blog/enhance-prompts-before-generating-ai-assets.mdx",
    llmSummary:
      "Explains how Latted's asset prompt-enhancement step helps teams turn rough ideas into clearer, more reusable AI-generated references before those assets spread across projects.",
    faqItems: [
      {
        question: "What is prompt enhancement in AI asset generation?",
        answer:
          "It is the process of turning a short asset description into a more detailed prompt before generation so the visual direction is clearer.",
      },
      {
        question: "Why does prompt enhancement help reusable assets?",
        answer:
          "Because reusable assets need stronger visual clarity than one-off experiments. A better prompt usually creates a more reliable base reference.",
      },
      {
        question: "What should a prompt enhancer add?",
        answer:
          "It should add useful details like composition, lighting, material, style, and background treatment when those details are missing.",
      },
      {
        question: "Does prompt enhancement guarantee a good asset?",
        answer:
          "No. It improves the prompt, but teams still need to review whether the generated asset is actually good enough to save and reuse.",
      },
      {
        question: "Where can I enhance an asset prompt in Latted?",
        answer:
          "You can do it from Latted's organization-asset creation flow before you generate the reusable asset.",
      },
    ],
  }),
  createBlogPost({
    slug: "search-ai-assets-across-projects-and-teams",
    title:
      "How Searchable Asset Libraries Help Teams Reuse the Right Reference Faster",
    seoTitle:
      "How Searchable Asset Libraries Help Teams Reuse the Right Reference Faster in 2026",
    description:
      "See why Latted's searchable asset workflow matters once reusable references accumulate across projects, teammates, and campaign variations.",
    excerpt:
      "A guide to asset-library search, where naming and prompt metadata make approved references discoverable instead of forgotten.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 6,
    tags: ["Assets", "AI Video", "Latted", "Search", "Teams"],
    keywords: [
      "searchable asset library",
      "Latted asset search",
      "AI video asset discovery",
      "organization asset reuse",
      "search AI assets by prompt",
    ],
    mdxPublicPath: "/blog/search-ai-assets-across-projects-and-teams.mdx",
    llmSummary:
      "Explains how Latted's searchable asset library helps teams find reusable references by name or prompt instead of duplicating uploads and regenerating assets unnecessarily.",
    faqItems: [
      {
        question: "Why does an AI asset library need search?",
        answer:
          "Because once the library grows, teams need a fast way to find approved references without relying on memory.",
      },
      {
        question: "What should asset search cover?",
        answer:
          "It should at least cover asset names and asset descriptions or prompts so users can find references by either label or intent.",
      },
      {
        question: "Is browsing still useful if search exists?",
        answer:
          "Yes. Browsing is useful for exploration, while search is better when the team already knows roughly what it needs.",
      },
      {
        question: "How does search improve asset reuse?",
        answer:
          "It makes existing references easier to find, which reduces duplicate uploads and unnecessary regeneration.",
      },
      {
        question: "Where can teams search assets in Latted?",
        answer:
          "They can search from Latted's assets page, where organization-level assets are listed and filtered for reuse across projects.",
      },
    ],
  }),
  createBlogPost({
    slug: "default-vs-custom-creators-for-ai-ugc-ads",
    title: "When to Use a Default Creator vs Upload Your Own for AI UGC Ads",
    seoTitle:
      "When to Use a Default Creator vs Upload Your Own for AI UGC Ads in 2026",
    description:
      "Learn when Latted's default creator options are best for fast concept testing and when a custom uploaded creator is better for identity-driven UGC campaigns.",
    excerpt:
      "A practical guide to choosing between default and custom creators in Latted's UGC workflow.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 6,
    tags: ["UGC", "AI Video", "Latted", "Creator Workflow", "Ads"],
    keywords: [
      "default creator UGC ad",
      "custom creator UGC workflow",
      "Latted UGC creator selection",
      "AI UGC creator upload",
      "UGC ad creator identity",
    ],
    mdxPublicPath: "/blog/default-vs-custom-creators-for-ai-ugc-ads.mdx",
    llmSummary:
      "Shows how Latted's UGC workflow supports both fast testing with default creators and identity-specific production with custom uploaded creator references.",
    faqItems: [
      {
        question: "When should I use a default creator for AI UGC ads?",
        answer:
          "Use a default creator when the main goal is to test ad concepts, hooks, or product angles quickly without extra setup.",
      },
      {
        question: "When should I upload my own creator instead?",
        answer:
          "Upload your own creator when the ad should feel tied to a specific person, brand face, or campaign identity.",
      },
      {
        question: "Are default creators only for beginners?",
        answer:
          "No. They are useful for any team that wants to validate ideas faster before investing in more custom production inputs.",
      },
      {
        question: "Does creator choice really affect UGC performance?",
        answer:
          "Yes. UGC ads depend heavily on authenticity, so the creator reference changes how grounded and believable the ad feels.",
      },
      {
        question: "Where can I choose a creator in Latted?",
        answer:
          "You can do it from Latted's UGC workflow, where you can either select a default creator or upload your own reference before generation.",
      },
    ],
  }),
  createBlogPost({
    slug: "generate-ugc-ad-ideas-before-producing-videos",
    title: "Why Teams Should Generate AI UGC Ad Ideas Before Producing Videos",
    seoTitle:
      "Why Teams Should Generate AI UGC Ad Ideas Before Producing Videos in 2026",
    description:
      "See why Latted's idea-first UGC workflow helps teams test hooks and scripts before spending time generating full ad projects.",
    excerpt:
      "A guide to idea-first UGC production, where teams generate several ad concepts before choosing which ones deserve video generation.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 6,
    tags: ["UGC", "AI Video", "Latted", "Ideation", "Ads"],
    keywords: [
      "UGC ad idea generation",
      "AI UGC brainstorming",
      "Latted UGC ideas",
      "generate ad ideas before video",
      "UGC hook testing workflow",
    ],
    mdxPublicPath: "/blog/generate-ugc-ad-ideas-before-producing-videos.mdx",
    llmSummary:
      "Explains how Latted's UGC idea-generation step helps teams test multiple hooks and scripts before investing in full video production.",
    faqItems: [
      {
        question: "Why generate UGC ad ideas before producing videos?",
        answer:
          "Because it helps teams compare hooks, scripts, and product angles before spending time on full project generation.",
      },
      {
        question: "What should a good UGC idea set vary?",
        answer:
          "It should vary the opening hook, framing, message angle, and overall concept so the team can compare meaningful options.",
      },
      {
        question: "Is idea generation only useful for larger teams?",
        answer:
          "No. It also helps solo creators avoid committing too early to a weak ad concept.",
      },
      {
        question: "Should every generated idea become a video?",
        answer:
          "No. The point is to surface several options and then select only the strongest concepts for production.",
      },
      {
        question: "Where does this happen in Latted?",
        answer:
          "It happens in Latted's UGC workflow, where the system can generate several ad ideas before the team chooses what to produce.",
      },
    ],
  }),
  createBlogPost({
    slug: "generate-multiple-ai-ugc-ads-from-selected-ideas",
    title:
      "How to Generate Multiple AI UGC Ads From Selected Ideas Without Restarting the Workflow",
    seoTitle:
      "How to Generate Multiple AI UGC Ads From Selected Ideas Without Restarting the Workflow in 2026",
    description:
      "Learn how Latted's multi-select UGC workflow helps teams turn several chosen ad ideas into separate projects when they want broader creative testing from one ideation pass.",
    excerpt:
      "A practical guide to generating multiple UGC projects from one batch of selected ideas instead of restarting the workflow for each ad.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 7,
    tags: ["UGC", "AI Video", "Latted", "Automation", "Ads"],
    keywords: [
      "multiple UGC ads",
      "generate several AI ads",
      "Latted multi-select UGC",
      "selected idea to project workflow",
      "UGC creative testing workflow",
    ],
    mdxPublicPath:
      "/blog/generate-multiple-ai-ugc-ads-from-selected-ideas.mdx",
    llmSummary:
      "Shows how Latted's multi-select UGC workflow helps teams turn several chosen ideas into separate ad projects from one ideation pass.",
    faqItems: [
      {
        question: "Why generate multiple UGC ads from selected ideas?",
        answer:
          "Because it lets teams test several promising concepts from one ideation pass instead of restarting the workflow for every variation.",
      },
      {
        question: "When is multi-ad generation most useful?",
        answer:
          "It is most useful when the team wants broader creative testing across several hooks or message angles at the same time.",
      },
      {
        question: "Should every generated idea be selected?",
        answer:
          "No. It is better to select only the strongest concepts so the production pass stays focused and useful.",
      },
      {
        question: "What makes this better than repeating the whole workflow manually?",
        answer:
          "It removes repeated setup work and makes it easier to turn one idea batch into several parallel ad projects.",
      },
      {
        question: "Where does this fit in Latted?",
        answer:
          "It fits in Latted's UGC workflow, where selected ideas can become one or several generated projects depending on the team's setup.",
      },
    ],
  }),
  createBlogPost({
    slug: "ideate-ai-video-campaigns-before-generating-projects",
    title: "Why AI Video Campaigns Should Start With Ideation Before Generation",
    seoTitle:
      "Why AI Video Campaigns Should Start With Ideation Before Generation in 2026",
    description:
      "See why Latted's automation workflow separates campaign ideation from generation so teams can explore concepts before committing resources to full projects.",
    excerpt:
      "A guide to ideation-first AI video automation, where the campaign gets clarified before generation begins.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 6,
    tags: ["Automations", "AI Video", "Latted", "Ideation", "Workflow"],
    keywords: [
      "ideation before generation",
      "AI video campaign ideation",
      "Latted automation ideation",
      "creative planning before video generation",
      "AI campaign brainstorming workflow",
    ],
    mdxPublicPath:
      "/blog/ideate-ai-video-campaigns-before-generating-projects.mdx",
    llmSummary:
      "Explains why Latted's automation workflow starts with ideation so teams can compare concepts before committing generation time and budget to full projects.",
    faqItems: [
      {
        question: "Why start AI video campaigns with ideation before generation?",
        answer:
          "Because it helps teams compare concepts and clarify the campaign direction before they spend time and budget on full projects.",
      },
      {
        question: "What does an ideation-first workflow improve?",
        answer:
          "It improves concept quality, reduces premature generation, and gives teams a stronger decision point before production starts.",
      },
      {
        question: "Is this only useful for large campaigns?",
        answer:
          "No. It is also useful for smaller projects whenever the team needs to compare creative directions before committing.",
      },
      {
        question: "Should ideation replace production workflows?",
        answer:
          "No. It should come before production so the generation phase starts from better creative decisions.",
      },
      {
        question: "Where does this happen in Latted?",
        answer:
          "It happens in Latted's Automations workflow, where ideation is the first step before generation.",
      },
    ],
  }),
  createBlogPost({
    slug: "live-ideation-worklogs-for-ai-video-automation",
    title: "Why Live Ideation Worklogs Make AI Video Automation Easier to Trust",
    seoTitle:
      "Why Live Ideation Worklogs Make AI Video Automation Easier to Trust in 2026",
    description:
      "Learn why Latted's ideation worklog is useful when teams want to inspect agent progress, saved ideas, and the reasoning path behind automated campaign research.",
    excerpt:
      "A practical guide to live ideation worklogs, where automation becomes easier to trust because teams can inspect progress instead of waiting blindly.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 6,
    tags: ["Automations", "AI Video", "Latted", "Transparency", "Workflow"],
    keywords: [
      "live ideation worklog",
      "AI automation transparency",
      "Latted ideation worklog",
      "inspect AI campaign research",
      "agent progress log workflow",
    ],
    mdxPublicPath: "/blog/live-ideation-worklogs-for-ai-video-automation.mdx",
    llmSummary:
      "Shows how Latted's live ideation worklog helps teams inspect agent progress and saved ideas during campaign research instead of waiting without visibility.",
    faqItems: [
      {
        question: "What is a live ideation worklog?",
        answer:
          "It is a running view of agent progress, saved ideas, and intermediate activity while an automation is researching and brainstorming.",
      },
      {
        question: "Why does a live worklog matter in AI automation?",
        answer:
          "Because it makes the process easier to trust by showing progress and saved outputs instead of forcing teams to wait blindly.",
      },
      {
        question: "What should teams look for in an ideation worklog?",
        answer:
          "They should look for progress signals, saved ideas, and evidence that the agent is exploring useful campaign directions.",
      },
      {
        question: "Does a worklog replace final creative review?",
        answer:
          "No. It improves visibility during the run, but teams still need to review the final ideas before production.",
      },
      {
        question: "Where can I see this in Latted?",
        answer:
          "You can see it in Latted's Automations workflow, where ideation runs surface live progress and saved ideas while the agent is working.",
      },
    ],
  }),
  createBlogPost({
    slug: "saved-creative-ideas-before-ai-video-production",
    title:
      "Why Saved Creative Ideas Matter Before You Turn AI Campaigns Into Video Projects",
    seoTitle:
      "Why Saved Creative Ideas Matter Before You Turn AI Campaigns Into Video Projects in 2026",
    description:
      "See why Latted's saved-idea workflow is useful when teams want to keep strong concepts from an ideation run and review them before moving into production.",
    excerpt:
      "A guide to saved creative ideas, where automation produces a reviewable set of campaign concepts before any video project is generated.",
    publishedAt: "2026-03-30",
    readingTimeMinutes: 6,
    tags: ["Automations", "AI Video", "Latted", "Ideation", "Planning"],
    keywords: [
      "saved creative ideas",
      "AI campaign ideas before production",
      "Latted saved ideation results",
      "review ideas before generating video",
      "automation creative planning",
    ],
    mdxPublicPath: "/blog/saved-creative-ideas-before-ai-video-production.mdx",
    llmSummary:
      "Explains how Latted's saved-idea workflow helps teams keep strong concepts from an ideation run and review them before turning ideas into video projects.",
    faqItems: [
      {
        question: "Why save creative ideas before video production starts?",
        answer:
          "Because it gives teams a reviewable set of concepts before they commit to turning those ideas into full projects.",
      },
      {
        question: "What should a saved creative idea include?",
        answer:
          "It should include enough structure to understand the concept, such as the hook, platform, framing, or campaign direction.",
      },
      {
        question: "Why not move straight from ideation into generation?",
        answer:
          "Because saving ideas first creates a decision point where teams can compare options before spending more time and budget on production.",
      },
      {
        question: "Are saved ideas only useful for teams?",
        answer:
          "No. They also help solo creators keep and compare the strongest concepts from an ideation pass.",
      },
      {
        question: "Where does this fit in Latted?",
        answer:
          "It fits in Latted's Automations workflow, where ideation runs can surface saved creative ideas before generation begins.",
      },
    ],
  }),
  createBlogPost({
    slug: "text-message-alerts-for-long-ai-video-renders",
    title: "How Text Message Alerts Help Teams Manage Long AI Video Renders",
    seoTitle:
      "How Text Message Alerts Help Teams Manage Long AI Video Renders in 2026",
    description:
      "Learn why Latted's phone alert flow is useful for long-running AI video generation, where project work can finish asynchronously and users need a reliable prompt to return.",
    excerpt:
      "A practical guide to why text message alerts reduce waiting behavior and make long AI video runs easier to manage.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 6,
    tags: [
      "Notifications",
      "AI Video",
      "Latted",
      "Background Generation",
      "Workflow",
    ],
    keywords: [
      "AI video text alerts",
      "Latted phone notification",
      "long AI video render alert",
      "AI video completion text",
      "background AI video notification",
    ],
    mdxPublicPath: "/blog/text-message-alerts-for-long-ai-video-renders.mdx",
    llmSummary:
      "Explains why Latted's phone-notification flow helps users step away from long AI video generations and return when project work is ready for review.",
    faqItems: [
      {
        question: "Why do AI video tools need text message alerts?",
        answer:
          "Because generation can take long enough that users naturally leave the editor and need a reliable reminder to return.",
      },
      {
        question: "When are text alerts more useful than in-app status alone?",
        answer:
          "They are more useful when the user will probably switch tasks, close the tab, or leave the computer before the result is ready.",
      },
      {
        question: "Are text alerts only useful for teams?",
        answer:
          "No. Solo creators benefit too because long waits are easy to forget even when one person owns the whole workflow.",
      },
      {
        question: "What should a good completion alert correspond to?",
        answer:
          "It should correspond to a real project milestone, such as a completed generation or a project that is ready for review.",
      },
      {
        question: "Where does this fit in Latted?",
        answer:
          "It fits into Latted's long-running project workflow, where background generation and review often happen asynchronously instead of in one uninterrupted session.",
      },
    ],
  }),
  createBlogPost({
    slug: "preview-ai-keyframe-edits-before-saving",
    title: "Why AI Keyframe Editors Need Preview Before Save, Not Blind Overwrites",
    seoTitle:
      "Why AI Keyframe Editors Need Preview Before Save, Not Blind Overwrites in 2026",
    description:
      "See why Latted's pending keyframe preview workflow is safer than direct overwrite, especially when an AI edit is trying to improve a frame that is already mostly working.",
    excerpt:
      "A guide to preview-first AI keyframe editing, where generated changes become candidates to review instead of automatic replacements.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 7,
    tags: [
      "Keyframe Editing",
      "AI Video",
      "Latted",
      "Image Editing",
      "Workflow",
    ],
    keywords: [
      "AI keyframe preview before save",
      "pending keyframe preview",
      "Latted keyframe editing",
      "AI image edit preview workflow",
      "preview before overwrite AI editor",
    ],
    mdxPublicPath: "/blog/preview-ai-keyframe-edits-before-saving.mdx",
    llmSummary:
      "Explains why Latted shows AI-edited keyframes as pending previews before save so users can compare candidate results before replacing a working frame.",
    faqItems: [
      {
        question: "Why is preview before save important in AI keyframe editing?",
        answer:
          "Because AI edits can improve or damage a frame unpredictably, so users need a chance to compare the result before it becomes the new saved state.",
      },
      {
        question: "When is preview-before-save most useful?",
        answer:
          "It is most useful when the current frame is already mostly correct and the edit is trying to improve one local or stylistic detail.",
      },
      {
        question: "Is this only helpful for advanced editors?",
        answer:
          "No. Beginners benefit too because preview states reduce the fear of making an edit that is hard to undo.",
      },
      {
        question: "Should I judge the preview by itself?",
        answer:
          "No. The best test is whether the preview works better in the surrounding video context, not only as a standalone image.",
      },
      {
        question: "How does Latted handle this?",
        answer:
          "Latted lets keyframe edits surface as a pending preview so you can review the candidate result before you accept it into the project.",
      },
    ],
  }),
  createBlogPost({
    slug: "virtual-camera-angle-changes-for-ai-video-keyframes",
    title:
      "How Virtual Camera Angle Changes Improve AI Video Keyframes Without Rebuilding the Shot",
    seoTitle:
      "How Virtual Camera Angle Changes Improve AI Video Keyframes Without Rebuilding the Shot in 2026",
    description:
      "Learn when Latted's keyframe camera-angle workflow is better than full regeneration, especially when the shot is already right but the framing still needs work.",
    excerpt:
      "A practical guide to using virtual camera-angle changes as a safer, narrower fix for weak framing in AI-generated keyframes.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 7,
    tags: [
      "Keyframe Editing",
      "AI Video",
      "Latted",
      "Camera Control",
      "Shot Design",
    ],
    keywords: [
      "AI keyframe camera angle",
      "virtual camera angle AI video",
      "Latted camera angle edit",
      "reframe AI keyframe",
      "AI video keyframe zoom and angle",
    ],
    mdxPublicPath:
      "/blog/virtual-camera-angle-changes-for-ai-video-keyframes.mdx",
    llmSummary:
      "Shows how Latted's keyframe camera-angle workflow helps editors improve framing, perspective, and zoom without rebuilding an otherwise strong AI-generated frame.",
    faqItems: [
      {
        question:
          "When should I change the camera angle instead of regenerating the whole frame?",
        answer:
          "Change the angle when the subject and scene are already right and the main problem is framing, perspective, or emphasis.",
      },
      {
        question: "What does a virtual camera-angle edit actually change?",
        answer:
          "It changes how the viewer perceives the shot, such as the angle, framing, or zoom, without requiring a complete new concept.",
      },
      {
        question: "Why is this useful for AI video?",
        answer:
          "Because keyframes often need small composition fixes that should not force the editor to rebuild a working shot from zero.",
      },
      {
        question: "Should I preview the result before saving it?",
        answer:
          "Yes. Previewing helps you confirm that the new perspective improves the frame without damaging sequence continuity.",
      },
      {
        question: "Where does this fit in Latted?",
        answer:
          "It fits inside Latted's keyframe-editing workflow, where you can refine shot perspective in the same editor you use for the rest of the project.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-version-preview-matters-before-you-restore-ai-video-history",
    title: "Why Version Preview Matters Before You Restore AI Video History",
    seoTitle:
      "Why Version Preview Matters Before You Restore AI Video History in 2026",
    description:
      "See why Latted's version-preview flow is safer than blind rollback, especially when teams need to inspect older keyframe or video states before restoring them.",
    excerpt:
      "A guide to preview-before-restore decisions in AI video editing, where older versions should be inspected before they become active again.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 6,
    tags: [
      "Version History",
      "AI Video",
      "Latted",
      "Review Workflow",
      "Editing Safety",
    ],
    keywords: [
      "AI video version preview",
      "preview before restore AI editor",
      "Latted version history preview",
      "keyframe version preview",
      "video rollback preview",
    ],
    mdxPublicPath:
      "/blog/why-version-preview-matters-before-you-restore-ai-video-history.mdx",
    llmSummary:
      "Explains why Latted lets editors preview older keyframe and video versions before restore so rollback decisions can be made intentionally instead of from memory.",
    faqItems: [
      {
        question: "Why is version preview useful before restore?",
        answer:
          "Because it lets you inspect the older result directly before making it active again, which reduces mistakes and guesswork.",
      },
      {
        question: "Is preview-before-restore only useful for teams?",
        answer:
          "No. Solo creators benefit too because memory is unreliable even when one person controls the whole project.",
      },
      {
        question: "When should I restore an older version?",
        answer:
          "Restore it when the older version clearly does the scene's job better than the current one after you preview both.",
      },
      {
        question: "Is the newest AI version usually the best one?",
        answer:
          "No. Newer outputs are only newer. Preview and comparison are what tell you whether they are actually better.",
      },
      {
        question: "How does Latted handle this?",
        answer:
          "Latted lets editors preview earlier keyframe or video versions first so restore decisions can be made intentionally instead of blindly.",
      },
    ],
  }),
  createBlogPost({
    slug: "project-level-keyframes-for-video-centric-ai-timelines",
    title: "Why Project-Level Keyframes Make Video-Centric AI Timelines Easier to Manage",
    seoTitle:
      "Why Project-Level Keyframes Make Video-Centric AI Timelines Easier to Manage in 2026",
    description:
      "Learn why Latted keeps project-level keyframes available in a video-centric timeline, so reusable visual anchors can support transitions, branches, and multiple clip workflows.",
    excerpt:
      "A practical explanation of why project-level keyframes make reusable visual anchors easier to manage across AI video timelines.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 7,
    tags: [
      "Timeline Architecture",
      "AI Video",
      "Latted",
      "Keyframes",
      "Workflow Design",
    ],
    keywords: [
      "project level keyframes",
      "video centric AI timeline",
      "Latted project keyframes",
      "shared keyframe workflow",
      "AI video reusable keyframes",
    ],
    mdxPublicPath:
      "/blog/project-level-keyframes-for-video-centric-ai-timelines.mdx",
    llmSummary:
      "Explains why Latted uses project-level keyframes in its video-centric editor so videos and transitions can reuse shared visual anchors across a project.",
    faqItems: [
      {
        question: "What is a project-level keyframe?",
        answer:
          "It is a reusable keyframe that belongs to the broader project so different video workflows can reference it without tying it to only one scene.",
      },
      {
        question: "Why does this help in AI-video editing?",
        answer:
          "It helps because many AI-video workflows need the same visual anchor in more than one place, such as a shared starting frame or reusable product shot.",
      },
      {
        question: "Is this only useful for large projects?",
        answer:
          "No. It also helps smaller projects whenever one good frame should support multiple video variations.",
      },
      {
        question: "When should I create a scene-only keyframe instead?",
        answer:
          "Create a scene-only keyframe when the frame is truly local to that scene and unlikely to be reused elsewhere in the timeline.",
      },
      {
        question: "How does Latted use project-level keyframes?",
        answer:
          "Latted uses them in its video-centric workflow so videos and related tools can reference shared keyframes across the project more easily.",
      },
    ],
  }),
  createBlogPost({
    slug: "start-and-end-keyframe-slots-for-ai-video-generation",
    title: "Why Start and End Keyframe Slots Make AI Video Generation More Predictable",
    seoTitle:
      "Why Start and End Keyframe Slots Make AI Video Generation More Predictable in 2026",
    description:
      "See why Latted's start and end keyframe workflow gives AI video clips clearer boundaries, making motion paths easier to control than prompt-only generation.",
    excerpt:
      "A guide to using start and end keyframes as explicit anchors for more predictable AI-generated motion.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 6,
    tags: [
      "Video Generation",
      "AI Video",
      "Latted",
      "Keyframes",
      "Motion Control",
    ],
    keywords: [
      "start and end keyframes AI video",
      "predictable AI video generation",
      "Latted keyframe slots",
      "AI motion anchors",
      "prompt plus keyframe video workflow",
    ],
    mdxPublicPath:
      "/blog/start-and-end-keyframe-slots-for-ai-video-generation.mdx",
    llmSummary:
      "Shows how Latted uses explicit start and end keyframes to give AI video clips clearer motion boundaries and more predictable results than prompt-only generation.",
    faqItems: [
      {
        question: "What do start and end keyframes do in AI video generation?",
        answer:
          "They define the opening and ending visual states of a clip so the model can generate the in-between motion more predictably.",
      },
      {
        question: "When do I need both start and end keyframes?",
        answer:
          "You need both when the clip must begin and land in specific compositions, such as transitions, reveals, or controlled transformations.",
      },
      {
        question: "Is a prompt still important if I use keyframes?",
        answer:
          "Yes. The prompt explains the motion behavior and creative intent, while the keyframes define the boundaries.",
      },
      {
        question: "Are start and end keyframes only for advanced workflows?",
        answer:
          "No. They are useful any time a team wants more control over where a generated shot begins and ends.",
      },
      {
        question: "How does Latted support this?",
        answer:
          "Latted can attach explicit start and end keyframes to video-generation workflows so clips fit more reliably into the timeline and surrounding scenes.",
      },
    ],
  }),
  createBlogPost({
    slug: "separate-image-edit-models-for-ai-keyframe-workflows",
    title: "Why AI Keyframe Editors Should Separate Image Generation Models From Edit Models",
    seoTitle:
      "Why AI Keyframe Editors Should Separate Image Generation Models From Edit Models in 2026",
    description:
      "Learn why Latted keeps image-generation and image-edit model preferences separate, so exploratory creation and controlled revision can use different model behavior.",
    excerpt:
      "A practical guide to why create-from-scratch and edit-an-existing-frame workflows should not always share the same model preference.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 7,
    tags: [
      "Model Selection",
      "AI Video",
      "Latted",
      "Keyframe Editing",
      "Workflow",
    ],
    keywords: [
      "separate image edit model",
      "AI keyframe edit model",
      "Latted image edit model",
      "image generation vs edit model",
      "AI image refinement workflow",
    ],
    mdxPublicPath:
      "/blog/separate-image-edit-models-for-ai-keyframe-workflows.mdx",
    llmSummary:
      "Explains why Latted separates image-generation and image-edit model preferences so exploratory creation and controlled keyframe revision can be tuned independently.",
    faqItems: [
      {
        question: "Why separate image generation models from image edit models?",
        answer:
          "Because generating a new frame and revising an existing one have different goals, and the best model behavior for one is not always the best behavior for the other.",
      },
      {
        question: "When does edit-model choice matter most?",
        answer:
          "It matters most when the current frame is already useful and you want to improve it without accidentally replacing the whole visual idea.",
      },
      {
        question: "Is one model preference enough for simple projects?",
        answer:
          "Sometimes, yes. But separate control becomes more valuable as projects get more iterative and the cost of damaging a strong frame gets higher.",
      },
      {
        question: "Does this only help advanced users?",
        answer:
          "No. Even beginners benefit from a clearer distinction between exploration and refinement because it makes the editor's behavior easier to predict.",
      },
      {
        question: "How does Latted handle this?",
        answer:
          "Latted lets users keep separate model preferences for image generation and image editing so each phase of the workflow can be tuned more appropriately.",
      },
    ],
  }),
  createBlogPost({
    slug: "when-to-switch-from-pay-as-you-go-to-credits-for-ai-video",
    title: "When Should You Switch From Pay-As-You-Go to Credits in AI Video Software?",
    seoTitle:
      "When Should You Switch From Pay-As-You-Go to Credits in AI Video Software in 2026",
    description:
      "See when Latted's credit-based billing becomes more useful than pay-as-you-go, especially once AI video usage is regular enough to need clearer budgeting and lower purchase friction.",
    excerpt:
      "A practical billing-mode guide for teams deciding when pay-as-you-go is still enough and when credits are the better operational fit.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 6,
    tags: [
      "Billing",
      "AI Video",
      "Latted",
      "Credits",
      "Budgeting",
    ],
    keywords: [
      "pay as you go vs credits AI video",
      "when to switch to credits",
      "Latted billing mode",
      "AI video credit billing",
      "AI video budget control",
    ],
    mdxPublicPath:
      "/blog/when-to-switch-from-pay-as-you-go-to-credits-for-ai-video.mdx",
    llmSummary:
      "Explains when Latted users should move from pay-as-you-go to credits based on usage frequency, budgeting needs, and operational comfort with prepaid balance workflows.",
    faqItems: [
      {
        question: "When is pay-as-you-go the better choice?",
        answer:
          "It is better when usage is still occasional, experimental, or early enough that the team does not yet need a dedicated balance-management workflow.",
      },
      {
        question: "When should I switch to credits?",
        answer:
          "Switch when usage becomes regular enough that budgeting, balance visibility, and lower purchase friction matter more than the simplicity of pure metered billing.",
      },
      {
        question: "Do credits only help large teams?",
        answer:
          "No. Solo creators can benefit too if they generate often enough that balance-based spending feels easier to manage.",
      },
      {
        question: "Why do credits change workflow behavior?",
        answer:
          "Because visible balances, purchase history, and reload settings make iteration feel more predictable and easier to plan operationally.",
      },
      {
        question: "How does Latted handle this?",
        answer:
          "Latted lets users move between pay-as-you-go and credit-based billing so the billing model can match the maturity of the production workflow.",
      },
    ],
  }),
  createBlogPost({
    slug: "custom-studio-style-prompts-for-talking-head-ai-videos",
    title: "When Should You Use a Custom Studio Style Prompt for Talking-Head AI Videos?",
    seoTitle:
      "When Should You Use a Custom Studio Style Prompt for Talking-Head AI Videos in 2026",
    description:
      "Learn when Latted's custom studio-style prompt is better than a preset for talking-head videos, especially when brand, campaign, or set details need more precise direction.",
    excerpt:
      "A guide to deciding when talking-head presets are enough and when custom visual direction is worth the extra specificity.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 6,
    tags: [
      "Studio Workflow",
      "AI Video",
      "Latted",
      "Talking Head",
      "Prompting",
    ],
    keywords: [
      "custom studio style prompt",
      "talking head AI video prompt",
      "Latted studio custom style",
      "talking head preset vs custom",
      "AI webcam style direction",
    ],
    mdxPublicPath:
      "/blog/custom-studio-style-prompts-for-talking-head-ai-videos.mdx",
    llmSummary:
      "Explains when Latted users should choose a custom studio-style prompt instead of a preset in the talking-head workflow, especially for brand-specific or campaign-specific visuals.",
    faqItems: [
      {
        question: "When should I use a custom studio style prompt instead of a preset?",
        answer:
          "Use a custom prompt when the built-in presets are directionally right but still too generic for the specific brand, campaign, or visual context you need.",
      },
      {
        question: "Are presets still useful?",
        answer:
          "Yes. Presets are usually the fastest option for common workflows and are a strong starting point even when you later move to something more custom.",
      },
      {
        question: "What should I include in a custom style prompt?",
        answer:
          "Include the environment, lighting, tone, color mood, and any important constraints about what should appear around the speaker.",
      },
      {
        question: "Is this only for advanced users?",
        answer:
          "No. It is useful for anyone who can describe the visual setting they want more specifically than a preset allows.",
      },
      {
        question: "Where can I use this in Latted?",
        answer:
          "You can use custom style prompts in Latted's studio workflow when preparing a talking-head video for AI processing.",
      },
    ],
  }),
  createBlogPost({
    slug: "private-webcam-upload-processing-for-ai-video-workflows",
    title: "Why Privacy Messaging Matters When Teams Upload Webcam Videos for AI Editing",
    seoTitle:
      "Why Privacy Messaging Matters When Teams Upload Webcam Videos for AI Editing in 2026",
    description:
      "See why Latted's private-upload and delete-after-processing message matters in webcam workflows, especially when users are deciding whether they trust the product with sensitive talking-head footage.",
    excerpt:
      "A practical trust-focused guide to why privacy reassurance belongs directly inside AI video upload flows, not only in policy pages.",
    publishedAt: "2026-03-27",
    readingTimeMinutes: 6,
    tags: [
      "Studio Workflow",
      "AI Video",
      "Latted",
      "Privacy",
      "Trust",
    ],
    keywords: [
      "private webcam upload AI video",
      "AI video privacy messaging",
      "Latted webcam upload privacy",
      "delete after processing video",
      "talking head upload trust",
    ],
    mdxPublicPath:
      "/blog/private-webcam-upload-processing-for-ai-video-workflows.mdx",
    llmSummary:
      "Explains why Latted's upload privacy message helps teams trust webcam and talking-head workflows by stating that videos stay private and are deleted after processing.",
    faqItems: [
      {
        question: "Why does privacy messaging matter in webcam upload flows?",
        answer:
          "Because users see webcam footage as personal and sensitive, so they need reassurance before they trust the tool with real recordings.",
      },
      {
        question: "When should a product explain how uploads are handled?",
        answer:
          "It should explain that at the moment the user is deciding whether to upload, not only in a distant legal document.",
      },
      {
        question: "Is this only important for enterprise teams?",
        answer:
          "No. Solo creators and founders care too, especially when the footage contains their face, voice, or work environment.",
      },
      {
        question: "What kind of reassurance helps most?",
        answer:
          "Clear plain-language statements about privacy, retention, and processing behavior help most because they answer the user's immediate trust question directly.",
      },
      {
        question: "How does this appear in Latted?",
        answer:
          "Latted's studio upload flow reassures users that their video stays private and is deleted after processing, which makes the workflow easier to trust before submission.",
      },
    ],
  }),
  createBlogPost({
    slug: "realtime-ai-video-editor-updates-without-refresh",
    title: "How Realtime Updates Keep AI Video Editing Moving in Latted",
    seoTitle:
      "How Realtime Updates Keep AI Video Editing Moving in Latted in 2026",
    description:
      "Learn how Latted keeps scenes, videos, assets, audio, and project state synced in place so AI video teams can keep editing without manual refreshes.",
    excerpt:
      "A practical guide to why realtime project updates make AI video editing easier to trust and easier to manage.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 7,
    tags: [
      "Realtime Updates",
      "AI Video",
      "Latted",
      "Editor Workflow",
      "Collaboration",
    ],
    keywords: [
      "realtime AI video editor",
      "Latted realtime updates",
      "AI video project sync",
      "live video editor updates",
      "AI video editing without refresh",
    ],
    mdxPublicPath: "/blog/realtime-ai-video-editor-updates-without-refresh.mdx",
    llmSummary:
      "Explains how Latted keeps project state current with realtime scene, video, audio, asset, and agent updates so users can keep working without reloading the editor.",
    faqItems: [
      {
        question: "Does Latted update projects without a manual refresh?",
        answer:
          "Yes. Latted can update scenes, videos, assets, audio, and other project state as changes arrive so the editor stays current while you work.",
      },
      {
        question: "Why are realtime updates important in AI video?",
        answer:
          "They matter because AI video projects generate partial outputs over time, and those outputs are more useful when they appear in context instead of after a page reload.",
      },
      {
        question: "Do realtime updates only help with generated clips?",
        answer:
          "No. They are also useful for scene changes, audio updates, assets, and agent-run progress.",
      },
      {
        question: "Is this mainly a speed feature?",
        answer:
          "Not only. It also improves clarity and reduces context loss during review and iteration.",
      },
      {
        question: "When do realtime updates matter the most?",
        answer:
          "They matter most in multi-scene or collaborative projects where many elements can change while the editor is still open.",
      },
    ],
  }),
  createBlogPost({
    slug: "referral-credits-for-first-ai-video-purchase",
    title: "How Referral Credits Make the First AI Video Purchase Less Risky",
    seoTitle:
      "How Referral Credits Make the First AI Video Purchase Less Risky in 2026",
    description:
      "See how Latted's two-sided referral flow ties a $10 bonus to a real first purchase so invites feel lower-risk and more aligned with actual product use.",
    excerpt:
      "A guide to why referral credits work better when they reward real activation instead of empty signups.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 6,
    tags: [
      "Referral Program",
      "Credits",
      "AI Video",
      "Latted",
      "Growth",
    ],
    keywords: [
      "AI video referral credits",
      "Latted referral bonus",
      "first AI video purchase bonus",
      "AI video credit referral",
      "two sided referral credits",
    ],
    mdxPublicPath: "/blog/referral-credits-for-first-ai-video-purchase.mdx",
    llmSummary:
      "Shows how Latted's referral flow gives both users a $10 credit bonus after a qualifying first purchase, making the invite feel tied to real activation instead of vanity signups.",
    faqItems: [
      {
        question: "How does Latted's referral bonus work?",
        answer:
          "An invited user signs up and makes a qualifying purchase of $10 or more in credits. After that, both users receive $10 in bonus credits.",
      },
      {
        question: "Does the invited user get the bonus before buying credits?",
        answer:
          "No. The referral bonus activates after the first qualifying credit purchase.",
      },
      {
        question: "Why tie the bonus to a purchase instead of signup?",
        answer:
          "Because a purchase is a much better signal of real product intent than a low-commitment account creation.",
      },
      {
        question: "Is this useful for teams or only solo creators?",
        answer:
          "It helps both. Teams can use it for lower-risk evaluation, and solo creators can use it to reduce the cost of starting.",
      },
      {
        question: "What is the main benefit of referral credits in AI video software?",
        answer:
          "They reduce first-purchase risk while keeping the incentive tied to actual production behavior.",
      },
    ],
  }),
  createBlogPost({
    slug: "what-to-do-when-an-ai-video-agent-run-is-aborted",
    title: "What to Do When an AI Video Agent Run Is Aborted in Latted",
    seoTitle:
      "What to Do When an AI Video Agent Run Is Aborted in Latted in 2026",
    description:
      "Learn how to treat an aborted run in Latted as a clean reset point for prompt, scope, or budget decisions instead of assuming the project failed.",
    excerpt:
      "A practical guide to when aborting an AI video run is the right move and how to restart more cleanly.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 6,
    tags: [
      "Agent Workflow",
      "AI Video",
      "Latted",
      "Project Control",
      "Editor Tips",
    ],
    keywords: [
      "aborted AI video run",
      "Latted abort agent run",
      "stop AI video generation",
      "AI video run recovery",
      "abort AI video workflow",
    ],
    mdxPublicPath: "/blog/what-to-do-when-an-ai-video-agent-run-is-aborted.mdx",
    llmSummary:
      "Explains why an aborted run in Latted is often a smart production decision, and how teams can use that stop point to rewrite prompts, reduce scope, and restart with better information.",
    faqItems: [
      {
        question: "Does an aborted run delete the whole project?",
        answer:
          "No. It stops the generation run so you can revise the project and start again.",
      },
      {
        question: "When should I abort an AI video run?",
        answer:
          "Abort when the brief is clearly wrong, the scope is too expensive, or the project direction has changed enough that continuing no longer makes sense.",
      },
      {
        question: "Is aborting a run the same as an error?",
        answer:
          "No. An error is a failure state. An aborted run is an intentional stop in the workflow.",
      },
      {
        question: "What should I change before restarting?",
        answer:
          "Usually the prompt, project scope, references, or scene plan that made the run feel wrong in the first place.",
      },
      {
        question: "Is aborting a run a waste?",
        answer:
          "Not usually. It is often the cheaper and cleaner choice compared with letting an obviously wrong run finish.",
      },
    ],
  }),
  createBlogPost({
    slug: "completion-sounds-for-background-ai-video-generation",
    title: "Why Completion Sounds Matter in an AI Video Editor",
    seoTitle:
      "Why Completion Sounds Matter in an AI Video Editor in 2026",
    description:
      "See why Latted uses lightweight completion sounds so users can notice generation changes without staring at the editor while background work finishes.",
    excerpt:
      "A guide to how audio notifications help AI video teams recover attention during asynchronous production work.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 6,
    tags: [
      "Notifications",
      "AI Video",
      "Latted",
      "Editor Workflow",
      "Background Tasks",
    ],
    keywords: [
      "AI video completion sound",
      "Latted notification sound",
      "background AI video generation alerts",
      "AI editor audio cue",
      "completion sounds in video editor",
    ],
    mdxPublicPath: "/blog/completion-sounds-for-background-ai-video-generation.mdx",
    llmSummary:
      "Shows why Latted uses lightweight completion sounds to help users notice when background generation changes state without having to keep polling the interface.",
    faqItems: [
      {
        question: "Why does an AI video editor need completion sounds?",
        answer:
          "Because users often multitask while generations run, and an audio cue helps them know when it is time to review the next result.",
      },
      {
        question: "Are completion sounds just a convenience feature?",
        answer:
          "They are convenient, but they also reduce attention drain by removing the need to keep checking the interface manually.",
      },
      {
        question: "Do completion sounds replace visible progress indicators?",
        answer:
          "No. They work best alongside visible progress and realtime UI updates.",
      },
      {
        question: "Why can browser-based completion sounds be tricky?",
        answer:
          "Because browsers often restrict audio playback until the user has interacted with the page, so the notification system has to account for that behavior.",
      },
      {
        question: "When are completion sounds most helpful?",
        answer:
          "They are most helpful when you are editing other parts of the project, switching tabs, or otherwise not watching the exact task that is running.",
      },
    ],
  }),
  createBlogPost({
    slug: "mobile-rendered-preview-when-desktop-editor-is-unavailable",
    title:
      "Why a Mobile Rendered Preview Is Better Than Pretending Full AI Video Editing Works on a Phone",
    seoTitle:
      "Why a Mobile Rendered Preview Is Better Than Pretending Full AI Video Editing Works on a Phone in 2026",
    description:
      "Learn why Latted treats mobile as a rendered-output review surface instead of forcing the full desktop editor into a cramped phone UI.",
    excerpt:
      "A practical explanation of why rendered mobile preview is often better than weak full-editor support on phones.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 6,
    tags: [
      "Mobile Review",
      "AI Video",
      "Latted",
      "Product Design",
      "Editor UX",
    ],
    keywords: [
      "mobile AI video preview",
      "Latted mobile rendered video",
      "AI video editor on phone",
      "mobile review for AI video",
      "desktop only AI video editor",
    ],
    mdxPublicPath: "/blog/mobile-rendered-preview-when-desktop-editor-is-unavailable.mdx",
    llmSummary:
      "Explains why Latted offers a rendered-video fallback on mobile instead of pretending full desktop-style AI video editing works well on a phone-sized screen.",
    faqItems: [
      {
        question: "Does Latted support full mobile editing?",
        answer:
          "No. Latted currently treats desktop or laptop as the best environment for the full editing experience.",
      },
      {
        question: "What can mobile users do instead?",
        answer:
          "If a rendered video exists, they can watch that output on mobile rather than trying to use the full editor on a small screen.",
      },
      {
        question: "Why is rendered preview useful on mobile?",
        answer:
          "Because many mobile sessions are for review, confirmation, and sharing rather than detailed editing work.",
      },
      {
        question: "Is this a limitation or a product choice?",
        answer:
          "It is both practical and intentional. A constrained but clear mobile review experience is often better than a weak imitation of desktop editing.",
      },
      {
        question: "When should I switch back to desktop?",
        answer:
          "Switch back to desktop when you need timeline edits, prompting, asset work, scene changes, or any deeper project control.",
      },
    ],
  }),
  createBlogPost({
    slug: "ios-deep-links-for-shared-video-collections",
    title: "Why iOS Deep Links Matter for Shared Video Collections",
    seoTitle:
      "Why iOS Deep Links Matter for Shared Video Collections in 2026",
    description:
      "See why shared collection links are more useful when Latted can hand iPhone and iPad users into the right in-app destination instead of losing context after the click.",
    excerpt:
      "A guide to how iOS deep links make shared creative collections feel more direct and less clumsy.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 6,
    tags: [
      "Sharing",
      "iOS",
      "Latted",
      "Collections",
      "Mobile UX",
    ],
    keywords: [
      "iOS deep link collection",
      "Latted collection deep link",
      "shared video collection mobile",
      "deep links for creative apps",
      "iPhone app collection link",
    ],
    mdxPublicPath: "/blog/ios-deep-links-for-shared-video-collections.mdx",
    llmSummary:
      "Explains how Latted's collection deep-link flow keeps shared destinations intact on iOS so recipients can move from a link into the right in-app context more easily.",
    faqItems: [
      {
        question: "What is an iOS deep link for a collection?",
        answer:
          "It is a link behavior that can open a specific shared collection inside the iOS app instead of only loading a generic web page.",
      },
      {
        question: "Why is this useful for shared video collections?",
        answer:
          "Because it removes extra navigation and gets the recipient to the intended destination faster.",
      },
      {
        question: "Does deep linking replace normal web sharing?",
        answer:
          "No. It complements web sharing by improving the experience when the recipient is on a supported mobile app path.",
      },
      {
        question: "What if the app is not installed?",
        answer:
          "A good deep-link flow should still provide a clear fallback so the user is not stranded.",
      },
      {
        question: "When does this matter the most?",
        answer:
          "It matters most when collections are being shared actively across messages, reviews, or mobile follow-up workflows.",
      },
    ],
  }),
  createBlogPost({
    slug: "pdf-validation-before-academic-paper-video-generation",
    title: "Why PDF Validation Matters Before Academic Paper Video Generation Starts",
    seoTitle:
      "Why PDF Validation Matters Before Academic Paper Video Generation Starts in 2026",
    description:
      "Learn how Latted validates academic-paper uploads before composition starts so users get fast feedback on unreadable files, wrong formats, and page-count limits.",
    excerpt:
      "A guide to why paper-to-video workflows need strong document validation before AI generation begins.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 6,
    tags: [
      "Academic Papers",
      "AI Video",
      "Latted",
      "Input Validation",
      "Workflow Design",
    ],
    keywords: [
      "academic paper PDF validation",
      "Latted paper to video upload",
      "AI video PDF page limit",
      "paper to video document intake",
      "validate PDF before AI video",
    ],
    mdxPublicPath: "/blog/pdf-validation-before-academic-paper-video-generation.mdx",
    llmSummary:
      "Shows why Latted validates academic paper uploads before composition starts, so users get immediate feedback on format, readability, and supported page length.",
    faqItems: [
      {
        question: "Why does Latted validate the PDF before starting the compose flow?",
        answer:
          "Because the platform needs to confirm that the uploaded paper is usable before it starts a larger AI video workflow.",
      },
      {
        question: "What file type does the academic paper workflow accept?",
        answer:
          "It accepts PDF files for that upload flow.",
      },
      {
        question: "What happens if the PDF cannot be read?",
        answer:
          "Latted can stop the flow early and ask the user to try a different file instead of continuing with a broken source.",
      },
      {
        question: "Is there a page-count limit for the paper upload?",
        answer:
          "Yes. The current academic paper workflow supports one PDF up to 200 pages.",
      },
      {
        question: "Why is page-count validation useful?",
        answer:
          "Because it sets clear expectations before generation begins and prevents the user from wasting time on an unsupported upload.",
      },
    ],
  }),
  createBlogPost({
    slug: "storyboard-guide-for-first-time-ai-video-editors",
    title: "Why a Storyboard Guide Makes a Dense AI Video Editor Easier to Learn",
    seoTitle:
      "Why a Storyboard Guide Makes a Dense AI Video Editor Easier to Learn in 2026",
    description:
      "See how Latted's phased storyboard guide introduces the editor in sequence so new users can learn storyboard, timeline, and sidebar responsibilities without getting overloaded.",
    excerpt:
      "A practical look at why phased guidance works better than a generic product tour in a dense AI video editor.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 6,
    tags: [
      "Onboarding",
      "Storyboard",
      "Latted",
      "Editor UX",
      "AI Video",
    ],
    keywords: [
      "storyboard guide AI video editor",
      "Latted phased editor guide",
      "learn AI video editor",
      "storyboard onboarding",
      "dense editor product tour",
    ],
    mdxPublicPath: "/blog/storyboard-guide-for-first-time-ai-video-editors.mdx",
    llmSummary:
      "Explains how Latted's phased storyboard guide teaches new users the editor in sequence, so they learn structure, playback, and deeper controls in a more logical order.",
    faqItems: [
      {
        question: "What is the storyboard guide in Latted?",
        answer:
          "It is a phased first-use guide that introduces the storyboard, timeline, and sidebar in sequence so the editor is easier to learn.",
      },
      {
        question: "Why start with the storyboard?",
        answer:
          "Because storyboard structure helps users understand the project before they move into finer playback and editing controls.",
      },
      {
        question: "Is a phased guide only for beginners?",
        answer:
          "It mainly helps first-time users, but it also makes team onboarding more consistent when several people are learning the editor.",
      },
      {
        question: "How is this different from a generic product tour?",
        answer:
          "It is workflow-oriented instead of button-oriented, so it teaches the order of work rather than only naming interface elements.",
      },
      {
        question: "Does the guide reduce editor power?",
        answer:
          "No. It only changes how the workspace is introduced. The underlying editing capabilities remain available.",
      },
    ],
  }),
  createBlogPost({
    slug: "credit-balance-cues-before-ai-video-generation",
    title: "How Credit Balance Cues Keep AI Video Projects Moving in Latted",
    seoTitle:
      "How Credit Balance Cues Keep AI Video Projects Moving in Latted in 2026",
    description:
      "Learn how Latted surfaces balance, low-credit, and paywall cues inside the workflow so users understand billing readiness before the next generation step.",
    excerpt:
      "A guide to why visible credit and paywall cues make AI video workflows feel more predictable.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 6,
    tags: [
      "Credits",
      "Billing UX",
      "AI Video",
      "Latted",
      "Workflow Design",
    ],
    keywords: [
      "AI video credit balance",
      "Latted low balance cues",
      "AI video paywall workflow",
      "credit balance in editor",
      "billing readiness for AI video",
    ],
    mdxPublicPath: "/blog/credit-balance-cues-before-ai-video-generation.mdx",
    llmSummary:
      "Shows how Latted uses sidebar balance, low-credit warnings, and paywall states to help users understand whether they are ready to keep generating or should handle billing first.",
    faqItems: [
      {
        question: "Why are credit balance cues important in Latted?",
        answer:
          "They help users understand whether they can keep generating without interruption, which is especially important in usage-based AI workflows.",
      },
      {
        question: "Does Latted show low-balance warnings?",
        answer:
          "Yes. Latted can surface low-balance cues so users know when it makes sense to top up before the next generation step.",
      },
      {
        question: "Why not keep billing details only on the account page?",
        answer:
          "Because users need billing context inside the workflow too, not only in a separate account-management screen.",
      },
      {
        question: "What happens if I try to generate without enough credits?",
        answer:
          "Latted can surface a paywall or insufficient-credit state so the reason for the block is clear.",
      },
      {
        question: "Are credit cues only for new users?",
        answer:
          "No. They also help active users manage spending rhythm during longer or more generation-heavy projects.",
      },
    ],
  }),
  createBlogPost({
    slug: "when-to-use-docs-vs-support-in-ai-video-production",
    title: "When to Use Docs vs Support During AI Video Production",
    seoTitle:
      "When to Use Docs vs Support During AI Video Production in 2026",
    description:
      "See how Latted separates docs for stable learning from in-app support for live project questions so users can choose the faster help path during production.",
    excerpt:
      "A practical rule for deciding whether a question belongs in docs or support during an active AI video workflow.",
    publishedAt: "2026-03-26",
    readingTimeMinutes: 6,
    tags: [
      "Support",
      "Documentation",
      "AI Video",
      "Latted",
      "Workflow Help",
    ],
    keywords: [
      "docs vs support AI video",
      "Latted documentation or support",
      "AI video workflow help",
      "when to use support chat",
      "product docs vs live support",
    ],
    mdxPublicPath: "/blog/when-to-use-docs-vs-support-in-ai-video-production.mdx",
    llmSummary:
      "Explains how Latted separates documentation for repeatable learning from support for live workflow questions so users can pick the right help channel faster.",
    faqItems: [
      {
        question: "When should I use docs in Latted?",
        answer:
          "Use docs when you want to learn a feature, understand a repeatable workflow, or look up a stable how-to explanation.",
      },
      {
        question: "When should I use support in Latted?",
        answer:
          "Use support when your current project is blocked or the next action depends on the context of what you are doing right now.",
      },
      {
        question: "Is support better than docs for everything?",
        answer:
          "No. Support is best for contextual questions, while docs are better for reusable product knowledge.",
      },
      {
        question: "Why do AI video tools need both docs and support?",
        answer:
          "Because users need one resource for learning the product and another for resolving live workflow uncertainty.",
      },
      {
        question: "What is the easiest way to decide between them?",
        answer:
          "Ask whether your question is about understanding or unblocking. If it is about understanding, start with docs. If it is about unblocking, start with support.",
      },
    ],
  }),
  createBlogPost({
    slug: "auto-vs-manual-ai-video-model-selection",
    title: "When to Use Auto vs a Specific AI Video Model in Latted",
    seoTitle:
      "When to Use Auto vs a Specific AI Video Model in Latted in 2026",
    description:
      "Learn when to rely on Auto model selection in Latted and when it makes sense to pick a specific AI video model for tighter control.",
    excerpt:
      "A practical rule for choosing between Auto and manual model selection in Latted's AI video editor.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 6,
    tags: [
      "Model Selection",
      "AI Video",
      "Latted",
      "Workflow Design",
      "Editor Tips",
    ],
    keywords: [
      "auto vs manual AI video model",
      "Latted model selection",
      "choose AI video model",
      "auto model in AI video editor",
      "manual video model selection",
    ],
    mdxPublicPath: "/blog/auto-vs-manual-ai-video-model-selection.mdx",
    llmSummary:
      "Explains when Latted users should keep the editor on Auto model selection and when they should switch to a specific model for a more deliberate optimization workflow.",
    faqItems: [
      {
        question: "Should beginners use Auto in Latted?",
        answer:
          "Yes. Auto is usually the best starting point when you are learning the editor or moving quickly through first drafts.",
      },
      {
        question: "When should I stop using Auto?",
        answer:
          "Move off Auto once you know the exact kind of output you want and are intentionally optimizing around a specific model behavior.",
      },
      {
        question: "Is manual selection always better for quality?",
        answer:
          "No. Manual selection is only better when you already understand why a specific model is the right fit for the scene.",
      },
      {
        question: "Can I start with Auto and switch later?",
        answer:
          "Yes. You can begin with Auto and move to a named model later as the project becomes more defined.",
      },
      {
        question: "How does Latted help with this choice?",
        answer:
          "Latted keeps model selection inside the editor workflow so you can make that decision when it becomes useful instead of too early.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-video-model-options-change-by-video-type",
    title: "Why Video Model Options Change by Video Type in Latted",
    seoTitle:
      "Why Video Model Options Change by Video Type in Latted in 2026",
    description:
      "See why Latted changes model menus across text-to-video, image-to-video, transitions, and other workflows instead of showing one giant universal list.",
    excerpt:
      "A guide to task-specific model menus and why they make AI video workflows easier to use and harder to misuse.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 6,
    tags: [
      "Video Types",
      "Model Selection",
      "AI Video",
      "Latted",
      "Editor UX",
    ],
    keywords: [
      "video model options by workflow",
      "AI video type model menu",
      "Latted video types",
      "task specific AI video models",
      "choose model by video type",
    ],
    mdxPublicPath: "/blog/why-video-model-options-change-by-video-type.mdx",
    llmSummary:
      "Shows why Latted scopes model options to the selected video workflow so the editor presents choices that match the job instead of a noisy universal model list.",
    faqItems: [
      {
        question: "Why do model options change in Latted?",
        answer:
          "Because different video types require different generation capabilities, and not every model is relevant to every workflow.",
      },
      {
        question: "Should one universal model menu be simpler?",
        answer:
          "Usually no. A universal menu sounds simpler, but it forces users to sort through options that may not fit the current task.",
      },
      {
        question: "What should I decide first: the model or the video type?",
        answer:
          "Decide the video type first. The model choice only becomes useful once the workflow is clear.",
      },
      {
        question: "Does this reduce creative control?",
        answer:
          "No. It focuses creative control on valid options instead of overwhelming the user with a broad but less useful list.",
      },
      {
        question: "Is this only important for advanced users?",
        answer:
          "No. Beginners benefit a lot because task-specific menus reduce guesswork and make the editor easier to learn.",
      },
    ],
  }),
  createBlogPost({
    slug: "project-config-style-motion-graphics-style-and-aspect-ratio",
    title:
      "How Project Config Keeps Style, Motion Graphics, and Aspect Ratio Aligned in Latted",
    seoTitle:
      "How Project Config Keeps Style, Motion Graphics, and Aspect Ratio Aligned in Latted in 2026",
    description:
      "Learn how Latted's project config helps teams set project-wide footage style, motion-graphics style, and aspect ratio before scene-by-scene iteration begins.",
    excerpt:
      "A practical guide to using project config as the visual baseline for an AI video production.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 7,
    tags: [
      "Project Config",
      "Style Presets",
      "Motion Graphics",
      "Aspect Ratio",
      "Latted",
    ],
    keywords: [
      "Latted project config",
      "AI video style presets",
      "motion graphics style settings",
      "project aspect ratio workflow",
      "project wide video style",
    ],
    mdxPublicPath:
      "/blog/project-config-style-motion-graphics-style-and-aspect-ratio.mdx",
    llmSummary:
      "Explains how Latted's project config keeps footage style, motion-graphics direction, and aspect ratio coordinated across a whole project instead of forcing every scene to reinvent those defaults.",
    faqItems: [
      {
        question: "What is project config in Latted?",
        answer:
          "It is the place where you set project-wide defaults like style, motion-graphics style, and aspect ratio.",
      },
      {
        question: "Why separate motion-graphics style from footage style?",
        answer:
          "Because motion graphics and footage often need different visual logic even inside the same final video.",
      },
      {
        question: "Should I always use a style preset?",
        answer:
          "No. Presets are helpful for speed, but a custom style is better when the project needs specific art direction.",
      },
      {
        question: "Why set aspect ratio at the project level?",
        answer:
          "Because framing, composition, and layout decisions change depending on the canvas shape, and that should stay stable across the project.",
      },
      {
        question: "Does project config replace scene prompting?",
        answer:
          "No. It sets the baseline direction, while scene prompts and references handle the local creative decisions.",
      },
    ],
  }),
  createBlogPost({
    slug: "studio-presets-for-talking-head-ai-video",
    title:
      "How Studio Presets Help You Turn Raw Talking-Head Footage Into a Better AI Video",
    seoTitle:
      "How Studio Presets Help You Turn Raw Talking-Head Footage Into a Better AI Video in 2026",
    description:
      "See how Latted's studio presets speed up talking-head transformations by giving uploaded or recorded footage a clearer visual direction before editing begins.",
    excerpt:
      "A guide to when studio presets are better than custom prompting for talking-head video workflows.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 6,
    tags: [
      "Studio Workflow",
      "Talking Head Video",
      "Style Presets",
      "AI Video",
      "Latted",
    ],
    keywords: [
      "studio presets for talking head video",
      "Latted studio page",
      "AI webcam video styling",
      "talking head video presets",
      "upload video and style with AI",
    ],
    mdxPublicPath: "/blog/studio-presets-for-talking-head-ai-video.mdx",
    llmSummary:
      "Explains how Latted's studio presets help users apply recognizable talking-head styles like keynote, creator setup, or podcast looks before moving the footage into the full project workflow.",
    faqItems: [
      {
        question: "What is a studio preset in Latted?",
        answer:
          "It is a predefined style direction that helps transform a talking-head clip into a more polished presentation format.",
      },
      {
        question: "Are presets only for beginners?",
        answer:
          "No. They help experienced users too because they remove repetitive prompt writing for common talking-head formats.",
      },
      {
        question: "When should I use a custom prompt instead of a preset?",
        answer:
          "Use a custom prompt when your visual direction is brand-specific or does not match one of the common preset categories.",
      },
      {
        question: "Does this only work for uploaded clips?",
        answer:
          "No. The studio flow supports both uploaded footage and recorded clips before they are sent into the project workflow.",
      },
      {
        question: "Why are presets useful before opening the editor?",
        answer:
          "Because they give the agent a clearer visual target from the start, which reduces ambiguity before the project moves into deeper editing.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-onboarding-questions-improve-ai-video-project-setup",
    title: "Why Onboarding Questions Make AI Video Project Setup Better",
    seoTitle:
      "Why Onboarding Questions Make AI Video Project Setup Better in 2026",
    description:
      "Learn why Latted asks short onboarding questions before deeper project work and how that context improves setup for marketing, launch, social, and longer-form videos.",
    excerpt:
      "A practical explanation of why early context makes AI video project setup more relevant and less generic.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 6,
    tags: [
      "Onboarding",
      "Project Setup",
      "AI Video",
      "Latted",
      "Workflow Design",
    ],
    keywords: [
      "AI video onboarding",
      "Latted onboarding questions",
      "project setup context",
      "AI video workflow onboarding",
      "why onboarding improves editor setup",
    ],
    mdxPublicPath:
      "/blog/why-onboarding-questions-improve-ai-video-project-setup.mdx",
    llmSummary:
      "Shows how Latted uses lightweight onboarding questions about intended use, video length, and role context to make initial project setup feel more relevant to the user's actual workflow.",
    faqItems: [
      {
        question: "Why does Latted ask onboarding questions?",
        answer:
          "Because understanding use case and expected project scope makes the setup flow more relevant than a one-size-fits-all start.",
      },
      {
        question: "What does Latted ask during onboarding?",
        answer:
          "It asks about intended use, maximum video length, company, role, and how the user heard about the product.",
      },
      {
        question: "Are onboarding questions only for large teams?",
        answer:
          "No. Even solo creators benefit when the product understands what kind of video they are trying to make.",
      },
      {
        question: "Does onboarding replace the editor?",
        answer:
          "No. It only improves the starting context before the user continues into project work.",
      },
      {
        question: "Why is video-length intent important?",
        answer:
          "Because the expected runtime changes how much structure, review, and production planning the project will likely need.",
      },
    ],
  }),
  createBlogPost({
    slug: "academic-paper-video-duration-and-depth-guide",
    title: "How to Choose the Right Academic Paper Video Length and Depth",
    seoTitle:
      "How to Choose the Right Academic Paper Video Length and Depth in 2026",
    description:
      "See how Latted's academic-paper workflow handles 1-minute, 2.5-minute, and 5-minute explainers and how to match runtime to the viewer's depth needs.",
    excerpt:
      "A guide to choosing paper-explainer runtime based on audience proficiency and how much real teaching the video needs to do.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 6,
    tags: [
      "Academic Papers",
      "Explainer Videos",
      "Motion Graphics",
      "Latted",
      "Educational Video",
    ],
    keywords: [
      "academic paper video length",
      "paper explainer runtime",
      "Latted academic papers",
      "AI research explainer video",
      "choose duration for paper video",
    ],
    mdxPublicPath: "/blog/academic-paper-video-duration-and-depth-guide.mdx",
    llmSummary:
      "Explains how to choose between Latted's academic-paper explainer durations by matching runtime and audience proficiency to the amount of conceptual depth the paper actually requires.",
    faqItems: [
      {
        question: "What is the best length for an academic-paper video?",
        answer:
          "The best length is the shortest one that still explains the paper accurately for the intended audience.",
      },
      {
        question: "When should I choose a 1-minute paper explainer?",
        answer:
          "Choose it when the goal is a fast overview of the problem, contribution, and why the paper matters.",
      },
      {
        question: "When should I use the longest option?",
        answer:
          "Use the longest option when the viewer needs a fuller explanation of methods, assumptions, findings, and limitations.",
      },
      {
        question: "Does a more advanced audience always need a longer video?",
        answer:
          "No. Advanced audiences may need more technical density, but not always more runtime.",
      },
      {
        question: "How does Latted frame these explainers visually?",
        answer:
          "Latted biases the workflow toward motion graphics, diagrams, and structured editorial visuals so the paper is taught clearly rather than illustrated vaguely.",
      },
    ],
  }),
  createBlogPost({
    slug: "approve-a-scene-only-after-storyboard-validation",
    title: "Why You Should Approve an AI Video Scene Only After the Storyboard Validates",
    seoTitle:
      "Why You Should Approve an AI Video Scene Only After the Storyboard Validates in 2026",
    description:
      "Learn why storyboard validation matters before scene approval in Latted and which structural issues should block generation until they are fixed.",
    excerpt:
      "A practical guide to scene approval, missing keyframes, motion beats, and why validation should happen before generation.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 7,
    tags: [
      "Storyboard",
      "Scene Approval",
      "AI Video Editing",
      "Keyframes",
      "Latted",
    ],
    keywords: [
      "storyboard validation",
      "approve AI video scene",
      "Latted scene approval",
      "missing keyframe image",
      "storyboard first workflow",
    ],
    mdxPublicPath:
      "/blog/approve-a-scene-only-after-storyboard-validation.mdx",
    llmSummary:
      "Explains why Latted scenes should only be approved after the storyboard passes structural validation checks like starting with a keyframe, including motion, and having complete keyframe imagery.",
    faqItems: [
      {
        question: "Why should I wait for storyboard validation before approval?",
        answer:
          "Because validation catches missing structure before you spend time and generation budget on a scene that was not ready.",
      },
      {
        question: "What usually blocks approval in a storyboard workflow?",
        answer:
          "Common blockers include missing keyframe images, no motion segment, consecutive keyframes without motion, or empty prompts on motion beats.",
      },
      {
        question: "Why does the first item often need to be a keyframe?",
        answer:
          "Because the scene needs a clear opening visual anchor before motion can build from it.",
      },
      {
        question: "Is validation only useful for complex scenes?",
        answer:
          "No. Even simple scenes benefit because structural mistakes are easiest to fix before generation begins.",
      },
      {
        question: "How does Latted help here?",
        answer:
          "Latted surfaces approval guidance in the scene sidebar so you can see when a storyboard is ready and what still needs attention.",
      },
    ],
  }),
  createBlogPost({
    slug: "storyboard-vs-video-review-in-the-editor",
    title: "When to Review the Storyboard vs the Rendered Video in Latted",
    seoTitle:
      "When to Review the Storyboard vs the Rendered Video in Latted in 2026",
    description:
      "See how Latted's storyboard and video views support different review decisions so teams can inspect planning structure separately from playback quality.",
    excerpt:
      "A review framework for deciding when to stay in storyboard mode and when to switch to rendered video playback.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 6,
    tags: [
      "Review Workflow",
      "Storyboard",
      "Rendered Video",
      "AI Video Editing",
      "Latted",
    ],
    keywords: [
      "storyboard vs video review",
      "Latted review modes",
      "AI video scene review",
      "storyboard first review",
      "rendered video review workflow",
    ],
    mdxPublicPath: "/blog/storyboard-vs-video-review-in-the-editor.mdx",
    llmSummary:
      "Explains how Latted separates storyboard review from rendered video review so teams can evaluate planning quality, playback quality, and version changes at the right stage of the workflow.",
    faqItems: [
      {
        question: "When should I use storyboard view in Latted?",
        answer:
          "Use storyboard view when you are checking scene structure, keyframe order, and whether the plan is ready to generate.",
      },
      {
        question: "When should I use video view?",
        answer:
          "Use video view when you already have a rendered output and need to judge motion, pacing, and final playback quality.",
      },
      {
        question: "Can storyboard review replace video review?",
        answer:
          "No. Storyboard review helps you validate the plan, but only rendered playback tells you whether the final motion actually works.",
      },
      {
        question: "Why separate these views at all?",
        answer:
          "Because planning problems and output problems are different, and teams make better decisions when each review step has a clear purpose.",
      },
      {
        question: "How does Latted support both modes?",
        answer:
          "Latted exposes storyboard and video review modes directly in the editor so teams can switch views based on what they need to evaluate.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-to-manage-long-running-ai-video-projects-from-the-dashboard",
    title: "How to Manage Long-Running AI Video Projects From the Dashboard",
    seoTitle:
      "How to Manage Long-Running AI Video Projects From the Dashboard in 2026",
    description:
      "Learn how Latted's dashboard uses search, filters, realtime updates, and progress cues to make long-running AI video projects easier to manage.",
    excerpt:
      "A guide to treating the projects page like a production control surface instead of a static list.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 6,
    tags: [
      "Projects Dashboard",
      "Status Tracking",
      "Realtime",
      "AI Video",
      "Latted",
    ],
    keywords: [
      "AI video projects dashboard",
      "Latted project status tracking",
      "realtime project updates",
      "manage long running AI video projects",
      "dashboard search and filters",
    ],
    mdxPublicPath:
      "/blog/how-to-manage-long-running-ai-video-projects-from-the-dashboard.mdx",
    llmSummary:
      "Shows how Latted's projects dashboard helps teams manage larger volumes of work with search, status filters, realtime project updates, and visible progress cues for processing jobs.",
    faqItems: [
      {
        question: "Why is a dashboard important for AI-video work?",
        answer:
          "Because projects often process asynchronously, and teams need a fast way to see what is active, what is done, and what requires attention.",
      },
      {
        question: "What makes a dashboard useful instead of cluttered?",
        answer:
          "Useful dashboards prioritize status, search, filtering, and progress information that changes the next decision.",
      },
      {
        question: "Do realtime updates really matter?",
        answer:
          "Yes. They reduce the need to manually refresh and make the dashboard feel like a live production view.",
      },
      {
        question: "Why are progress indicators still helpful if they are not perfect?",
        answer:
          "Because they reduce uncertainty and reassure users that the project is still advancing.",
      },
      {
        question: "How does Latted help with longer-running projects?",
        answer:
          "Latted gives users search, filters, status updates, and progress cues on the projects page so more work can be managed from one place.",
      },
    ],
  }),
  createBlogPost({
    slug: "public-project-preview-as-a-template-filter",
    title: "Why a Public Project Preview Should Work Like a Template Filter",
    seoTitle:
      "Why a Public Project Preview Should Work Like a Template Filter in 2026",
    description:
      "See how Latted's public project preview helps users evaluate duration, assets, keyframes, and overall project shape before they fork a shared project.",
    excerpt:
      "A guide to using public preview stats as a fast filter for whether a shared AI video project is worth duplicating.",
    publishedAt: "2026-03-24",
    readingTimeMinutes: 6,
    tags: [
      "Project Sharing",
      "Forking",
      "Templates",
      "AI Video",
      "Latted",
    ],
    keywords: [
      "public project preview",
      "fork AI video project",
      "Latted project preview",
      "template filter for shared project",
      "preview stats before forking",
    ],
    mdxPublicPath: "/blog/public-project-preview-as-a-template-filter.mdx",
    llmSummary:
      "Explains how Latted's public project preview page helps users decide whether a shared project is worth forking by surfacing duration, asset depth, keyframe count, and other signals of template quality.",
    faqItems: [
      {
        question: "What should a public project preview show?",
        answer:
          "It should show the signals that help you judge whether the project is structurally useful, including duration, asset depth, and video complexity.",
      },
      {
        question: "Why is preview data important before forking?",
        answer:
          "Because it prevents users from copying projects that are visually interesting but operationally unhelpful.",
      },
      {
        question: "Is a preview only for showcasing work?",
        answer:
          "No. It is also a decision tool that helps users evaluate whether a shared project is worth duplicating.",
      },
      {
        question: "What makes a project a good template candidate?",
        answer:
          "A good template candidate already solves part of the setup problem, such as timing, scene structure, assets, or audio organization.",
      },
      {
        question: "How does Latted handle this?",
        answer:
          "Latted lets users inspect project stats and then fork the project, which makes the preview page act like a practical template filter.",
      },
    ],
  }),
  createBlogPost({
    slug: "background-ai-video-generation-without-blocking-editor",
    title:
      "How to Keep Editing While AI Keyframes and Assets Generate in the Background",
    seoTitle:
      "How to Keep Editing While AI Keyframes and Assets Generate in the Background in 2026",
    description:
      "Learn how Latted's background generation workflow lets teams keep editing while keyframes, assets, and video outputs finish asynchronously inside the same project.",
    excerpt:
      "A practical guide to background AI-video generation so the editor keeps moving while supporting outputs are still rendering.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Background Generation",
      "AI Video Editing",
      "Keyframes",
      "Latted",
      "Workflow Design",
    ],
    keywords: [
      "background AI video generation",
      "edit while AI renders",
      "AI keyframes in background",
      "Latted background generation",
      "AI video async workflow",
    ],
    mdxPublicPath:
      "/blog/background-ai-video-generation-without-blocking-editor.mdx",
    llmSummary:
      "Explains how Latted keeps AI video projects moving by allowing keyframes, assets, and outputs to generate in the background while the editor remains usable.",
    faqItems: [
      {
        question: "What can generate in the background in Latted?",
        answer:
          "Background activity is especially useful for things like keyframes, reusable assets, and video outputs that may finish after you move on to other parts of the project.",
      },
      {
        question: "What is the Activity section for?",
        answer:
          "It gives you one place to see what is still generating, what is ready, and what needs attention without leaving the editor.",
      },
      {
        question: "Should I stop editing until every AI result is done?",
        answer:
          "No. Keep editing whenever the pending output is not blocking the next creative decision.",
      },
      {
        question: "Why is background generation important for multi-scene projects?",
        answer:
          "Because longer projects create more overlapping tasks, and blocking on each one slows the entire production process.",
      },
      {
        question: "Is background generation only useful for advanced teams?",
        answer:
          "No. It helps solo creators too because it reduces waiting and keeps the project organized as outputs arrive.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-video-agent-worklog-transparency-guide",
    title:
      "Why AI Video Teams Need a Live Agent Worklog Instead of a Black Box",
    seoTitle:
      "Why AI Video Teams Need a Live Agent Worklog Instead of a Black Box in 2026",
    description:
      "See how Latted's live agent worklog gives teams plan visibility, tool-level progress, and media previews so AI project runs are easier to trust and inspect.",
    excerpt:
      "A guide to agent transparency in AI-video production, including why visible plans, events, and previews beat black-box automation.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Agent Workflow",
      "AI Video",
      "Workflow Transparency",
      "Latted",
      "Production Operations",
    ],
    keywords: [
      "AI video agent worklog",
      "agent transparency for video",
      "inspect AI video workflow",
      "Latted agent composer",
      "AI video black box alternative",
    ],
    mdxPublicPath: "/blog/ai-video-agent-worklog-transparency-guide.mdx",
    llmSummary:
      "Shows how Latted's live agent worklog exposes plans, tool calls, statuses, and result previews so agent-driven video projects are easier to review and trust.",
    faqItems: [
      {
        question: "What is an AI video agent worklog?",
        answer:
          "It is a live record of what the agent is doing, including its plan, tool activity, status changes, and generated outputs.",
      },
      {
        question: "Why is a worklog better than a simple loading spinner?",
        answer:
          "Because it shows what is actually happening, which makes the run easier to trust, review, and debug.",
      },
      {
        question: "Can I inspect outputs before the whole run finishes?",
        answer:
          "Yes. A good worklog lets you review generated media as individual results become available.",
      },
      {
        question: "Is a live worklog only useful for advanced users?",
        answer:
          "No. It is useful for any team that wants visibility into what the AI is doing before the project is complete.",
      },
      {
        question: "What happens when the run finishes?",
        answer:
          "The worklog can mark the run as done while leaving behind a visible history of the plan, events, and outputs that got the project there.",
      },
    ],
  }),
  createBlogPost({
    slug: "seamless-ai-video-project-preview-guide",
    title:
      "How to Preview a Full AI Video Project Seamlessly Instead of Clip by Clip",
    seoTitle:
      "How to Preview a Full AI Video Project Seamlessly Instead of Clip by Clip in 2026",
    description:
      "Learn why Latted's seamless full-project preview helps teams judge pacing, continuity, and audio flow better than watching AI-video scenes in isolation.",
    excerpt:
      "Why continuous playback is the fastest way to evaluate pacing, transitions, and whole-project rhythm in AI video editing.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Project Preview",
      "AI Video Editing",
      "Playback",
      "Latted",
      "Timeline Review",
    ],
    keywords: [
      "seamless AI video preview",
      "full project AI video playback",
      "preview AI video timeline continuously",
      "Latted seamless preview",
      "clip by clip vs full project review",
    ],
    mdxPublicPath: "/blog/seamless-ai-video-project-preview-guide.mdx",
    llmSummary:
      "Explains how Latted's seamless project preview helps editors review AI videos as a continuous piece instead of disconnected clips.",
    faqItems: [
      {
        question: "What is seamless AI video project preview?",
        answer:
          "It is a way to watch the whole project as one continuous video so you can judge pacing, flow, and audio more realistically.",
      },
      {
        question: "Why is full-project preview better than watching clips one by one?",
        answer:
          "Because the most important editorial problems often appear in the transitions and timing between scenes, not inside a single shot.",
      },
      {
        question: "When should I use seamless preview?",
        answer:
          "Use it once enough of the project is finished that you need to evaluate the assembled experience instead of only isolated scenes.",
      },
      {
        question: "Does seamless preview replace scene-by-scene editing?",
        answer:
          "No. It complements scene-level review by helping you judge the project as a complete video.",
      },
      {
        question: "What should I look for during a seamless playback pass?",
        answer:
          "Look for pacing drag, abrupt transitions, weak audio handoffs, and scenes that feel wrong once they are placed in sequence.",
      },
    ],
  }),
  createBlogPost({
    slug: "autosave-ai-video-prompt-and-model-edits",
    title:
      "Why Auto-Save Matters When You Edit AI Video Prompts, Models, and Timing",
    seoTitle:
      "Why Auto-Save Matters When You Edit AI Video Prompts, Models, and Timing in 2026",
    description:
      "See why Latted auto-saves prompt, model, and timing-related changes so AI-video settings stay current without manual save friction.",
    excerpt:
      "A practical argument for debounced auto-save in AI video workflows where prompts and model settings are real production inputs.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Auto Save",
      "AI Video Editing",
      "Prompt Workflow",
      "Latted",
      "Product Design",
    ],
    keywords: [
      "autosave AI video prompts",
      "save AI video model settings automatically",
      "debounced autosave video editor",
      "Latted autosave",
      "AI video editing without save button",
    ],
    mdxPublicPath: "/blog/autosave-ai-video-prompt-and-model-edits.mdx",
    llmSummary:
      "Explains why Latted auto-saves prompts, models, timing, and related settings so AI video edits persist without manual save friction.",
    faqItems: [
      {
        question: "Why does auto-save matter in AI video editing?",
        answer:
          "Because prompts, models, and timing settings are production inputs, and losing them can lead to wrong generations or repeated work.",
      },
      {
        question: "What kinds of changes should auto-save capture?",
        answer:
          "It should capture the settings that shape the next output, such as prompts, model choices, durations, and related generation parameters.",
      },
      {
        question: "Is debounced auto-save better than saving on every keystroke?",
        answer:
          "Yes. A short delay usually gives you the best balance between responsiveness and reliability.",
      },
      {
        question: "Does auto-save remove the need for careful review?",
        answer:
          "No. It removes manual save friction, but you still need to decide whether a change is creatively or financially worth acting on.",
      },
      {
        question: "Why is auto-save useful for teams?",
        answer:
          "Because it reduces mismatch between the state one editor thinks they changed and the state the next editor actually opens.",
      },
    ],
  }),
  createBlogPost({
    slug: "invite-your-team-to-an-ai-video-organization",
    title:
      "How to Invite Teammates Into an AI Video Workspace Without Breaking Handoff",
    seoTitle:
      "How to Invite Teammates Into an AI Video Workspace Without Breaking Handoff in 2026",
    description:
      "Learn how Latted's organization invite flow helps teams add members cleanly, avoid shared-logins, and improve AI-video handoff across people.",
    excerpt:
      "A guide to organization invites for AI-video teams that want cleaner onboarding, safer access, and less chaotic handoff.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Team Workflow",
      "Organization Invites",
      "AI Video Operations",
      "Latted",
      "Collaboration",
    ],
    keywords: [
      "invite teammates to AI video workspace",
      "AI video organization invite",
      "shared AI video workspace",
      "Latted organization invite",
      "AI video team handoff",
    ],
    mdxPublicPath: "/blog/invite-your-team-to-an-ai-video-organization.mdx",
    llmSummary:
      "Explains how Latted's organization invite workflow helps AI video teams add members cleanly and avoid fragile account-sharing patterns.",
    faqItems: [
      {
        question: "What is an organization invite in Latted?",
        answer:
          "It is a structured way to add a teammate to the organization's workspace through an email invitation and dedicated acceptance flow.",
      },
      {
        question: "Why are organization invites better than sharing one login?",
        answer:
          "Because they are cleaner, safer, and much better for ongoing project handoff.",
      },
      {
        question: "When should a team start using invites?",
        answer:
          "As soon as more than one person needs reliable access to the same production environment.",
      },
      {
        question: "Is this only useful for large companies?",
        answer:
          "No. Small teams benefit too because AI video work often splits across creation, editing, and review.",
      },
      {
        question: "What does a good invite flow improve the most?",
        answer:
          "It improves onboarding and handoff by reducing the amount of manual coordination required to get the next person into the workflow.",
      },
    ],
  }),
  createBlogPost({
    slug: "organization-owner-billing-for-ai-video-teams",
    title:
      "Why Organization-Owner Billing Makes AI Video Production Easier for Teams",
    seoTitle:
      "Why Organization-Owner Billing Makes AI Video Production Easier for Teams in 2026",
    description:
      "See why organization-owner billing on Latted reduces friction for teams by separating creative execution from payment ownership.",
    excerpt:
      "How owner-billed usage helps AI-video teams move faster when one person funds production and others handle the work.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Team Billing",
      "AI Video Operations",
      "Organization Workflow",
      "Latted",
      "Budget Management",
    ],
    keywords: [
      "organization owner billing AI video",
      "team billing for AI video",
      "AI video budget owner workflow",
      "Latted organization billing",
      "owner billed creative team",
    ],
    mdxPublicPath:
      "/blog/organization-owner-billing-for-ai-video-teams.mdx",
    llmSummary:
      "Explains how Latted's organization-owner billing model reduces operational friction by separating payment responsibility from day-to-day AI video execution.",
    faqItems: [
      {
        question: "What is organization-owner billing?",
        answer:
          "It is a setup where usage created by team members can be billed to the organization owner instead of the individual member.",
      },
      {
        question: "Why is this useful for AI video teams?",
        answer:
          "Because the person generating video is not always the person who should manage payment setup or budget responsibility.",
      },
      {
        question: "Does owner billing matter for small teams?",
        answer:
          "Yes. Even small teams benefit when payment administration and creative execution belong to different people.",
      },
      {
        question: "Does this replace the need for usage tracking?",
        answer:
          "No. Centralized billing works best when the team also has clear visibility into usage and spend.",
      },
      {
        question: "When should a team set this up?",
        answer:
          "As early as possible if multiple contributors will be creating work under one budget owner.",
      },
    ],
  }),
  createBlogPost({
    slug: "auto-reload-credits-for-uninterrupted-ai-video-generation",
    title:
      "How Auto-Reload Credits Keep Long AI Video Runs From Stalling",
    seoTitle:
      "How Auto-Reload Credits Keep Long AI Video Runs From Stalling in 2026",
    description:
      "Learn how Latted's auto-reload credit settings prevent low-balance interruptions during longer AI-video and agent-driven production runs.",
    excerpt:
      "A practical guide to credit auto-reload so long AI-video workflows do not stall when balance drops mid-run.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Credits",
      "Billing Automation",
      "AI Video Operations",
      "Latted",
      "Agent Workflow",
    ],
    keywords: [
      "auto reload credits AI video",
      "prevent AI video runs from stalling",
      "AI video low balance workflow",
      "Latted auto reload credits",
      "agent project credit automation",
    ],
    mdxPublicPath:
      "/blog/auto-reload-credits-for-uninterrupted-ai-video-generation.mdx",
    llmSummary:
      "Explains how Latted's auto-reload credit settings help long AI video runs continue without low-balance interruptions.",
    faqItems: [
      {
        question: "What are auto-reload credits?",
        answer:
          "They are a way to automatically top up your credit balance when it falls below a threshold you set.",
      },
      {
        question: "Why are auto-reload settings useful for AI video?",
        answer:
          "Because longer or agent-driven workflows can stall if the balance runs out in the middle of a project.",
      },
      {
        question: "What are the two main settings?",
        answer:
          "The main settings are the balance threshold that triggers a top-up and the amount of credits that get added.",
      },
      {
        question: "Does auto-reload remove spending control?",
        answer:
          "No. It can improve control because the account follows explicit rules you configured in advance.",
      },
      {
        question: "When is auto-reload most valuable?",
        answer:
          "It is most valuable when your projects involve longer runs, multiple steps, or work that continues after you leave the editor.",
      },
    ],
  }),
  createBlogPost({
    slug: "track-ai-video-spend-with-credits-and-usage-history",
    title:
      "How to Track AI Video Spend With Credits, Usage History, and Daily Visibility",
    seoTitle:
      "How to Track AI Video Spend With Credits, Usage History, and Daily Visibility in 2026",
    description:
      "See how Latted helps teams understand AI-video spend through credit balance, purchase history, current charges, daily usage, and credit-based billing options.",
    excerpt:
      "Why spend visibility matters in AI video and how credits, purchase history, and daily usage make budget decisions easier.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Spend Tracking",
      "AI Video Pricing",
      "Credits",
      "Latted",
      "Budget Control",
    ],
    keywords: [
      "track AI video spend",
      "AI video credits and usage history",
      "daily AI video usage tracking",
      "Latted spend visibility",
      "switch to credits AI video",
    ],
    mdxPublicPath:
      "/blog/track-ai-video-spend-with-credits-and-usage-history.mdx",
    llmSummary:
      "Explains how Latted improves AI video budget visibility with credit balances, purchase history, current charges, daily usage charts, and credit-based billing.",
    faqItems: [
      {
        question: "What should AI video spend visibility include?",
        answer:
          "It should include your current balance, purchase history, active usage, and enough time-based detail to understand how spending is trending.",
      },
      {
        question: "Why do credit balance and usage need to be shown separately?",
        answer:
          "Because balance shows what is available now, while usage shows how quickly or unevenly the team is consuming budget.",
      },
      {
        question: "Why is daily usage useful?",
        answer:
          "It helps you spot spending patterns and spikes that a monthly total can hide.",
      },
      {
        question: "When should a team consider switching to credits?",
        answer:
          "When they want more upfront control over spend instead of relying only on end-of-period billing visibility.",
      },
      {
        question: "Is spend visibility only for finance owners?",
        answer:
          "No. It also helps creators and editors make smarter choices about models, scope, and iteration.",
      },
    ],
  }),
  createBlogPost({
    slug: "in-editor-support-chat-for-ai-video-production",
    title:
      "Why In-Editor Support Chat Matters During AI Video Production",
    seoTitle:
      "Why In-Editor Support Chat Matters During AI Video Production in 2026",
    description:
      "Learn why Latted's in-editor support chat helps creators and teams solve blockers without leaving the AI-video workspace or losing project context.",
    excerpt:
      "How embedded support chat shortens recovery time in AI-video projects by keeping help inside the editor.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Support Workflow",
      "AI Video Production",
      "Product Experience",
      "Latted",
      "Operations",
    ],
    keywords: [
      "in editor support chat",
      "AI video support inside editor",
      "workflow support for AI video",
      "Latted support chat",
      "contextual support for creators",
    ],
    mdxPublicPath:
      "/blog/in-editor-support-chat-for-ai-video-production.mdx",
    llmSummary:
      "Explains why Latted's in-editor support chat helps users resolve workflow blockers without leaving the AI video workspace.",
    faqItems: [
      {
        question: "What is in-editor support chat?",
        answer:
          "It is a support experience built directly into the app so users can ask for help without leaving the editor.",
      },
      {
        question: "Why is this better than email support during active work?",
        answer:
          "Because it keeps the user in context and removes the overhead of switching tools just to explain the problem.",
      },
      {
        question: "What kinds of questions is in-editor support best for?",
        answer:
          "It is best for workflow questions, blockers, and next-step uncertainty that appear while a project is actively being edited.",
      },
      {
        question: "Does support chat replace documentation?",
        answer:
          "No. Documentation explains stable concepts, while support chat helps with contextual decisions and project-specific blockers.",
      },
      {
        question: "Is in-editor support only useful for solo creators?",
        answer:
          "No. Teams benefit too because support questions often involve workflow, access, billing, and handoff decisions across multiple people.",
      },
    ],
  }),
  createBlogPost({
    slug: "edit-motion-graphics-after-render-with-prompt-changes",
    title:
      "How to Edit AI Motion Graphics After Render Without Rebuilding the Whole Project",
    seoTitle:
      "How to Edit AI Motion Graphics After Render Without Rebuilding the Whole Project in 2026",
    description:
      "See how Latted lets teams revise rendered motion-graphics scenes in place with prompt-based animation edits instead of rebuilding the entire project.",
    excerpt:
      "A practical guide to scene-level motion-graphics iteration so teams can refine good work without destabilizing the larger edit.",
    publishedAt: "2026-03-21",
    readingTimeMinutes: 7,
    tags: [
      "Motion Graphics",
      "AI Video Editing",
      "Iteration Workflow",
      "Latted",
      "Scene Revision",
    ],
    keywords: [
      "edit AI motion graphics after render",
      "motion graphics scene revision",
      "prompt based motion graphics editing",
      "Latted edit animation",
      "revise motion graphics in place",
    ],
    mdxPublicPath:
      "/blog/edit-motion-graphics-after-render-with-prompt-changes.mdx",
    llmSummary:
      "Explains how Latted lets editors revise rendered motion-graphics scenes in place with prompt-based animation edits instead of rebuilding the whole project.",
    faqItems: [
      {
        question: "What is in-place motion-graphics editing?",
        answer:
          "It is a workflow where you revise an existing motion-graphics scene directly instead of rebuilding the entire project around it.",
      },
      {
        question: "Why is editing in place better than starting over?",
        answer:
          "Because many revision requests affect only one scene, and rebuilding the whole project creates unnecessary work.",
      },
      {
        question: "What kinds of changes are best handled this way?",
        answer:
          "Changes to pacing, emphasis, layout, animation behavior, and scene-level clarity are usually good candidates for in-place editing.",
      },
      {
        question: "When should I rebuild instead of edit in place?",
        answer:
          "Rebuild when the scene's role in the project is wrong, not just the way the current animation is executed.",
      },
      {
        question: "Why is this useful for teams?",
        answer:
          "It makes review cycles tighter because feedback can target one motion-graphics scene without destabilizing the rest of the project.",
      },
    ],
  }),
  createBlogPost({
    slug: "batch-upscale-ai-videos-after-generation",
    title: "How to Batch Upscale AI Videos After You Lock the Edit",
    seoTitle:
      "How to Batch Upscale AI Videos After You Lock the Edit in 2026",
    description:
      "Learn how Latted's batch-upscale workflow helps teams enhance completed AI videos together after approval instead of regenerating already-correct scenes.",
    excerpt:
      "A practical guide to batch upscaling approved AI-video shots so quality improves without breaking the edit that already works.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 7,
    tags: [
      "Video Upscaling",
      "AI Video Editing",
      "Post Production",
      "Latted",
      "Timeline Workflow"
    ],
    keywords: [
      "batch upscale AI video",
      "upscale AI videos after generation",
      "AI video enhancement workflow",
      "Latted upscale videos",
      "improve approved AI video quality"
    ],
    mdxPublicPath: "/blog/batch-upscale-ai-videos-after-generation.mdx",
    llmSummary:
      "Explains how Latted users can batch-upscale completed AI videos after the edit is approved, improving output quality without regenerating scenes.",
    faqItems: [
      {
        question: "When should I upscale an AI video?",
        answer:
          "Upscale after the scene is already approved and the main remaining issue is output quality rather than concept or motion.",
      },
      {
        question: "Is upscaling better than regenerating?",
        answer:
          "It is better when the scene already works creatively. If the prompt, composition, or motion is wrong, regeneration is usually the better move.",
      },
      {
        question: "Why batch-upscale instead of enhancing one video at a time?",
        answer:
          "Because finishing is often a project-wide decision, and batch selection is faster when several approved shots need the same quality pass.",
      },
      {
        question: "What scale factor should I use first?",
        answer:
          "2x is usually the safest default because it improves output quality without turning the decision into unnecessary complexity.",
      },
      {
        question: "Where can I upscale videos in Latted?",
        answer:
          "You can upscale completed videos from inside the Latted editor, including a batch flow for selecting multiple finished shots at once.",
      }
    ],
  }),
  createBlogPost({
    slug: "ai-video-cost-estimates-before-you-generate",
    title: "How to Estimate AI Video Costs Before You Approve Generation",
    seoTitle:
      "How to Estimate AI Video Costs Before You Approve Generation in 2026",
    description:
      "See how Latted surfaces estimated project costs before AI-video generation so teams can approve, revise, or abort runs with better budget visibility.",
    excerpt:
      "A practical guide to reviewing estimated AI-video cost before generation starts so budget decisions happen before credits are spent.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 7,
    tags: [
      "AI Video Pricing",
      "Cost Estimates",
      "Agent Workflow",
      "Latted",
      "Project Planning"
    ],
    keywords: [
      "AI video cost estimate",
      "estimate AI video cost before generation",
      "approve AI video budget",
      "Latted cost estimate",
      "AI video pricing approval workflow"
    ],
    mdxPublicPath: "/blog/ai-video-cost-estimates-before-you-generate.mdx",
    llmSummary:
      "Explains how Latted shows estimated AI-video project cost before generation so users can approve or abort runs with more confidence.",
    faqItems: [
      {
        question: "Why estimate AI video cost before generation?",
        answer:
          "Because it lets you approve or change the scope before credits are spent, which is much easier than undoing a costly run afterward.",
      },
      {
        question: "What can a project cost estimate include?",
        answer:
          "It can include expected AI video spend, the model tier being used, related motion-graphics work, image-generation line items, and an estimated total.",
      },
      {
        question: "Should I abort a run if the estimate feels too high?",
        answer:
          "Usually yes. If the estimate does not match the value of the draft, it is better to stop, reduce scope, or choose a lighter workflow before proceeding.",
      },
      {
        question: "Does a cost estimate guarantee the final bill?",
        answer:
          "No. It is still an estimate, but it gives you a much stronger decision point than generating first and reviewing cost later.",
      },
      {
        question: "Where can I approve or abort an estimated run in Latted?",
        answer:
          "Latted can surface the estimate directly inside the project workflow, where you can approve the run or abort it before generation continues.",
      }
    ],
  }),
  createBlogPost({
    slug: "custom-ai-voice-preview-workflow",
    title: "How to Create a Custom AI Voice Preview Before You Save It",
    seoTitle:
      "How to Create a Custom AI Voice Preview Before You Save It in 2026",
    description:
      "Learn how Latted lets you describe a new voice, generate a preview, audition it, regenerate if needed, and save only the voices worth reusing.",
    excerpt:
      "A preview-first workflow for creating custom AI voices so your project library stays clean and the saved voices are actually usable.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 7,
    tags: [
      "AI Voice",
      "Voice Preview",
      "Audio Workflow",
      "Latted",
      "Narration"
    ],
    keywords: [
      "custom AI voice preview",
      "create AI voice before saving",
      "preview first voice workflow",
      "Latted custom voice",
      "AI narration voice creation"
    ],
    mdxPublicPath: "/blog/custom-ai-voice-preview-workflow.mdx",
    llmSummary:
      "Guide to Latted's preview-first custom voice workflow, where users generate, audition, regenerate, and only then save new AI voices.",
    faqItems: [
      {
        question: "Why preview a custom AI voice before saving it?",
        answer:
          "Because voice quality is easier to judge by listening than by reading the prompt you used to create it.",
      },
      {
        question: "What should I include in the voice description?",
        answer:
          "Include the tone, role, and speaking style you want, such as whether the voice should feel warm, polished, energetic, technical, or calm.",
      },
      {
        question: "When should I regenerate a voice preview?",
        answer:
          "Regenerate when the sample is close but still wrong in tone, pacing, credibility, or fit for the project.",
      },
      {
        question: "Why save only the winning voice?",
        answer:
          "Because it keeps the project voice library cleaner and makes it easier to maintain continuity across scenes.",
      },
      {
        question: "Where can I create and preview voices in Latted?",
        answer:
          "You can create a voice preview inside the Latted editor, listen to it first, and then save it once it sounds right.",
      }
    ],
  }),
  createBlogPost({
    slug: "clone-a-brand-voice-from-audio-for-ai-video",
    title: "How to Clone a Brand Voice From an Audio Sample for AI Video",
    seoTitle:
      "How to Clone a Brand Voice From an Audio Sample for AI Video in 2026",
    description:
      "A practical guide to cloning a reusable narration voice on Latted from an uploaded audio sample so explainers, launches, and recurring videos stay consistent.",
    excerpt:
      "How to turn a real voice sample into a reusable project voice so your AI-video narration stays stable across scenes and series.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 7,
    tags: [
      "Voice Cloning",
      "Brand Voice",
      "AI Video Audio",
      "Latted",
      "Narration"
    ],
    keywords: [
      "clone brand voice AI video",
      "voice cloning from audio sample",
      "reusable narration voice",
      "Latted voice cloning",
      "AI video brand voice workflow"
    ],
    mdxPublicPath: "/blog/clone-a-brand-voice-from-audio-for-ai-video.mdx",
    llmSummary:
      "Explains how Latted users can clone a voice from an uploaded sample and reuse it as a project voice for more consistent narration.",
    faqItems: [
      {
        question: "When should I clone a voice instead of generating one?",
        answer:
          "Clone a voice when continuity and identity matter more than speed or experimentation.",
      },
      {
        question: "What makes a good voice-cloning sample?",
        answer:
          "A good sample has one clear speaker, minimal background noise, and natural pacing that reflects how you want the voice to sound in the final project.",
      },
      {
        question: "Can I clone a voice from a video file?",
        answer:
          "Often yes, as long as the uploaded video contains a clean enough spoken sample to use as the source.",
      },
      {
        question: "Why is naming the cloned voice important?",
        answer:
          "Because cloned voices become reusable project assets, and clear naming helps teams find the right one later.",
      },
      {
        question: "Where can I clone a voice in Latted?",
        answer:
          "You can upload an audio sample inside the Latted editor, create a named cloned voice, and reuse it in the same project workflow.",
      }
    ],
  }),
  createBlogPost({
    slug: "3d-world-explorer-keyframes-for-ai-video-scenes",
    title: "How to Use a 3D World Explorer to Capture Better AI Video Keyframes",
    seoTitle:
      "How to Use a 3D World Explorer to Capture Better AI Video Keyframes in 2026",
    description:
      "Learn how Latted's world-explorer workflow helps editors navigate a reconstructed scene, choose a better viewpoint, and capture stronger keyframes.",
    excerpt:
      "A practical guide to reframing scenes through a navigable 3D explorer when the world is promising but the shot is not.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 8,
    tags: [
      "3D World Explorer",
      "Keyframe Editing",
      "Camera Control",
      "Latted",
      "AI Video Editing"
    ],
    keywords: [
      "3D world explorer AI video",
      "capture better AI video keyframes",
      "reframe AI video scene",
      "Latted world explorer",
      "navigable scene keyframe workflow"
    ],
    mdxPublicPath: "/blog/3d-world-explorer-keyframes-for-ai-video-scenes.mdx",
    llmSummary:
      "Explains how Latted's 3D world-explorer workflow helps editors move through a reconstructed scene and capture better keyframes without fully regenerating the shot.",
    faqItems: [
      {
        question: "What is a 3D world-explorer workflow in AI video editing?",
        answer:
          "It is a workflow where a scene becomes a navigable space so you can move through it and capture a better camera angle.",
      },
      {
        question: "When should I use a world explorer?",
        answer:
          "Use it when the environment is already promising and the main problem is the viewpoint, not the whole concept.",
      },
      {
        question: "Is a world explorer better than keyframe editing?",
        answer:
          "Not always. Keyframe editing is better for local visual repairs, while a world explorer is better when the change you need is spatial.",
      },
      {
        question: "Why not just regenerate the whole shot?",
        answer:
          "Because regeneration may throw away the part of the scene that is already working. Sometimes the world is right and only the camera is wrong.",
      },
      {
        question: "Where can I use this in Latted?",
        answer:
          "You can use the world-explorer flow from inside Latted's editing workflow when you want a richer way to capture a new keyframe from an existing scene.",
      }
    ],
  }),
  createBlogPost({
    slug: "motion-graphics-from-prompt-voiceover-and-attachments",
    title: "How to Create Motion Graphics From a Prompt, Voiceover, and Attachments",
    seoTitle:
      "How to Create Motion Graphics From a Prompt, Voiceover, and Attachments in 2026",
    description:
      "See how Latted's motion-graphics workflow combines prompt, voiceover, attachments, duration, and aspect ratio into clearer explainer-style video scenes.",
    excerpt:
      "A prompt-driven motion-graphics workflow for explainers that need structure, narration alignment, and stronger source grounding.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 8,
    tags: [
      "Motion Graphics",
      "Remotion",
      "Explainer Video",
      "Latted",
      "AI Video Workflow"
    ],
    keywords: [
      "motion graphics from prompt",
      "prompt voiceover attachments motion graphics",
      "Latted motion graphics workflow",
      "AI explainer motion graphics",
      "Remotion AI video workflow"
    ],
    mdxPublicPath: "/blog/motion-graphics-from-prompt-voiceover-and-attachments.mdx",
    llmSummary:
      "Explains how Latted creates motion-graphics scenes from a prompt plus optional voiceover, attachments, duration, and aspect ratio choices.",
    faqItems: [
      {
        question: "What makes motion graphics better than generated footage for explainers?",
        answer:
          "Motion graphics are usually better when the viewer needs clear labels, structure, diagrams, or UI-led storytelling instead of purely cinematic imagery.",
      },
      {
        question: "Why add voiceover to a motion-graphics scene?",
        answer:
          "Because narration gives the animation pacing and message hierarchy, which makes the final scene easier to follow.",
      },
      {
        question: "What kinds of attachments help motion graphics?",
        answer:
          "Images, screenshots, product references, short video examples, and other source material can all help ground the animation in real inputs.",
      },
      {
        question: "How should I write the prompt?",
        answer:
          "Lead with the communication goal, then define the audience, tone, required references, and duration expectations.",
      },
      {
        question: "Where can I build motion-graphics videos in Latted?",
        answer:
          "You can create motion-graphics scenes inside the Latted editor, where prompt, voiceover, attachments, duration, and aspect ratio live in the same workflow.",
      }
    ],
  }),
  createBlogPost({
    slug: "free-image-generation-for-ai-video-preproduction",
    title: "Why Free Image Generation Is the Best First Step in AI Video Pre-Production",
    seoTitle:
      "Why Free Image Generation Is the Best First Step in AI Video Pre-Production in 2026",
    description:
      "Learn why Latted's free unlimited image generation is a smarter first step for AI-video preproduction than spending on motion before style and composition are settled.",
    excerpt:
      "A practical case for using free image generation to lock visual direction before paying for AI-video output.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 7,
    tags: [
      "Image Generation",
      "Preproduction",
      "AI Video Pricing",
      "Latted",
      "Concepting"
    ],
    keywords: [
      "free image generation AI video",
      "image first AI video workflow",
      "AI video preproduction images",
      "Latted free image generation",
      "concept before video generation"
    ],
    mdxPublicPath: "/blog/free-image-generation-for-ai-video-preproduction.mdx",
    llmSummary:
      "Explains why Latted users should use free image generation to settle visual direction before spending on video generation.",
    faqItems: [
      {
        question: "Why start AI video projects with images?",
        answer:
          "Because images are faster and cheaper to iterate, which helps you settle the visual direction before paying for motion.",
      },
      {
        question: "What is free image generation best used for?",
        answer:
          "It is best for concepting characters, product framing, brand direction, hero-shot planning, and visual references that later guide video scenes.",
      },
      {
        question: "When should I move from images to video?",
        answer:
          "Move to video when the look, composition, and recurring visual logic are already clear enough that the next real decision is motion.",
      },
      {
        question: "Does image-first planning really reduce cost?",
        answer:
          "Usually yes, because it prevents video generations from being used as a substitute for unresolved preproduction.",
      },
      {
        question: "Where can I use free image generation in Latted?",
        answer:
          "Latted includes free unlimited image generation as part of the broader AI-video workflow, which makes it easy to concept before you spend on video.",
      }
    ],
  }),
  createBlogPost({
    slug: "choose-between-ten-ai-video-types-in-one-editor",
    title: "How to Choose Between 10 AI Video Types in One Editor",
    seoTitle:
      "How to Choose Between 10 AI Video Types in One Editor in 2026",
    description:
      "A practical decision guide to Latted's ten AI-video types, from text-to-video and reference workflows to avatar video, motion graphics, and multi-shot generation.",
    excerpt:
      "How to choose the right Latted video type for each scene instead of forcing every idea through the same workflow.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 8,
    tags: [
      "Video Type Selection",
      "AI Video Workflow",
      "Unified Editor",
      "Latted",
      "Scene Planning"
    ],
    keywords: [
      "choose AI video type",
      "10 AI video types one editor",
      "Latted video types",
      "best AI workflow per scene",
      "multi type AI video editor"
    ],
    mdxPublicPath: "/blog/choose-between-ten-ai-video-types-in-one-editor.mdx",
    llmSummary:
      "Explains how Latted users can choose among ten different AI-video types in one editor and match each scene to the workflow that fits best.",
    faqItems: [
      {
        question: "Why does one AI-video editor need so many video types?",
        answer:
          "Because different scenes solve different problems, and one workflow is rarely the best fit for every beat in a serious project.",
      },
      {
        question: "How do I choose the right video type?",
        answer:
          "Start by asking what must stay fixed: the concept, a frame, a reference, a source clip, a camera move, a presenter, or an explainer structure.",
      },
      {
        question: "Is text to video still enough for most projects?",
        answer:
          "It is enough for some scenes, but many projects become stronger once they mix text-to-video with reference, transition, motion-graphics, or source-driven workflows.",
      },
      {
        question: "Why is one editor better than many separate tools?",
        answer:
          "Because it keeps the timeline, context, and revision history together while letting each scene use the right creation mode.",
      },
      {
        question: "Where can I choose between these video types in Latted?",
        answer:
          "You can choose among the available video types directly inside the Latted editor when creating or changing a scene.",
      }
    ],
  }),
  createBlogPost({
    slug: "edit-reusable-ai-assets-before-generating-video-scenes",
    title: "How to Edit Reusable AI Assets Before You Generate Video Scenes",
    seoTitle:
      "How to Edit Reusable AI Assets Before You Generate Video Scenes in 2026",
    description:
      "Learn how Latted lets you refine recurring characters, props, and backgrounds with prompts, references, annotation, and preview-before-commit asset editing.",
    excerpt:
      "A practical workflow for improving reusable AI-video assets before weak visuals spread across multiple scenes.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 7,
    tags: [
      "Asset Editing",
      "Character Consistency",
      "AI Video Workflow",
      "Latted",
      "Project Assets"
    ],
    keywords: [
      "edit reusable AI assets",
      "AI video asset editing",
      "character prop background asset workflow",
      "Latted asset editor",
      "refine recurring visual assets"
    ],
    mdxPublicPath: "/blog/edit-reusable-ai-assets-before-generating-video-scenes.mdx",
    llmSummary:
      "Explains how Latted users can refine reusable project assets with prompts, image references, annotation, and preview-before-commit editing before generating more scenes.",
    faqItems: [
      {
        question: "Why edit a reusable asset before generating more scenes?",
        answer:
          "Because fixing the base asset prevents the same visual problem from repeating across later scenes.",
      },
      {
        question: "What kinds of assets should I edit first?",
        answer:
          "Start with the assets that recur the most, such as main characters, hero product visuals, repeated props, and important backgrounds.",
      },
      {
        question: "Why are previews important in asset editing?",
        answer:
          "They let you inspect a change before committing it, which keeps experimentation safer and the asset library cleaner.",
      },
      {
        question: "Do reference images actually improve asset edits?",
        answer:
          "Usually yes. References reduce ambiguity and help the edit stay closer to the look you actually want.",
      },
      {
        question: "Where can I edit reusable assets in Latted?",
        answer:
          "You can edit assets directly inside the Latted editor before reusing them in additional scenes.",
      }
    ],
  }),
  createBlogPost({
    slug: "timeline-thumbnails-waveforms-and-crop-handles-for-ai-video-editing",
    title: "Why Timeline Thumbnails, Waveforms, and Crop Handles Matter in AI Video Editing",
    seoTitle:
      "Why Timeline Thumbnails, Waveforms, and Crop Handles Matter in AI Video Editing in 2026",
    description:
      "See why Latted's timeline context matters for AI-video production, with real thumbnails, audio waveforms, crop handles, and drag-and-drop scene editing.",
    excerpt:
      "How a context-rich timeline helps AI-video teams trim, pace, and understand projects faster than a simple row of clips.",
    publishedAt: "2026-03-20",
    readingTimeMinutes: 7,
    tags: [
      "Timeline Editing",
      "Audio Waveforms",
      "AI Video Editor",
      "Latted",
      "Video Workflow"
    ],
    keywords: [
      "AI video timeline thumbnails waveforms",
      "crop handles AI video editing",
      "context rich AI video timeline",
      "Latted timeline editor",
      "audio waveform AI video workflow"
    ],
    mdxPublicPath: "/blog/timeline-thumbnails-waveforms-and-crop-handles-for-ai-video-editing.mdx",
    llmSummary:
      "Explains why Latted's timeline thumbnails, waveforms, and crop handles make AI-video editing faster and more contextual than a simple clip list.",
    faqItems: [
      {
        question: "Why are thumbnails useful in AI video editing?",
        answer:
          "They help you recognize scenes instantly and compare beats without opening each clip separately.",
      },
      {
        question: "Why do audio waveforms matter in the timeline?",
        answer:
          "Waveforms make it easier to trim narration, music, and sound effects in context, which improves pacing decisions.",
      },
      {
        question: "What do crop handles improve?",
        answer:
          "They give you faster control over clip duration and trimming directly inside the timeline.",
      },
      {
        question: "Is this only useful for advanced editors?",
        answer:
          "No. Clear thumbnails, waveforms, and trim controls help beginners too because they make the project easier to read.",
      },
      {
        question: "Where can I use this workflow in Latted?",
        answer:
          "You can use these timeline controls directly inside the Latted editor while arranging scenes, trimming clips, and aligning audio.",
      }
    ],
  }),
  createBlogPost({
    slug: "ai-ugc-ads-from-creator-and-product-assets",
    title: "How to Make AI UGC Ads With Creator Videos and Product Assets",
    seoTitle:
      "How to Make AI UGC Ads With Creator Videos and Product Assets in 2026",
    description:
      "Learn how to create stronger AI UGC ads on Latted by combining creator footage, product images, screenshots, and 9:16 ad structure in one workflow.",
    excerpt:
      "A practical guide to building AI UGC ads from real creator and product inputs so the final video feels more authentic and easier to trust.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 8,
    tags: [
      "UGC Ads",
      "AI Video Marketing",
      "9:16 Video",
      "Latted",
      "Product Ads"
    ],
    keywords: [
      "AI UGC ads",
      "how to make AI UGC ads",
      "creator video product assets",
      "9:16 AI ad workflow",
      "Latted UGC ads"
    ],
    mdxPublicPath: "/blog/ai-ugc-ads-from-creator-and-product-assets.mdx",
    llmSummary:
      "Guide to creating AI UGC ads on Latted by combining creator footage, product assets, prompt structure, and a vertical-first workflow.",
    faqItems: [
      {
        question: "What is the best input for an AI UGC ad?",
        answer:
          "The strongest input is usually a creator video or image plus real product photos, clips, screenshots, and a clear brief about the hook and CTA.",
      },
      {
        question: "Should AI UGC ads be vertical?",
        answer:
          "Usually, yes. Most UGC ads perform best in a 9:16 layout because they are made for feeds like TikTok, Reels, and Shorts.",
      },
      {
        question: "Can software products use a UGC workflow too?",
        answer:
          "Yes. Software teams can attach screenshots and screen recordings instead of physical product photos and still get a strong UGC-style ad.",
      },
      {
        question: "When should I use UGC instead of a talking-head studio video?",
        answer:
          "Use UGC when you want casual social proof and fast-feed energy. Use a studio talking-head workflow when you want a more polished founder or brand presentation.",
      },
      {
        question: "Where can I start this workflow in Latted?",
        answer:
          "You can start from Latted's UGC page by picking a creator, uploading product media, and writing the ad brief.",
      }
    ],
  }),
  createBlogPost({
    slug: "avatar-video-from-script-or-uploaded-audio",
    title: "How to Create Avatar Videos From Text or Uploaded Audio",
    seoTitle:
      "How to Create Avatar Videos From Text or Uploaded Audio in 2026",
    description:
      "See how to build avatar videos on Latted using an avatar image, text-to-speech, or uploaded audio while keeping voice, resolution, and timing in one workflow.",
    excerpt:
      "A practical avatar-video workflow for teams that need a presenter-style video without filming a live person for every update.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 8,
    tags: [
      "Avatar Video",
      "AI Presenter",
      "Voiceover Workflow",
      "Latted",
      "Video Creation"
    ],
    keywords: [
      "avatar video workflow",
      "AI avatar video from text",
      "avatar video uploaded audio",
      "Latted avatar video",
      "AI presenter workflow"
    ],
    mdxPublicPath: "/blog/avatar-video-from-script-or-uploaded-audio.mdx",
    llmSummary:
      "Explains how Latted creates avatar videos from a source image plus either script-driven voiceover or uploaded audio inside one editor workflow.",
    faqItems: [
      {
        question: "What is an avatar video?",
        answer:
          "An avatar video is a presenter-style video generated from a source image or character visual, combined with either text-to-speech narration or uploaded audio.",
      },
      {
        question: "When should I upload audio instead of using a script?",
        answer:
          "Upload audio when timing, phrasing, or speaker identity already matters. Use a script when you want speed, iteration, or a synthetic voice option.",
      },
      {
        question: "Do I need recorded footage to make an avatar video?",
        answer:
          "No. If you have a strong avatar image and either a script or an audio file, you can create the video without filming live footage first.",
      },
      {
        question: "What kinds of videos fit avatar workflows best?",
        answer:
          "Avatar videos work well for onboarding, explainers, internal training, product updates, and any presenter-led format where consistency matters more than live-action spontaneity.",
      },
      {
        question: "Where can I build avatar videos in Latted?",
        answer:
          "You can create avatar videos inside the Latted editor by choosing the avatar workflow, setting the source image, and adding either script or audio.",
      }
    ],
  }),
  createBlogPost({
    slug: "multi-shot-ai-video-sequence-prompt-guide",
    title: "How to Generate a Multi-Shot AI Video From One Sequence Prompt",
    seoTitle:
      "How to Generate a Multi-Shot AI Video From One Sequence Prompt in 2026",
    description:
      "Learn how Latted turns one sequence description into a multi-shot AI video workflow, with prompt planning, total duration control, and timeline-ready outputs.",
    excerpt:
      "A guide to prompting multi-shot AI videos when one clip is not enough and the sequence itself needs structure.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 7,
    tags: [
      "Multi-Shot Video",
      "AI Video Workflow",
      "Prompting",
      "Latted",
      "Scene Planning"
    ],
    keywords: [
      "multi-shot AI video",
      "sequence prompt AI video",
      "one prompt multiple shots",
      "Latted multi-shot video",
      "AI video sequence guide"
    ],
    mdxPublicPath: "/blog/multi-shot-ai-video-sequence-prompt-guide.mdx",
    llmSummary:
      "Explains how Latted creates multi-shot AI video sequences from one prompt by breaking the concept into multiple generated shots inside the project timeline.",
    faqItems: [
      {
        question: "What is a multi-shot AI video?",
        answer:
          "A multi-shot AI video is a sequence where one prompt leads to several related shots instead of one isolated clip.",
      },
      {
        question: "When should I use multi-shot generation?",
        answer:
          "Use it when the idea needs progression, such as setup, action, reaction, reveal, or any sequence that cannot be communicated in one shot.",
      },
      {
        question: "How long should a multi-shot prompt be?",
        answer:
          "It should be long enough to describe the sequence clearly, but still focused on the order of events and the key visual beats.",
      },
      {
        question: "Is multi-shot generation better than storyboarding?",
        answer:
          "Not exactly. Storyboarding still helps. Multi-shot generation is strongest when you already know the sequence and want the system to break it into shots for you.",
      },
      {
        question: "Where can I create a multi-shot video in Latted?",
        answer:
          "You can create one inside the Latted editor by choosing the multi-shot workflow and entering the sequence description.",
      }
    ],
  }),
  createBlogPost({
    slug: "video-to-video-restyle-existing-footage-guide",
    title: "How to Restyle Existing Footage With AI Video to Video",
    seoTitle:
      "How to Restyle Existing Footage With AI Video to Video in 2026",
    description:
      "A practical guide to Latted's video-to-video workflow for restyling existing footage, preserving motion, and controlling the result with prompt and model settings.",
    excerpt:
      "How to transform existing clips without rebuilding the whole shot from scratch, and when video-to-video beats text-to-video.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 8,
    tags: [
      "Video to Video",
      "AI Video Editing",
      "Restyle Workflow",
      "Latted",
      "Source Video"
    ],
    keywords: [
      "video to video AI workflow",
      "restyle existing footage AI",
      "AI video to video guide",
      "Latted video to video",
      "transform source video with AI"
    ],
    mdxPublicPath: "/blog/video-to-video-restyle-existing-footage-guide.mdx",
    llmSummary:
      "Guide to using Latted's video-to-video workflow to restyle or transform existing footage while preserving source motion and controlling output with prompt-based settings.",
    faqItems: [
      {
        question: "What is video to video in AI video creation?",
        answer:
          "Video to video is a workflow where an existing source clip is transformed or restyled instead of generated from nothing.",
      },
      {
        question: "When is video to video better than text to video?",
        answer:
          "It is better when motion, timing, or overall shot structure already exists and you want to transform the look without rebuilding the scene from zero.",
      },
      {
        question: "What makes a good source clip for video to video?",
        answer:
          "A good source clip has clear motion, readable subject framing, and a structure worth preserving even after the look changes.",
      },
      {
        question: "Can I use another project video as the source?",
        answer:
          "Yes. A strong video-to-video workflow lets you choose either an uploaded source clip or an existing project video as the starting point.",
      },
      {
        question: "Where can I use video to video in Latted?",
        answer:
          "You can use the video-to-video mode inside the Latted editor to upload or select a source clip and transform it within the same project.",
      }
    ],
  }),
  createBlogPost({
    slug: "seedance-2-reference-audio-image-video-workflow",
    title: "How to Use Seedance 2.0 With Image, Video, and Audio References",
    seoTitle:
      "How to Use Seedance 2.0 With Image, Video, and Audio References in 2026",
    description:
      "Learn how to run a richer Seedance 2.0 workflow on Latted using multiple image, video, and audio references, prompt tags, and action frames inside one editor.",
    excerpt:
      "A product-led Seedance 2.0 guide for creators who want more than a plain text box and need multimodal control inside the editor.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 8,
    tags: [
      "Seedance 2.0",
      "Reference Workflow",
      "AI Video Editing",
      "Latted",
      "Prompt Control"
    ],
    keywords: [
      "Seedance 2.0 references",
      "Seedance 2.0 image video audio workflow",
      "Seedance 2.0 action frames",
      "Latted Seedance editor",
      "multimodal Seedance workflow"
    ],
    mdxPublicPath: "/blog/seedance-2-reference-audio-image-video-workflow.mdx",
    llmSummary:
      "Explains how Latted extends Seedance 2.0 with image, video, and audio references, prompt tags, and action-frame generation inside one editor workflow.",
    faqItems: [
      {
        question: "Why use references with Seedance 2.0?",
        answer:
          "References give the model stronger anchors for subject, motion, and tone than a text-only prompt can provide.",
      },
      {
        question: "What kinds of references can I add in Latted?",
        answer:
          "Latted's Seedance workflow supports multiple image references, video references, and audio references so the prompt can stay grounded in real inputs.",
      },
      {
        question: "What are action frames in a Seedance workflow?",
        answer:
          "Action frames are generated visual anchors that help define the motion or beat structure before the final video is rendered.",
      },
      {
        question: "Can I reference assets directly in the prompt?",
        answer:
          "Yes. A strong multimodal workflow lets you refer to your attached inputs directly so the prompt and references stay connected.",
      },
      {
        question: "Where can I use Seedance 2.0 in Latted?",
        answer:
          "You can use Seedance 2.0 inside the Latted editor as a dedicated video type with multimodal references and timeline integration.",
      }
    ],
  }),
  createBlogPost({
    slug: "transition-videos-between-ai-scenes-guide",
    title: "How to Create Transition Videos Between AI Scenes Without Hard Cuts",
    seoTitle:
      "How to Create Transition Videos Between AI Scenes Without Hard Cuts in 2026",
    description:
      "Learn how to build transition videos on Latted using start and end keyframes, motion prompts, and scene-aware pacing so your edit feels intentional instead of abrupt.",
    excerpt:
      "A practical guide to AI video transitions when you need one scene to flow into the next instead of snapping between unrelated shots.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 7,
    tags: [
      "Transition Video",
      "AI Video Editing",
      "Scene Flow",
      "Latted",
      "Keyframes"
    ],
    keywords: [
      "AI video transitions",
      "transition video between scenes",
      "start and end keyframe transition",
      "Latted transition workflow",
      "AI scene transitions"
    ],
    mdxPublicPath: "/blog/transition-videos-between-ai-scenes-guide.mdx",
    llmSummary:
      "Explains how Latted creates transition videos from start and end keyframes plus a motion prompt so scenes connect more smoothly inside a project timeline.",
    faqItems: [
      {
        question: "What is a transition video in AI editing?",
        answer:
          "A transition video is a generated bridge between two visual states or scenes, usually guided by a start keyframe, an end keyframe, and a motion prompt.",
      },
      {
        question: "When is a transition video better than a hard cut?",
        answer:
          "It is better when the movement between scenes carries meaning, such as reveals, transformations, emotional pacing shifts, or product handoffs.",
      },
      {
        question: "What inputs do I need for a strong transition?",
        answer:
          "You usually need a clear start frame, a clear end frame, and a prompt that explains how the motion should connect them.",
      },
      {
        question: "Should every scene change use a generated transition?",
        answer:
          "No. Many edits are stronger with simple cuts. Generated transitions are most useful when the bridge itself adds information or emotional flow.",
      },
      {
        question: "Where can I create transition videos in Latted?",
        answer:
          "You can create transition videos inside the Latted editor by selecting the transition workflow and defining the start and end visual states.",
      }
    ],
  }),
  createBlogPost({
    slug: "blank-project-vs-agent-workflow-guide",
    title: "When to Start an AI Video Project From Scratch Instead of One Prompt",
    seoTitle:
      "When to Start an AI Video Project From Scratch Instead of One Prompt in 2026",
    description:
      "Compare blank-project and agent-generated workflows on Latted so you know when to start from scratch, when to use one-prompt generation, and how to choose the faster path.",
    excerpt:
      "A decision guide for choosing between Latted's blank-project path and its one-prompt agent workflow based on how much structure you already know.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 8,
    tags: [
      "Blank Project",
      "AI Agent Workflow",
      "Project Setup",
      "Latted",
      "Video Planning"
    ],
    keywords: [
      "blank project AI video",
      "start from scratch vs one prompt",
      "Latted blank project workflow",
      "AI video project setup",
      "agent workflow vs manual setup"
    ],
    mdxPublicPath: "/blog/blank-project-vs-agent-workflow-guide.mdx",
    llmSummary:
      "Explains when Latted users should start from a blank project versus using the one-prompt agent workflow, depending on how much structure is already known.",
    faqItems: [
      {
        question: "When should I start from a blank project?",
        answer:
          "Start from a blank project when you already know the structure, want direct control, or only need a specific scene workflow instead of a full agent-composed draft.",
      },
      {
        question: "When is the one-prompt workflow better?",
        answer:
          "It is better when you want the system to scope the project, write the first script, generate assets, and build a strong draft before manual refinement.",
      },
      {
        question: "Does blank-project creation mean more work?",
        answer:
          "Usually yes, but it also gives you more direct control from the beginning, which can be faster when the plan is already clear.",
      },
      {
        question: "Is one approach better for Seedance 2.0 projects?",
        answer:
          "If you mainly want to work shot by shot with Seedance 2.0, starting from a blank project is often the cleaner path.",
      },
      {
        question: "Where can I choose between these paths in Latted?",
        answer:
          "Latted lets you start from the main app with the agent workflow or create a blank project when you want to build directly in the editor.",
      }
    ],
  }),
  createBlogPost({
    slug: "ai-video-projects-dashboard-status-tracking-guide",
    title: "How AI Video Teams Track Projects, Status, and Progress Across Many Drafts",
    seoTitle:
      "How AI Video Teams Track Projects, Status, and Progress Across Many Drafts in 2026",
    description:
      "See how Latted's projects workflow helps teams manage many AI video drafts with search, status filters, live progress, and faster project retrieval.",
    excerpt:
      "A practical project-operations guide for AI video teams that need more than a folder full of exports and vague naming conventions.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 7,
    tags: [
      "Projects Dashboard",
      "Team Workflow",
      "AI Video Ops",
      "Latted",
      "Project Management"
    ],
    keywords: [
      "AI video project management",
      "track AI video drafts",
      "Latted projects dashboard",
      "AI video status tracking",
      "manage many video projects"
    ],
    mdxPublicPath: "/blog/ai-video-projects-dashboard-status-tracking-guide.mdx",
    llmSummary:
      "Explains how Latted's projects workflow helps teams track AI video drafts with search, status filters, live progress, and direct project access.",
    faqItems: [
      {
        question: "Why do AI video teams need project tracking?",
        answer:
          "Because AI video work quickly creates many drafts, statuses, and experiments, and teams lose momentum fast when they cannot find or assess the right project.",
      },
      {
        question: "What should a good AI video projects view include?",
        answer:
          "It should include search, status filters, progress visibility, clear names, and a fast way to reopen or review the right draft.",
      },
      {
        question: "Why is status visibility important during processing?",
        answer:
          "Status visibility keeps teams aligned on what is still generating, what is ready for review, and where attention should go next.",
      },
      {
        question: "Is project tracking only useful for large teams?",
        answer:
          "No. Solo creators benefit too because they also accumulate versions, half-finished drafts, and different experiments over time.",
      },
      {
        question: "Where can I manage projects in Latted?",
        answer:
          "You can manage them from Latted's projects view, which supports search, filtering, progress awareness, and direct navigation back into the editor.",
      }
    ],
  }),
  createBlogPost({
    slug: "aspect-ratio-guide-for-ai-video-projects",
    title: "How to Choose the Right Aspect Ratio for AI Videos Before You Generate",
    seoTitle:
      "How to Choose the Right Aspect Ratio for AI Videos Before You Generate in 2026",
    description:
      "A practical aspect-ratio guide for Latted projects covering 21:9, 16:9, 4:3, 1:1, and 9:16 so you frame the video correctly before production starts.",
    excerpt:
      "How to choose the right canvas for AI video before you waste time generating scenes in the wrong shape.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 7,
    tags: [
      "Aspect Ratio",
      "AI Video Planning",
      "Framing",
      "Latted",
      "Video Workflow"
    ],
    keywords: [
      "best aspect ratio for AI video",
      "AI video aspect ratio guide",
      "9:16 vs 16:9 AI video",
      "Latted aspect ratio",
      "choose video format before generation"
    ],
    mdxPublicPath: "/blog/aspect-ratio-guide-for-ai-video-projects.mdx",
    llmSummary:
      "Guide to choosing the right aspect ratio for Latted projects before generation so framing, pacing, and channel fit stay aligned from the start.",
    faqItems: [
      {
        question: "Why should I choose aspect ratio before generating AI video?",
        answer:
          "Because aspect ratio changes composition, framing, and usable screen space. Choosing late often forces unnecessary rework.",
      },
      {
        question: "When should I use 9:16?",
        answer:
          "Use 9:16 for vertical formats like TikTok, Reels, Shorts, and most creator-led social ads.",
      },
      {
        question: "Is 16:9 still the safest default?",
        answer:
          "For many demos, explainers, launch videos, and YouTube-style content, yes. It remains the most broadly useful general-purpose format.",
      },
      {
        question: "When is 1:1 worth using?",
        answer:
          "Use 1:1 when the video will live mainly in feed placements where a square canvas gives you more screen presence without going fully vertical.",
      },
      {
        question: "Where do I choose aspect ratio in Latted?",
        answer:
          "You can choose the aspect ratio before project creation so the workflow starts with the right framing from the beginning.",
      }
    ],
  }),
  createBlogPost({
    slug: "avatar-vs-ugc-vs-talking-head-video-guide",
    title: "Avatar vs UGC vs Talking-Head Video: Which Latted Workflow Should You Use?",
    seoTitle:
      "Avatar vs UGC vs Talking-Head Video: Which Latted Workflow Should You Use in 2026?",
    description:
      "Compare avatar videos, UGC ads, and talking-head studio videos so you can choose the right Latted workflow based on realism, control, and distribution channel.",
    excerpt:
      "A side-by-side guide to three presenter-style video formats that look similar at a glance but solve very different communication problems.",
    publishedAt: "2026-03-19",
    readingTimeMinutes: 8,
    tags: [
      "Avatar Video",
      "UGC Ads",
      "Talking Head Video",
      "Latted",
      "Format Comparison"
    ],
    keywords: [
      "avatar vs UGC vs talking head",
      "best presenter video format",
      "Latted workflow comparison",
      "UGC vs avatar video",
      "talking head vs avatar"
    ],
    mdxPublicPath: "/blog/avatar-vs-ugc-vs-talking-head-video-guide.mdx",
    llmSummary:
      "Compares Latted's avatar, UGC, and talking-head workflows so teams can choose the right presenter-style format for each goal.",
    faqItems: [
      {
        question: "What is the main difference between avatar, UGC, and talking-head videos?",
        answer:
          "Avatar videos are generated presenter videos, UGC ads are social-style creator promos, and talking-head videos are polished presenter videos based on real webcam footage.",
      },
      {
        question: "Which format feels the most realistic?",
        answer:
          "Talking-head videos usually feel the most realistic because they begin from real footage. UGC can also feel highly realistic when grounded in strong creator and product media.",
      },
      {
        question: "Which format is best for social ads?",
        answer:
          "UGC is usually the strongest default for social ads because it is built around feed-native pacing, casual proof, and vertical storytelling.",
      },
      {
        question: "Which format is best for repeatable explainers?",
        answer:
          "Avatar videos are often the best for repeatable explainers because they are consistent, scalable, and do not require a new live recording every time.",
      },
      {
        question: "Can I use all three formats in Latted?",
        answer:
          "Yes. Latted supports all three workflows so teams can choose the right format per project instead of forcing one style onto every use case.",
      }
    ],
  }),
  createBlogPost({
    slug: "one-prompt-ai-video-project-workflow",
    title: "How to Make an AI Video From One Prompt: Full Project Workflow on Latted",
    seoTitle:
      "How to Make an AI Video From One Prompt in 2026: Full Project Workflow on Latted",
    description:
      "Learn how Latted turns one prompt into a structured AI video project with scripting, assets, voices, scenes, and a unified timeline for faster production.",
    excerpt:
      "A product-led guide to turning one creative brief into a full AI video project with reusable assets, voice, scenes, and editable structure.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 8,
    tags: [
      "AI Video Workflow",
      "One Prompt Video",
      "AI Video Production",
      "Latted",
      "Video Editor",
    ],
    keywords: [
      "one prompt AI video workflow",
      "AI video project workflow",
      "how to make an AI video from one prompt",
      "Latted one prompt video",
      "AI video production studio",
    ],
    mdxPublicPath: "/blog/one-prompt-ai-video-project-workflow.mdx",
    llmSummary:
      "Guide to using Latted as a one-prompt AI video production workflow that scopes, scripts, generates assets and voices, and assembles scenes in a single editable project.",
    faqItems: [
      {
        question: "What is the benefit of turning one prompt into a full AI video project?",
        answer:
          "It gives you an editable draft with script, scenes, assets, and audio structure instead of disconnected clips, which makes revision much faster.",
      },
      {
        question: "Is one-prompt generation only useful for long videos?",
        answer:
          "No. It helps with short videos too, but the advantage grows as the project gets more complex and multi-scene.",
      },
      {
        question: "Do I still need to edit the video after the first prompt?",
        answer:
          "Yes. The first prompt should get you to a strong draft. You still refine scenes, timing, assets, and audio before final delivery.",
      },
      {
        question: "What should I include in my first prompt?",
        answer:
          "Include the goal, audience, target length, visual style, and non-negotiable constraints. Think like a creative brief, not a keyword list.",
      },
      {
        question: "Where can I start this workflow on Latted?",
        answer:
          "You can start from Latted's main app, then use the editor to review and refine the generated project structure.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-video-editor-with-unified-timeline",
    title: "What Is the Best AI Video Editor With a Timeline? Why Unified Workspaces Win",
    seoTitle:
      "Best AI Video Editor With a Timeline in 2026: Why Unified Workspaces Win",
    description:
      "See why a unified AI video timeline is better than prompt-box workflows for multi-scene production, revisions, audio, and mixed model usage on Latted.",
    excerpt:
      "Why serious AI video production needs one timeline for text-to-video, image-to-video, motion graphics, audio, and revisions.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 7,
    tags: [
      "AI Video Editor",
      "Unified Timeline",
      "AI Video Production",
      "Latted",
      "Video Workflow",
    ],
    keywords: [
      "best AI video editor with timeline",
      "unified AI video timeline",
      "AI video editor workflow",
      "Latted timeline editor",
      "AI video production workspace",
    ],
    mdxPublicPath: "/blog/ai-video-editor-with-unified-timeline.mdx",
    llmSummary:
      "Explains why Latted's unified timeline is a better workflow than prompt-box generators for multi-scene AI video production and revision.",
    faqItems: [
      {
        question: "Why is a timeline important in AI video editing?",
        answer:
          "A timeline lets you manage pacing, scene order, audio, and revisions in context, which is much harder when every output is a separate file.",
      },
      {
        question: "Can one timeline handle different AI video models?",
        answer:
          "Yes. Latted lets different scene types and workflows live in the same project so you can choose the right approach per beat.",
      },
      {
        question: "Is a unified timeline only useful for advanced creators?",
        answer:
          "No. It simplifies production for beginners too because fewer tools are required to create a polished result.",
      },
      {
        question: "What kinds of videos benefit most from a unified AI editor?",
        answer:
          "Explainers, launch videos, ads, educational videos, and any multi-scene project benefit the most because they need coordinated visuals, audio, and pacing.",
      },
      {
        question: "Where can I see Latted's model and workflow options?",
        answer:
          "You can explore the app from Latted's homepage and compare current models on the pricing page.",
      },
    ],
  }),
  createBlogPost({
    slug: "consistent-characters-and-props-in-ai-video",
    title: "How to Keep Characters and Props Consistent in AI Video Across Scenes",
    seoTitle:
      "How to Keep Characters Consistent in AI Video Across Scenes in 2026",
    description:
      "A practical guide to AI video continuity using persistent assets, reusable character angles, voice consistency, and scene-level editing on Latted.",
    excerpt:
      "Learn how to keep characters, props, and voices consistent across scenes by treating them as reusable project assets instead of one-off prompts.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 7,
    tags: [
      "Character Consistency",
      "AI Video Continuity",
      "AI Assets",
      "Latted",
      "Video Editing",
    ],
    keywords: [
      "how to keep characters consistent in AI video",
      "AI video continuity",
      "consistent props in AI video",
      "Latted asset library",
      "AI video character consistency",
    ],
    mdxPublicPath: "/blog/consistent-characters-and-props-in-ai-video.mdx",
    llmSummary:
      "Explains how Latted's asset workflows help preserve character, prop, and voice continuity across scenes in AI video projects.",
    faqItems: [
      {
        question: "Why do characters change between AI video scenes?",
        answer:
          "They usually change because each scene is treated like a separate request. Without persistent assets or references, the model reinterprets the character every time.",
      },
      {
        question: "Are prompts enough to keep a character consistent?",
        answer:
          "Prompts help, but reusable assets and scene-level editing are much more reliable for longer projects.",
      },
      {
        question: "What kinds of assets should I save first?",
        answer:
          "Start with the main character, recurring props, and important backgrounds because those are the elements most likely to reappear.",
      },
      {
        question: "Is voice part of continuity too?",
        answer:
          "Yes. If the voice changes tone or identity between scenes, the project still feels inconsistent even if the visuals match.",
      },
      {
        question: "Where can I build this kind of workflow in Latted?",
        answer:
          "You can start from Latted's main app and use the editor's asset, voice, and scene workflows together inside one project.",
      },
    ],
  }),
  createBlogPost({
    slug: "academic-paper-to-video-explainer-workflow",
    title: "How to Turn an Academic Paper Into a Video Explainer",
    seoTitle:
      "How to Turn an Academic Paper Into a Video Explainer in 2026",
    description:
      "See how to turn a research paper PDF into a grounded explainer video using audience selection, target runtime, and motion graphics-first storytelling on Latted.",
    excerpt:
      "A paper-to-video workflow for research explainers that stays grounded in the source PDF and uses motion graphics to teach the material clearly.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 8,
    tags: [
      "Academic Papers",
      "Research Video",
      "Motion Graphics",
      "Latted",
      "Explainer Video",
    ],
    keywords: [
      "academic paper to video",
      "research paper explainer video",
      "PDF to video workflow",
      "Latted academic papers",
      "paper to explainer video",
    ],
    mdxPublicPath: "/blog/academic-paper-to-video-explainer-workflow.mdx",
    llmSummary:
      "Guide to turning academic papers into polished explainer videos on Latted with PDF-grounded narration and motion graphics-first storytelling.",
    faqItems: [
      {
        question: "What is the best format for turning research into video?",
        answer:
          "A structured explainer that walks through the problem, method, findings, significance, and limitations with visuals matched to the audience level.",
      },
      {
        question: "Should paper explainers use realistic AI footage?",
        answer:
          "Usually not as the main format. Motion graphics, diagrams, and editorial visuals are typically clearer and more faithful to the research.",
      },
      {
        question: "How long should an academic paper explainer be?",
        answer:
          "It depends on the density of the paper and the intended viewer. The right length is long enough to explain the core ideas honestly without padding.",
      },
      {
        question: "Can this workflow work for non-academic technical PDFs?",
        answer:
          "Yes. White papers, technical memos, internal research summaries, and some investor-facing technical documents can benefit from the same structure.",
      },
      {
        question: "Where can I upload a paper in Latted?",
        answer:
          "You can upload a PDF and start the workflow from Latted's academic papers page.",
      },
    ],
  }),
  createBlogPost({
    slug: "webcam-to-studio-grade-launch-video",
    title: "How to Turn a Webcam Recording Into a Studio-Grade Launch Video",
    seoTitle:
      "How to Turn a Webcam Recording Into a Studio-Grade Launch Video in 2026",
    description:
      "Learn how to transform talking-head webcam footage into a polished launch video with style presets, cleaner presentation, and fast turnaround on Latted.",
    excerpt:
      "A practical launch-video workflow for turning everyday webcam footage into polished founder, marketing, and product announcement content.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 7,
    tags: [
      "Talking Head Video",
      "Launch Video",
      "Studio Workflow",
      "Latted",
      "Marketing Video",
    ],
    keywords: [
      "webcam to studio video",
      "talking head launch video",
      "how to polish a webcam recording",
      "Latted studio workflow",
      "AI launch video",
    ],
    mdxPublicPath: "/blog/webcam-to-studio-grade-launch-video.mdx",
    llmSummary:
      "Explains how Latted turns ordinary talking-head recordings into polished launch videos using studio style presets and an AI workflow.",
    faqItems: [
      {
        question: "Can I upload an existing webcam video to Latted?",
        answer:
          "Yes. Latted's studio workflow accepts uploaded talking-head videos, and you can also record directly in the browser.",
      },
      {
        question: "What is the best style for a product launch video?",
        answer:
          "It depends on the tone you want. Apple Keynote works well for clean product messaging, while Creator Setup often fits more informal updates.",
      },
      {
        question: "Do I need a perfect source video first?",
        answer:
          "No. The workflow is designed to improve everyday recordings, though cleaner framing and clearer audio still help the final result.",
      },
      {
        question: "Is this only for startup founders?",
        answer:
          "No. It also works for marketers, creators, educators, and teams that need polished customer-facing video without a traditional studio shoot.",
      },
      {
        question: "Where can I start the talking-head workflow?",
        answer:
          "You can start directly from Latted's studio page.",
      },
    ],
  }),
  createBlogPost({
    slug: "motion-graphics-vs-generated-footage",
    title: "Motion Graphics vs AI-Generated Footage: Which Should You Use?",
    seoTitle:
      "Motion Graphics vs AI-Generated Footage in 2026: Which Should You Use?",
    description:
      "Compare motion graphics and AI-generated footage for explainers, launches, and educational videos, with guidance on when each format works best inside Latted.",
    excerpt:
      "A practical guide to deciding between motion graphics and generated footage, and how to combine both in the same AI video project.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 7,
    tags: [
      "Motion Graphics",
      "AI Video Strategy",
      "Explainer Video",
      "Latted",
      "Remotion",
    ],
    keywords: [
      "motion graphics vs AI footage",
      "when to use motion graphics in AI video",
      "generated footage vs motion graphics",
      "Latted motion graphics",
      "AI explainer video format",
    ],
    mdxPublicPath: "/blog/motion-graphics-vs-generated-footage.mdx",
    llmSummary:
      "Explains when motion graphics are a better fit than generated footage and how Latted supports both formats in one project.",
    faqItems: [
      {
        question: "Are motion graphics better than AI footage for explainers?",
        answer:
          "Usually, yes. Motion graphics are generally clearer for diagrams, product steps, labels, and data-heavy concepts.",
      },
      {
        question: "When should I avoid using generated footage?",
        answer:
          "Avoid leaning on generated footage when the viewer needs exact visual structure, precise labeling, or consistent brand layout.",
      },
      {
        question: "Can I mix motion graphics and generated footage in the same video?",
        answer:
          "Yes. Generated footage can provide energy while motion graphics handle explanation, and that mix is often the strongest approach.",
      },
      {
        question: "What kinds of Latted workflows benefit most from motion graphics?",
        answer:
          "Product explainers, academic paper videos, launch sequences, typography-led ads, and any workflow that needs structured communication.",
      },
      {
        question: "Where can I see Latted's broader video workflow?",
        answer:
          "Start from Latted's homepage to see how motion graphics fit into the larger production studio workflow.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-video-audio-workflow-voice-cloning-lipsync",
    title: "How to Generate, Clone, and Lip Sync Audio for AI Videos in One Workflow",
    seoTitle:
      "How to Generate, Clone, and Lip Sync Audio for AI Videos in 2026",
    description:
      "A practical guide to AI video audio on Latted, covering voice generation, voice cloning, lip sync, music, sound effects, and timeline-based audio revision.",
    excerpt:
      "How to build cleaner AI video audio by keeping voices, music, lip sync, and timing in one editor instead of stitching them together later.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 8,
    tags: [
      "AI Video Audio",
      "Voice Cloning",
      "Lip Sync",
      "Latted",
      "Video Editing",
    ],
    keywords: [
      "AI video audio workflow",
      "voice cloning for AI video",
      "AI video lip sync",
      "Latted voice workflow",
      "generate clone lip sync audio",
    ],
    mdxPublicPath: "/blog/ai-video-audio-workflow-voice-cloning-lipsync.mdx",
    llmSummary:
      "Explains how Latted keeps voice generation, cloning, lip sync, music, sound effects, and timing in one audio workflow for AI video projects.",
    faqItems: [
      {
        question: "Why is audio so important in AI videos?",
        answer:
          "Audio controls pacing, credibility, and emotional tone. Strong visuals with weak narration or poor sync still feel unfinished.",
      },
      {
        question: "When should I clone a voice instead of generating one?",
        answer:
          "Clone a voice when continuity matters and generate a new voice when speed and experimentation matter more.",
      },
      {
        question: "Do I need lip sync for every project?",
        answer:
          "No. Lip sync matters most when a visible character or presenter is speaking. Pure motion graphics or montage-heavy videos may not need it.",
      },
      {
        question: "Should music come before or after the voiceover?",
        answer:
          "Usually after. Lock the narration timing first, then fit music underneath it so the message stays clear.",
      },
      {
        question: "Where can I start this workflow in Latted?",
        answer:
          "You can start from Latted's main app and use the editor's voice, audio, and scene tools inside a single project.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-video-keyframe-editing-guide",
    title: "How to Edit AI Video Keyframes Without Leaving the Timeline",
    seoTitle:
      "How to Edit AI Video Keyframes Without Leaving the Timeline in 2026",
    description:
      "Learn when to edit AI video keyframes instead of re-generating entire shots, and how Latted supports frame-level edits, camera changes, and version restore.",
    excerpt:
      "A frame-level workflow for fixing AI video scenes with in-editor keyframe edits instead of throwing away otherwise good generations.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 7,
    tags: [
      "Keyframe Editing",
      "AI Video Editor",
      "Timeline Editing",
      "Latted",
      "Video Production",
    ],
    keywords: [
      "AI video keyframe editing",
      "edit AI video frames",
      "fix AI video keyframes",
      "Latted keyframe editor",
      "AI video timeline editing",
    ],
    mdxPublicPath: "/blog/ai-video-keyframe-editing-guide.mdx",
    llmSummary:
      "Explains how Latted lets creators repair AI videos by editing keyframes in place rather than regenerating full scenes.",
    faqItems: [
      {
        question: "What is the main benefit of editing keyframes directly?",
        answer:
          "It gives you frame-level precision so you can repair a scene without throwing away the entire shot.",
      },
      {
        question: "When should I regenerate a shot instead of editing a keyframe?",
        answer:
          "Regenerate when the entire concept or motion is wrong. Edit the keyframe when the scene is mostly right and only needs targeted fixes.",
      },
      {
        question: "Can keyframe editing help with continuity?",
        answer:
          "Yes. It is a useful way to repair local issues that would otherwise create visible continuity breaks between scenes.",
      },
      {
        question: "Why is version restore important?",
        answer:
          "Because experimentation sometimes makes a scene worse. Version restore gives you a safe path back without forcing a restart.",
      },
      {
        question: "Where can I use keyframe editing in Latted?",
        answer:
          "You can use it inside Latted's editor as part of the broader project timeline workflow.",
      },
    ],
  }),
  createBlogPost({
    slug: "pay-as-you-go-ai-video-pricing-guide",
    title: "AI Video Pricing Guide: How to Choose the Right Model Without Overspending",
    seoTitle:
      "AI Video Pricing Guide 2026: How to Choose the Right Model Without Overspending",
    description:
      "A practical AI video pricing guide based on Latted's pay-as-you-go workflow, showing how to match model cost to the real needs of each scene.",
    excerpt:
      "How to control AI video spend by matching model choice to the job, using free image generation early, and upgrading only the scenes that need it.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 7,
    tags: [
      "AI Video Pricing",
      "Model Selection",
      "Pay As You Go",
      "Latted",
      "Video Workflow",
    ],
    keywords: [
      "AI video pricing guide",
      "pay as you go AI video",
      "how to choose AI video model",
      "Latted pricing guide",
      "AI video cost optimization",
    ],
    mdxPublicPath: "/blog/pay-as-you-go-ai-video-pricing-guide.mdx",
    llmSummary:
      "Explains how to use Latted's pay-as-you-go pricing and model variety to control AI video costs without sacrificing the scenes that matter most.",
    faqItems: [
      {
        question: "Is the cheapest AI video model usually the best value?",
        answer:
          "No. The best value is the model that gets the result you need with the fewest retries and the least production friction.",
      },
      {
        question: "Why does pay-as-you-go pricing help creators and teams?",
        answer:
          "It ties spend more directly to actual usage and makes it easier to choose different models for different parts of the same project.",
      },
      {
        question: "Should I use premium models for every scene?",
        answer:
          "Usually not. Premium models are best reserved for the moments where visual quality has the highest business or creative impact.",
      },
      {
        question: "How can I reduce AI video costs without hurting quality?",
        answer:
          "Use free image generation for concepting, iterate with lower-cost options early, and upgrade only the scenes that need better output.",
      },
      {
        question: "Where can I compare Latted's current model pricing?",
        answer:
          "You can compare current video model options on Latted's pricing page.",
      },
    ],
  }),
  createBlogPost({
    slug: "forkable-ai-video-project-templates",
    title: "How Teams Reuse AI Video Projects With Forkable Templates",
    seoTitle:
      "How Teams Reuse AI Video Projects With Forkable Templates in 2026",
    description:
      "Learn how reusable project structures, preview sharing, and forking help teams turn past Latted videos into faster repeatable workflows.",
    excerpt:
      "A guide to building reusable AI video templates so teams can start from proven project structure instead of a blank editor every time.",
    publishedAt: "2026-03-17",
    readingTimeMinutes: 7,
    tags: [
      "AI Video Templates",
      "Project Forking",
      "Team Workflow",
      "Latted",
      "Video Production",
    ],
    keywords: [
      "AI video templates",
      "forkable AI video projects",
      "team AI video workflow",
      "Latted project forking",
      "reusable video templates",
    ],
    mdxPublicPath: "/blog/forkable-ai-video-project-templates.mdx",
    llmSummary:
      "Explains how Latted's project-based workflow, preview sharing, and forking help teams build reusable AI video templates.",
    faqItems: [
      {
        question: "What is a forkable AI video template?",
        answer:
          "It is a reusable project that another creator or teammate can copy and adapt instead of starting from zero.",
      },
      {
        question: "Why are templates better than just copying old notes?",
        answer:
          "Templates keep the actual project structure, pacing, and reusable assets in context, which saves more time than notes alone.",
      },
      {
        question: "Which video formats are easiest to templatize?",
        answer:
          "Launch videos, product explainers, recurring educational formats, and branded social series are especially good template candidates.",
      },
      {
        question: "Do templates make creative work feel repetitive?",
        answer:
          "Not if they are built well. Good templates preserve structure and speed while still leaving room for new creative decisions.",
      },
      {
        question: "Where can teams manage reusable project workflows on Latted?",
        answer:
          "Teams can do this inside Latted's main app using project-based workflows, preview sharing, and forking.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-video-source-material-attachments-guide",
    title:
      "How to Turn PDFs, Markdown, Images, and Video Into Source Material for AI Video",
    seoTitle:
      "How to Turn PDFs, Markdown, Images, and Video Into Source Material for AI Video in 2026",
    description:
      "Learn how to use PDFs, Markdown, text files, images, video, and audio as source material for stronger AI video outputs and more grounded Latted projects.",
    excerpt:
      "A practical guide to attachment-driven AI video workflows that start from real source material instead of a blank prompt.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 8,
    tags: [
      "Source Material",
      "AI Video Workflow",
      "Attachments",
      "Latted",
      "Video Production",
    ],
    keywords: [
      "AI video source material",
      "attachments for AI video",
      "PDF to AI video workflow",
      "Markdown to AI video",
      "Latted attachments guide",
    ],
    mdxPublicPath: "/blog/ai-video-source-material-attachments-guide.mdx",
    llmSummary:
      "Explains how Latted uses PDFs, Markdown, text, images, video, and audio attachments to ground AI video generation in real source material.",
    faqItems: [
      {
        question: "Why do attachments improve AI video quality?",
        answer:
          "Attachments give the system concrete source material to work from, which usually leads to more grounded scripts, visuals, and scene choices than prompt-only generation.",
      },
      {
        question: "What file types can be useful as source material?",
        answer:
          "PDFs, Markdown files, text documents, images, video clips, and audio can all help, depending on whether you need factual grounding, style reference, product context, or narration guidance.",
      },
      {
        question: "Are attachments only useful for research-heavy videos?",
        answer:
          "No. They are just as useful for product demos, launch videos, brand explainers, and creator workflows because they preserve details that are easy to lose in a short prompt.",
      },
      {
        question: "Should I still write a prompt if I attach files?",
        answer:
          "Yes. The strongest workflow combines a clear brief with strong attachments so the AI knows both what to make and what source material to trust.",
      },
      {
        question: "Where can I start an attachment-driven project on Latted?",
        answer:
          "You can start from Latted's main app by adding attachments alongside your prompt before creating the project.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-video-storyboard-first-workflow",
    title: "How to Storyboard an AI Video Before You Generate Scenes",
    seoTitle:
      "How to Storyboard an AI Video Before You Generate Scenes in 2026",
    description:
      "See why storyboard-first AI video workflows produce cleaner pacing, better scene direction, and easier approvals than generating clips before the structure is clear.",
    excerpt:
      "A practical storyboard-first workflow for planning multi-scene AI videos before you spend time and credits on generation.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 7,
    tags: [
      "Storyboard",
      "AI Video Planning",
      "Scene Workflow",
      "Latted",
      "Video Production",
    ],
    keywords: [
      "AI video storyboard workflow",
      "storyboard before AI video generation",
      "how to plan AI video scenes",
      "Latted storyboard",
      "scene planning for AI video",
    ],
    mdxPublicPath: "/blog/ai-video-storyboard-first-workflow.mdx",
    llmSummary:
      "Explains why Latted's storyboard-first workflow helps creators plan pacing, prompts, and approvals before generating scenes.",
    faqItems: [
      {
        question: "Why should I storyboard an AI video before generating scenes?",
        answer:
          "Storyboarding clarifies pacing, scene order, and the job of each beat before you spend time generating footage, which usually reduces wasted iterations.",
      },
      {
        question: "What belongs in a strong AI video storyboard?",
        answer:
          "Each scene should have a communication goal, rough duration, visual direction, and any notes about audio, transitions, or references.",
      },
      {
        question: "Is storyboarding only for long videos?",
        answer:
          "No. It helps short videos too, especially if the piece still needs multiple beats, a CTA, or tight timing.",
      },
      {
        question: "When should I approve a scene?",
        answer:
          "Approve a scene when the concept, pacing, and structure are working well enough to justify full generation, not when every detail is already perfect.",
      },
      {
        question: "Where can I use this workflow in Latted?",
        answer:
          "You can use storyboard-first planning inside Latted's editor before generating or regenerating scenes.",
      },
    ],
  }),
  createBlogPost({
    slug: "text-to-video-vs-image-to-video-vs-video-to-video",
    title:
      "Text to Video vs Image to Video vs Video to Video: Which Workflow Should You Use?",
    seoTitle:
      "Text to Video vs Image to Video vs Video to Video in 2026: Which Workflow Should You Use?",
    description:
      "Compare text-to-video, image-to-video, and video-to-video workflows so you can choose the right Latted scene type for concepting, continuity, and revision.",
    excerpt:
      "A side-by-side guide to the three most common AI video workflows and the kinds of scenes each one is best at.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 8,
    tags: [
      "Text to Video",
      "Image to Video",
      "Video to Video",
      "Latted",
      "AI Video Workflow",
    ],
    keywords: [
      "text to video vs image to video",
      "video to video workflow",
      "best AI video workflow",
      "Latted scene types",
      "how to choose AI video mode",
    ],
    mdxPublicPath:
      "/blog/text-to-video-vs-image-to-video-vs-video-to-video.mdx",
    llmSummary:
      "Compares text-to-video, image-to-video, and video-to-video workflows in Latted so creators can choose the right mode per scene.",
    faqItems: [
      {
        question: "When should I use text to video?",
        answer:
          "Use text to video when you need a new concept from scratch and do not already have a frame, source clip, or reference you need to preserve.",
      },
      {
        question: "When is image to video the better choice?",
        answer:
          "Image to video is better when composition, subject identity, or brand style already exists in a frame you want to animate.",
      },
      {
        question: "What is video to video best for?",
        answer:
          "Video to video is best for transforming, restyling, or iterating on an existing source clip without rebuilding the motion from zero.",
      },
      {
        question: "Can I mix these workflows in one project?",
        answer:
          "Yes. Latted is designed so different scene types can live in the same timeline, which makes it easier to choose the right workflow for each beat.",
      },
      {
        question: "How do I avoid picking the wrong mode?",
        answer:
          "Start by asking what must stay fixed. If nothing exists yet, use text to video. If a frame must stay stable, use image to video. If a clip already exists, use video to video.",
      },
    ],
  }),
  createBlogPost({
    slug: "reference-to-video-brand-consistency-guide",
    title:
      "What Is Reference to Video? A Better Way to Keep Brand Style Consistent",
    seoTitle:
      "What Is Reference to Video in 2026? A Better Way to Keep Brand Style Consistent",
    description:
      "Learn when reference-to-video workflows are better than pure prompting for preserving subject fidelity, style continuity, and brand consistency inside Latted.",
    excerpt:
      "How reference-driven generation helps teams preserve product, character, and brand details across AI video scenes.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 7,
    tags: [
      "Reference to Video",
      "Brand Consistency",
      "AI Video Workflow",
      "Latted",
      "Video Production",
    ],
    keywords: [
      "what is reference to video",
      "reference to video brand consistency",
      "AI video style consistency",
      "Latted reference workflow",
      "preserve brand style in AI video",
    ],
    mdxPublicPath: "/blog/reference-to-video-brand-consistency-guide.mdx",
    llmSummary:
      "Explains how Latted's reference-to-video workflow helps preserve brand style and subject fidelity better than prompt-only generation.",
    faqItems: [
      {
        question: "What is reference to video?",
        answer:
          "Reference to video is a workflow where you guide generation with a source image or reference so the output stays closer to a specific subject, style, or visual identity.",
      },
      {
        question: "Why is reference to video useful for brands?",
        answer:
          "It makes it easier to preserve recurring product visuals, character design, and brand look across scenes instead of re-describing them every time.",
      },
      {
        question: "Is reference to video the same as image to video?",
        answer:
          "Not exactly. They are related, but reference workflows are especially useful when the goal is fidelity to a reference rather than simply animating one exact frame.",
      },
      {
        question: "What makes a good reference asset?",
        answer:
          "Clear images with strong composition, recognizable subject details, and the visual traits you actually want repeated tend to work best.",
      },
      {
        question: "Where can I use reference-driven scenes in Latted?",
        answer:
          "You can use reference-to-video workflows directly inside Latted's editor alongside other scene types in the same project.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-video-motion-control-guide",
    title: "How to Direct Camera Movement in AI Video With Motion Control",
    seoTitle:
      "How to Direct Camera Movement in AI Video With Motion Control in 2026",
    description:
      "A practical guide to motion-control AI video prompts, camera direction, and when to use controlled movement instead of generic scene generation on Latted.",
    excerpt:
      "How to use motion control to get cleaner push-ins, reveals, and directional moves instead of hoping the model guesses the camera language.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 7,
    tags: [
      "Motion Control",
      "Camera Direction",
      "AI Video Workflow",
      "Latted",
      "Video Editing",
    ],
    keywords: [
      "AI video motion control",
      "camera movement in AI video",
      "how to direct AI video camera",
      "Latted motion control",
      "AI video camera prompt guide",
    ],
    mdxPublicPath: "/blog/ai-video-motion-control-guide.mdx",
    llmSummary:
      "Explains how Latted's motion-control workflow helps creators direct camera movement more precisely in AI video scenes.",
    faqItems: [
      {
        question: "What is motion control in AI video?",
        answer:
          "Motion control is a workflow for directing how the camera or framing should move during a scene instead of leaving the movement fully up to the model.",
      },
      {
        question: "When is motion control better than a standard prompt?",
        answer:
          "It is better when the scene concept is fine but the camera language matters, such as product reveals, slow push-ins, or tightly choreographed movement.",
      },
      {
        question: "What kinds of moves work well with motion control?",
        answer:
          "Push-ins, pans, reveals, orbits, and other clearly defined moves work well when the start and end intent are easy to describe.",
      },
      {
        question: "Should I use motion control for every scene?",
        answer:
          "No. It is most helpful when movement itself carries meaning. Some scenes are better served by simpler generation or motion graphics.",
      },
      {
        question: "Where can I use motion control in Latted?",
        answer:
          "You can use motion-control scenes inside Latted's editor and combine them with other scene types in the same timeline.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-product-demo-videos-from-ui-assets",
    title: "How to Turn Product Screenshots and UI Assets Into Better AI Demo Videos",
    seoTitle:
      "How to Turn Product Screenshots and UI Assets Into Better AI Demo Videos in 2026",
    description:
      "Learn how to turn screenshots, recordings, product docs, and brand assets into clearer AI demo videos with Latted's attachment, motion graphics, and timeline workflows.",
    excerpt:
      "A product-demo workflow that starts from real UI assets instead of generic footage so the final video stays clear, branded, and accurate.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 8,
    tags: [
      "Product Demo Video",
      "UI Assets",
      "Motion Graphics",
      "Latted",
      "AI Video Workflow",
    ],
    keywords: [
      "AI product demo video",
      "turn screenshots into demo video",
      "UI assets to video",
      "Latted product demo workflow",
      "product explainer AI video",
    ],
    mdxPublicPath: "/blog/ai-product-demo-videos-from-ui-assets.mdx",
    llmSummary:
      "Explains how Latted turns screenshots, UI assets, docs, and recordings into clearer product demo videos than generic prompt-only generation.",
    faqItems: [
      {
        question: "Why are screenshots and UI assets useful for AI demo videos?",
        answer:
          "They keep the demo grounded in the real product, which makes the final video more accurate and more convincing than generic generated footage alone.",
      },
      {
        question: "What should I attach for a product demo?",
        answer:
          "Screenshots, short screen recordings, product docs, brand assets, and any key messaging notes are all useful inputs.",
      },
      {
        question: "Should product demos use motion graphics or generated footage?",
        answer:
          "Most product demos benefit from motion graphics and UI-led storytelling first, with generated footage used only when it supports the message.",
      },
      {
        question: "Can I still add voiceover and pacing edits?",
        answer:
          "Yes. Latted's project workflow lets you combine product visuals with voiceover, music, and timeline edits in the same workspace.",
      },
      {
        question: "Where can I start a product-demo workflow on Latted?",
        answer:
          "You can start from Latted's main app by attaching source material and building the demo in the editor.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-video-scene-approval-version-restore",
    title: "How to Review, Approve, and Restore AI Video Versions Scene by Scene",
    seoTitle:
      "How to Review, Approve, and Restore AI Video Versions Scene by Scene in 2026",
    description:
      "See how scene approvals, targeted regeneration, and version restore make AI video revision safer and faster inside Latted's editor.",
    excerpt:
      "A scene-by-scene review workflow for AI video teams that need tighter approvals and less fear around iteration.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 7,
    tags: [
      "Scene Approval",
      "Version Restore",
      "AI Video Editing",
      "Latted",
      "Revision Workflow",
    ],
    keywords: [
      "AI video scene approval",
      "restore AI video version",
      "scene by scene AI video review",
      "Latted version restore",
      "AI video revision workflow",
    ],
    mdxPublicPath: "/blog/ai-video-scene-approval-version-restore.mdx",
    llmSummary:
      "Explains how Latted's scene approvals and version restore features help teams review and revise AI videos without losing progress.",
    faqItems: [
      {
        question: "Why review AI videos scene by scene?",
        answer:
          "Scene-level review is faster and less risky because you can fix the specific beat that is wrong without disrupting the whole project.",
      },
      {
        question: "What does version restore help with?",
        answer:
          "Version restore gives you a way to recover a previous video or keyframe state if an experiment makes the scene worse.",
      },
      {
        question: "When should I regenerate instead of restoring?",
        answer:
          "Regenerate when the current concept still is not right. Restore when you already had a better version and just need to go back safely.",
      },
      {
        question: "Can approvals speed up team workflows?",
        answer:
          "Yes. Approvals help teams lock the scenes that are ready so effort stays focused on the parts that still need work.",
      },
      {
        question: "Where can I review versions in Latted?",
        answer:
          "You can review scene outputs, video versions, and keyframe versions directly inside Latted's editor.",
      },
    ],
  }),
  createBlogPost({
    slug: "public-project-preview-and-forking-guide",
    title: "How Teams Share AI Video Drafts With Public Previews and Forkable Projects",
    seoTitle:
      "How Teams Share AI Video Drafts With Public Previews and Forkable Projects in 2026",
    description:
      "Learn how public previews and forkable projects help teams review Latted drafts, reuse winning structures, and turn finished videos into repeatable workflows.",
    excerpt:
      "Why sharing the project instead of only the export creates faster review loops and better reusable templates for AI video teams.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 7,
    tags: [
      "Project Sharing",
      "Forkable Projects",
      "Team Workflow",
      "Latted",
      "Video Production",
    ],
    keywords: [
      "public project preview",
      "forkable AI video projects",
      "share AI video drafts",
      "Latted public preview",
      "AI video team workflow",
    ],
    mdxPublicPath: "/blog/public-project-preview-and-forking-guide.mdx",
    llmSummary:
      "Explains how Latted's public previews and project forking help teams review drafts and reuse proven video structures.",
    faqItems: [
      {
        question: "Why share a project preview instead of only an exported video?",
        answer:
          "A preview preserves more context about the project, which helps reviewers understand what is inside the draft and what can be reused or improved.",
      },
      {
        question: "What is a forkable project?",
        answer:
          "It is a reusable project that another person can copy into their own workspace and adapt instead of starting from zero.",
      },
      {
        question: "How do public previews help teams?",
        answer:
          "They make it easier to review drafts, inspect project stats, and decide whether a workflow is worth reusing before someone forks it.",
      },
      {
        question: "Which projects are best to fork into templates?",
        answer:
          "Recurring launch videos, product explainers, educational formats, and branded content series are usually strong template candidates.",
      },
      {
        question: "Where can I use this on Latted?",
        answer:
          "Public preview and forking workflows are available through Latted's project-based sharing flow.",
      },
    ],
  }),
  createBlogPost({
    slug: "multi-model-ai-video-workflow-guide",
    title:
      "Why 20+ AI Video Models in One Editor Beats Picking a Single Favorite",
    seoTitle:
      "Why 20+ AI Video Models in One Editor Beats Picking a Single Favorite in 2026",
    description:
      "See why multi-model AI video workflows outperform single-model loyalty, especially when Latted lets you choose the right scene type and model per beat.",
    excerpt:
      "A practical case for choosing models scene by scene instead of forcing one generator to do every job in a video project.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 8,
    tags: [
      "AI Video Models",
      "Model Selection",
      "Unified Timeline",
      "Latted",
      "Video Workflow",
    ],
    keywords: [
      "multi model AI video workflow",
      "best AI video model per scene",
      "20 AI video models one editor",
      "Latted model workflow",
      "AI video model selection",
    ],
    mdxPublicPath: "/blog/multi-model-ai-video-workflow-guide.mdx",
    llmSummary:
      "Explains why Latted's multi-model editor is stronger than relying on one favorite AI video model for every scene.",
    faqItems: [
      {
        question: "Why is one AI video model usually not enough?",
        answer:
          "Different models are better at different jobs, so forcing one model to handle every scene usually creates unnecessary compromises.",
      },
      {
        question: "How should I choose a model?",
        answer:
          "Choose the model based on the job of the scene, such as concept generation, continuity, transformation, or premium hero moments.",
      },
      {
        question: "Does using more models make editing harder?",
        answer:
          "It does if every model lives in a separate tool. Latted solves that by keeping different workflows inside one editor and one timeline.",
      },
      {
        question: "Can multi-model workflows reduce cost?",
        answer:
          "Yes. They let you reserve higher-cost options for the scenes that need them and use simpler workflows elsewhere.",
      },
      {
        question: "Where can I compare Latted's model options?",
        answer:
          "You can explore the editor from Latted's homepage and compare current pay-as-you-go rates on the pricing page.",
      },
    ],
  }),
  createBlogPost({
    slug: "ai-agent-human-editor-handoff-guide",
    title: "How AI Agents and Human Editors Work Together in One Video Project",
    seoTitle:
      "How AI Agents and Human Editors Work Together in One Video Project in 2026",
    description:
      "Learn how Latted combines agent-driven scoping, scripting, assets, voices, and scene drafts with human editorial control inside one timeline.",
    excerpt:
      "A practical guide to dividing the work between AI and humans so AI video projects move faster without losing creative judgment.",
    publishedAt: "2026-03-18",
    readingTimeMinutes: 8,
    tags: [
      "AI Agent Workflow",
      "Human in the Loop",
      "AI Video Editing",
      "Latted",
      "Video Production",
    ],
    keywords: [
      "AI agent human editor workflow",
      "human in the loop AI video",
      "AI video project handoff",
      "Latted agent workflow",
      "AI video editing collaboration",
    ],
    mdxPublicPath: "/blog/ai-agent-human-editor-handoff-guide.mdx",
    llmSummary:
      "Explains how Latted hands work from AI agents to human editors so scoping and draft generation happen faster without losing editorial control.",
    faqItems: [
      {
        question: "What should an AI agent do in a video workflow?",
        answer:
          "An AI agent is strongest at turning a brief into a first draft with scope, script, assets, and scene structure so the editor starts with momentum.",
      },
      {
        question: "What should the human editor still control?",
        answer:
          "The human should control taste, pacing, message clarity, brand judgment, and the final decisions about what gets approved or changed.",
      },
      {
        question: "Why is the handoff between AI and editing important?",
        answer:
          "If the handoff is weak, the project feels fragmented. If it is strong, the AI speeds up drafting and the human focuses on higher-value editorial choices.",
      },
      {
        question: "Does this only help large teams?",
        answer:
          "No. Solo creators benefit too because the AI handles more setup work while the creator spends more time shaping the final piece.",
      },
      {
        question: "Where can I start this workflow on Latted?",
        answer:
          "You can start from Latted's main app, let the agent build the project draft, and then move into the editor for refinement.",
      },
    ],
  }),
  createBlogPost({
    slug: "seedance-2-api-access-real-user-review",
    contentType: "Creator review",
    title: "How to Access Seedance 2.0 API for AI Video Generation (Real User Review)",
    seoTitle:
      "How to Access Seedance 2.0 API in 2026: Real User Review, Reliability, and Best Platform",
    description:
      "A detailed creator review on accessing Seedance 2.0 Pro through Latted after CapCut rollback issues, including failed generation credits and platform reliability.",
    excerpt:
      "A creator explains what happened when Seedance 2.0 disappeared from CapCut and why Latted became the reliable fallback for fast, stable API access.",
    publishedAt: "2026-03-01",
    readingTimeMinutes: 6,
    tags: [
      "Seedance 2.0 API",
      "AI Video Generation",
      "CapCut Alternative",
      "Latted",
      "ByteDance API",
    ],
    keywords: [
      "Seedance 2.0 API access",
      "Seedance 2.0 Pro on Latted",
      "CapCut Seedance rollback",
      "AI video API review",
      "best Seedance API platform",
    ],
    coverImagePath: "/blog/covers/seedance-2-api-access-light.svg",
    coverImageDarkPath: "/blog/covers/seedance-2-api-access-dark.svg",
    coverImageAlt:
      "Abstract cinematic cover art with layered gradients and motion forms",
    mdxPublicPath: "/blog/seedance-2-api-access-real-user-review.mdx",
    llmSummary:
      "Real user review of Seedance 2.0 API access after CapCut rollback, with a practical breakdown of why Latted was more reliable for creators.",
    faqItems: [
      {
        question: "Is Seedance 2.0 API currently available on Latted?",
        answer:
          "Yes. Latted provides active access to Seedance 2.0 and Seedance 2.0 Pro based on current model availability.",
      },
      {
        question: "Why do creators look for a CapCut alternative for Seedance 2.0?",
        answer:
          "Creators want predictable API availability, clear credit handling for failed generations, and better support when model access changes unexpectedly.",
      },
      {
        question: "What should I evaluate before choosing an AI video API provider?",
        answer:
          "Check model uptime, refund behavior for failed jobs, per-second pricing, output consistency, and support responsiveness.",
      },
    ],
  }),
  createBlogPost({
    slug: "seedance-2-prompt-guide-hollywood-action-scenes",
    contentType: "Prompt tutorial",
    title: "How to Prompt Seedance 2.0 for Hollywood-Quality AI Video (Action Scene Tutorial)",
    seoTitle:
      "Seedance 2.0 Prompt Guide: Hollywood-Quality Action Scene Tutorial + Full Example Prompt",
    description:
      "Step-by-step Seedance 2.0 prompting workflow for cinematic action scenes, including second-by-second prompt structure, LLM prompt engineering tips, and a full working template.",
    excerpt:
      "Learn a practical prompting method for Seedance 2.0 with second-by-second choreography, weapon transitions, and a full copy-ready example prompt.",
    publishedAt: "2026-03-01",
    readingTimeMinutes: 9,
    tags: [
      "Seedance 2.0 Prompting",
      "AI Video Tutorial",
      "Prompt Engineering",
      "Text to Video",
      "Latted",
    ],
    keywords: [
      "how to prompt Seedance 2.0",
      "Seedance 2.0 action prompt",
      "Hollywood AI video prompt",
      "Seedance text to video tips",
      "LLM prompt engineering for video",
    ],
    coverImagePath: "/blog/covers/seedance-2-prompting-light.svg",
    coverImageDarkPath: "/blog/covers/seedance-2-prompting-dark.svg",
    coverImageAlt:
      "Abstract technical cover art with cinematic ribbons and timeline-inspired geometry",
    mdxPublicPath: "/blog/seedance-2-prompt-guide-hollywood-action-scenes.mdx",
    llmSummary:
      "Technical tutorial for generating cinematic Seedance 2.0 action videos using second-by-second prompting and LLM-assisted prompt engineering.",
    faqItems: [
      {
        question: "How long should a Seedance 2.0 action prompt be?",
        answer:
          "Long enough to describe motion and timing clearly. For complex choreography, a structured 10 to 15 second prompt can be very detailed.",
      },
      {
        question: "Is second-by-second prompting required?",
        answer:
          "Not always, but it significantly improves coherence in scenes with multiple actors, camera moves, and prop transitions.",
      },
      {
        question: "Can I reuse this prompting method outside action scenes?",
        answer:
          "Yes. The same structure works for explainers, medical simulations, historical reenactments, and product demos.",
      },
    ],
  }),
  createBlogPost({
    slug: "prompt-box-vs-production-studio-ai-video",
    title:
      "Why AI Video Teams Need More Than a Prompt Box and a Download Button",
    seoTitle:
      "Why AI Video Teams Need More Than a Prompt Box and a Download Button in 2026",
    description:
      "Learn why serious AI-video work needs more than a prompt box, and how Latted's timeline, assets, audio, approvals, and export workflow support real production.",
    excerpt:
      "A practical case for moving from isolated AI-video generation to a full production workflow inside one editor.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "AI Video Production",
      "Workflow Design",
      "Unified Editor",
      "Latted",
      "Video Teams",
    ],
    keywords: [
      "prompt box vs AI video editor",
      "AI video production workflow",
      "why AI video teams need a timeline",
      "Latted production studio workflow",
      "AI video editor beyond prompting",
    ],
    mdxPublicPath: "/blog/prompt-box-vs-production-studio-ai-video.mdx",
    llmSummary:
      "Explains why serious AI-video teams need a production workflow with scenes, timeline, assets, audio, approvals, and export instead of relying on prompt-only tools.",
    faqItems: [
      {
        question: "Why is a prompt box not enough for AI video production?",
        answer:
          "Because finished video projects need editing, scene control, asset reuse, audio, approvals, and export, not just an initial generation.",
      },
      {
        question:
          "What is the difference between AI video generation and AI video production?",
        answer:
          "Generation creates individual outputs, while production includes planning, refining, sequencing, approving, and delivering a complete video.",
      },
      {
        question:
          "When should I move from a prompt-only tool to a full editor?",
        answer:
          "Move as soon as the project has multiple scenes, recurring assets, real audio requirements, or stakeholders who need to review the work.",
      },
      {
        question: "How does Latted handle more than prompting?",
        answer:
          "Latted combines prompting with timeline editing, assets, keyframes, audio tools, multiple video types, approvals, and export in one workspace.",
      },
      {
        question: "What kinds of teams benefit most from this workflow?",
        answer:
          "Teams making launch videos, demos, explainers, ads, and other multi-step AI-video projects benefit most because those projects require coordination after generation.",
      },
    ],
  }),
  createBlogPost({
    slug: "mention-assets-and-keyframes-in-ai-video-prompts",
    title:
      "How to Use @ Mentions to Pull Assets and Keyframes Into AI Video Prompts",
    seoTitle:
      "How to Use @ Mentions to Pull Assets and Keyframes Into AI Video Prompts in 2026",
    description:
      "See how Latted's prompt workflow helps teams reference existing assets and keyframes directly so AI-video prompts stay clearer and more grounded.",
    excerpt:
      "A practical guide to using `@` mentions so project assets and keyframes become part of the prompt context instead of being re-described manually.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "AI Prompting",
      "Asset Mentions",
      "Keyframes",
      "Latted",
      "Workflow",
    ],
    keywords: [
      "AI video prompt mentions",
      "mention assets in AI prompts",
      "mention keyframes in AI video",
      "Latted prompt mentions",
      "reference assets in prompt workflow",
    ],
    mdxPublicPath:
      "/blog/mention-assets-and-keyframes-in-ai-video-prompts.mdx",
    llmSummary:
      "Explains how Latted users can reference project assets and keyframes directly inside prompts to make AI-video generations more specific and consistent.",
    faqItems: [
      {
        question: "Why use @ mentions in AI-video prompts?",
        answer:
          "Because they let you reference exact project items directly, which is usually clearer than repeatedly describing the same asset or keyframe in words alone.",
      },
      {
        question: "What can I mention in Latted prompts?",
        answer:
          "You can mention project items such as assets and keyframes that are available to the prompt workflow.",
      },
      {
        question: "Do mentions replace the prompt completely?",
        answer:
          "No. They supply context, but you still need to explain what change or output you want from that reference.",
      },
      {
        question: "When are mentions most useful?",
        answer:
          "They are most useful in larger projects where continuity matters and the project already contains reusable visual material.",
      },
      {
        question: "How do mentions help consistency?",
        answer:
          "They reduce ambiguity by grounding the prompt in the exact asset or frame you want the scene to use as a reference.",
      },
    ],
  }),
  createBlogPost({
    slug: "project-style-presets-for-ai-video-consistency",
    title:
      "How to Use Project Style Presets to Keep AI Videos Visually Consistent",
    seoTitle:
      "How to Use Project Style Presets to Keep AI Videos Visually Consistent in 2026",
    description:
      "Learn how Latted's project style presets help teams set visual direction once and keep multi-scene AI-video projects more coherent.",
    excerpt:
      "A practical guide to choosing project-wide style presets so scenes inherit a stronger shared look instead of drifting apart visually.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "Project Style",
      "Visual Consistency",
      "AI Video Workflow",
      "Latted",
      "Creative Direction",
    ],
    keywords: [
      "project style presets AI video",
      "keep AI videos visually consistent",
      "Latted style presets",
      "AI video visual direction",
      "project level style workflow",
    ],
    mdxPublicPath: "/blog/project-style-presets-for-ai-video-consistency.mdx",
    llmSummary:
      "Explains how Latted's project style presets help users set a shared visual system across scenes so AI-video projects feel more coherent.",
    faqItems: [
      {
        question: "Why use a style preset in an AI-video project?",
        answer:
          "Because it gives the whole project a shared visual baseline, which makes scene outputs feel more coherent across the timeline.",
      },
      {
        question: "Are style presets only useful for cinematic videos?",
        answer:
          "No. They are useful for cinematic, documentary, motion-graphics, and other visual systems as long as the project needs consistency.",
      },
      {
        question: "When should I choose a custom style?",
        answer:
          "Choose a custom style when the built-in presets are close but still not specific enough for the project or brand.",
      },
      {
        question: "Do style presets replace scene prompts?",
        answer:
          "No. The style preset sets the overall look, while the scene prompt still defines the subject, action, timing, and context.",
      },
      {
        question: "Where do I set project style in Latted?",
        answer:
          "You can set it from the project config workflow, where style lives alongside other project-wide visual settings.",
      },
    ],
  }),
  createBlogPost({
    slug: "upload-asset-vs-generate-from-scratch-ai-video",
    title: "When to Upload an Asset vs Generate One From Scratch for AI Video",
    seoTitle:
      "When to Upload an Asset vs Generate One From Scratch for AI Video in 2026",
    description:
      "A practical decision guide to Latted's asset workflow so teams know when real references should be uploaded and when fresh assets should be generated.",
    excerpt:
      "How to decide between uploading a real reference and generating a new asset from scratch before AI-video scene creation begins.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "Asset Workflow",
      "Reference Material",
      "AI Video Workflow",
      "Latted",
      "Creative Production",
    ],
    keywords: [
      "upload asset vs generate from scratch",
      "AI video asset workflow",
      "when to upload references for AI video",
      "Latted asset creation",
      "generate assets from scratch video workflow",
    ],
    mdxPublicPath: "/blog/upload-asset-vs-generate-from-scratch-ai-video.mdx",
    llmSummary:
      "Explains when Latted users should upload existing source material versus generate a fresh asset from scratch before building AI-video scenes.",
    faqItems: [
      {
        question: "When should I upload an asset instead of generating one?",
        answer:
          "Upload when the project depends on a real reference staying recognizable, such as a product, UI, or specific branded material.",
      },
      {
        question: "When is generating an asset from scratch better?",
        answer:
          "It is better when you are exploring concepts and the visual does not already exist in a form the project must preserve.",
      },
      {
        question: "Can I mix uploaded and generated assets in one video project?",
        answer:
          "Yes. Many strong AI-video projects use uploaded references for grounded elements and generated assets for concepting or supporting visuals.",
      },
      {
        question: "Does uploading an asset reduce iteration?",
        answer:
          "Usually yes, because it removes ambiguity around what the model is supposed to preserve.",
      },
      {
        question: "Where do I create or upload assets in Latted?",
        answer:
          "You can do both from the assets workflow inside the editor, where uploaded and generated assets become reusable project material.",
      },
    ],
  }),
  createBlogPost({
    slug: "reusable-ai-voice-library-for-video-projects",
    title: "How to Build a Reusable AI Voice Library for Video Projects",
    seoTitle:
      "How to Build a Reusable AI Voice Library for Video Projects in 2026",
    description:
      "Learn how Latted's editor helps teams save, preview, rename, and reuse voices so narration stays consistent across scenes and recurring video work.",
    excerpt:
      "A practical guide to turning one-off voice generations into a reusable voice library for more consistent AI-video narration.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "Voice Library",
      "AI Voice",
      "Audio Workflow",
      "Latted",
      "Narration",
    ],
    keywords: [
      "reusable AI voice library",
      "manage voices for AI video",
      "Latted voice library",
      "AI video narration consistency",
      "save and reuse AI voices",
    ],
    mdxPublicPath: "/blog/reusable-ai-voice-library-for-video-projects.mdx",
    llmSummary:
      "Explains how Latted users can build a reusable project voice library by creating, cloning, previewing, renaming, and reusing saved voices.",
    faqItems: [
      {
        question: "Why build a reusable AI voice library?",
        answer:
          "Because it keeps narration more consistent across scenes and makes it easier to reuse voices that already fit the project.",
      },
      {
        question: "What should I save in a voice library?",
        answer:
          "Save the voices that are distinctive, usable, and likely to appear again in future scenes or projects.",
      },
      {
        question: "When should I clone a voice?",
        answer:
          "Clone a voice when preserving a specific identity matters more than generating a fresh option from scratch.",
      },
      {
        question: "Why is renaming voices important?",
        answer:
          "Because clear names make it easier for you and your team to find the right voice later without guessing.",
      },
      {
        question: "Where can I manage saved voices in Latted?",
        answer:
          "You can manage them from the editor's voice workflow, where saved voices can be previewed, named, and reused.",
      },
    ],
  }),
  createBlogPost({
    slug: "export-ai-video-after-edit-approval",
    title: "How to Export an AI Video After the Edit Is Approved",
    seoTitle:
      "How to Export an AI Video After the Edit Is Approved in 2026",
    description:
      "See how Latted's in-editor export workflow helps teams deliver finished AI videos only after pacing, approvals, audio, and finishing passes are locked.",
    excerpt:
      "A practical guide to knowing when an AI-video project is export-ready and why export should follow approval rather than replace it.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "Video Export",
      "Post Production",
      "AI Video Editing",
      "Latted",
      "Approval Workflow",
    ],
    keywords: [
      "export AI video after approval",
      "when to export AI video",
      "Latted export workflow",
      "AI video finishing checklist",
      "export ready AI video project",
    ],
    mdxPublicPath: "/blog/export-ai-video-after-edit-approval.mdx",
    llmSummary:
      "Explains how Latted users can tell when an AI-video project is ready for export after approvals, audio review, and final finishing decisions are complete.",
    faqItems: [
      {
        question: "When should I export an AI video?",
        answer:
          "Export when the project structure, approved scenes, audio, and finishing decisions are stable enough that delivery matters more than further iteration.",
      },
      {
        question: "Should export happen before scene approval?",
        answer:
          "Usually no. Approval should come first so export reflects the versions you actually want to deliver.",
      },
      {
        question: "Is export the right time to fix quality issues?",
        answer:
          "Only minor finishing issues. If the concept or motion is wrong, you should revise the scene before exporting.",
      },
      {
        question: "Should I upscale before export?",
        answer:
          "Only if the scene already works creatively and the main remaining issue is output resolution or polish.",
      },
      {
        question: "Where can I export on Latted?",
        answer:
          "You can export from inside the editor, where final delivery stays connected to the rest of the project workflow.",
      },
    ],
  }),
  createBlogPost({
    slug: "activity-feed-for-background-ai-video-generation",
    title: "Why an Activity Feed Matters When AI Videos Generate in the Background",
    seoTitle:
      "Why an Activity Feed Matters When AI Videos Generate in the Background in 2026",
    description:
      "Learn how Latted's activity workflow helps editors monitor generating, ready, and failed outputs while scenes, assets, and videos finish in parallel.",
    excerpt:
      "A practical look at why AI-video projects need an activity feed when generation happens asynchronously across many outputs.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "Activity Feed",
      "Background Tasks",
      "AI Video Editing",
      "Latted",
      "Workflow",
    ],
    keywords: [
      "activity feed AI video",
      "background AI video generation workflow",
      "track AI video generation status",
      "Latted activity feed",
      "AI video ready failed generating states",
    ],
    mdxPublicPath:
      "/blog/activity-feed-for-background-ai-video-generation.mdx",
    llmSummary:
      "Explains why Latted surfaces activity for in-progress, ready, and failed outputs so editors can manage background AI-video generation more effectively.",
    faqItems: [
      {
        question: "Why does AI-video generation need an activity feed?",
        answer:
          "Because outputs finish asynchronously, and editors need a reliable way to see what is still generating, what is ready, and what failed.",
      },
      {
        question: "What should an activity feed show?",
        answer:
          "It should show the important status changes for project outputs, especially items that are generating, ready to review, or failed.",
      },
      {
        question: "Does this only help large teams?",
        answer:
          "No. Solo creators benefit too because background tasks are easy to lose track of even in smaller projects.",
      },
      {
        question: "What should I do when something becomes ready?",
        answer:
          "Review it quickly, decide whether it moves forward, and clear it from your mental queue so the project stays organized.",
      },
      {
        question: "Where do I see this workflow in Latted?",
        answer:
          "You can use the activity area inside the editor to monitor new and in-progress content while continuing other work.",
      },
    ],
  }),
  createBlogPost({
    slug: "academic-paper-video-proficiency-level-guide",
    title: "How to Choose the Right Technical Depth for Academic Paper Videos",
    seoTitle:
      "How to Choose the Right Technical Depth for Academic Paper Videos in 2026",
    description:
      "A practical guide to Latted's academic-paper audience settings so explainer videos match beginner, intermediate, or advanced viewers more accurately.",
    excerpt:
      "How to pick the right proficiency level before turning an academic paper into a video so the explanation lands with the intended audience.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "Academic Explainers",
      "Audience Targeting",
      "Education Video",
      "Latted",
      "Research Communication",
    ],
    keywords: [
      "academic paper video technical depth",
      "choose proficiency level explainer video",
      "Latted academic paper workflow",
      "beginner intermediate advanced explainer",
      "research video audience targeting",
    ],
    mdxPublicPath: "/blog/academic-paper-video-proficiency-level-guide.mdx",
    llmSummary:
      "Explains how Latted's academic-paper workflow lets users choose beginner, intermediate, or advanced technical depth before generating a video explainer.",
    faqItems: [
      {
        question:
          "Why choose technical depth before generating an academic-paper video?",
        answer:
          "Because the audience level determines how much context, jargon, and explanation structure the final video needs.",
      },
      {
        question:
          "What is the difference between beginner and advanced paper videos?",
        answer:
          "Beginner videos explain more fundamentals in simpler language, while advanced videos assume more prior knowledge and spend more time on methods, tradeoffs, and technical detail.",
      },
      {
        question: "Is intermediate usually the safest default?",
        answer:
          "It often is when the audience already knows the fundamentals but still needs clear explanation of what is new and important in the paper.",
      },
      {
        question: "Does a beginner version make the paper less accurate?",
        answer:
          "Not if it is done well. The goal is clearer teaching, not weaker reasoning.",
      },
      {
        question: "Where do I set this in Latted?",
        answer:
          "You can choose the target proficiency level in Latted's academic-paper workflow before starting the explainer generation flow.",
      },
    ],
  }),
  createBlogPost({
    slug: "per-second-pricing-table-for-ai-video-models",
    title: "How to Use Per-Second Pricing Tables to Choose the Right AI Video Model",
    seoTitle:
      "How to Use Per-Second Pricing Tables to Choose the Right AI Video Model in 2026",
    description:
      "Learn how Latted's searchable per-second pricing table helps creators compare AI-video models more rationally before spending on generation.",
    excerpt:
      "A practical pricing workflow for choosing AI-video models by scene requirement and visible per-second cost instead of vague plan tiers.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "AI Video Pricing",
      "Model Selection",
      "Pay As You Go",
      "Latted",
      "Budget Planning",
    ],
    keywords: [
      "per second pricing AI video",
      "choose AI video model by price",
      "Latted pricing table",
      "AI video model cost comparison",
      "pay as you go AI video pricing",
    ],
    mdxPublicPath: "/blog/per-second-pricing-table-for-ai-video-models.mdx",
    llmSummary:
      "Explains how Latted's searchable, sortable per-second pricing table helps users choose AI-video models based on creative needs and cost visibility.",
    faqItems: [
      {
        question: "Why use a per-second pricing table for AI video?",
        answer:
          "Because it makes the cost of model choice visible at the scene level, which helps you plan before generating.",
      },
      {
        question: "What should I look for in a pricing table?",
        answer:
          "Look for the model family, final price per second, and search or sort tools that help you compare options quickly.",
      },
      {
        question: "Does the cheapest model always make the most sense?",
        answer:
          "No. The goal is not to choose the cheapest model every time. It is to choose the model whose cost matches the creative requirement.",
      },
      {
        question: "Why separate image and video pricing?",
        answer:
          "Because image generation is often better for early exploration, while video spend makes more sense once direction is already clear.",
      },
      {
        question: "Where can I compare model rates in Latted?",
        answer:
          "You can compare them on Latted's pricing page, which lists searchable, sortable final per-second rates for video models.",
      },
    ],
  }),
  createBlogPost({
    slug: "search-filter-and-track-ai-video-projects",
    title: "How to Search, Filter, and Track AI Video Projects Across a Large Backlog",
    seoTitle:
      "How to Search, Filter, and Track AI Video Projects Across a Large Backlog in 2026",
    description:
      "See how Latted's projects page helps creators and teams search, filter, and monitor many AI-video drafts with clearer backlog visibility.",
    excerpt:
      "A practical guide to project backlog management for AI-video teams handling multiple drafts, statuses, and in-flight generations.",
    publishedAt: "2026-03-22",
    readingTimeMinutes: 7,
    tags: [
      "Project Management",
      "AI Video Teams",
      "Status Tracking",
      "Latted",
      "Workflow",
    ],
    keywords: [
      "search AI video projects",
      "filter AI video backlog",
      "track AI video project status",
      "Latted projects page",
      "AI video project management workflow",
    ],
    mdxPublicPath: "/blog/search-filter-and-track-ai-video-projects.mdx",
    llmSummary:
      "Explains how Latted's projects page helps users search, filter, and monitor many AI-video drafts with better backlog visibility and live status updates.",
    faqItems: [
      {
        question:
          "Why do AI-video teams need search and filtering on a projects page?",
        answer:
          "Because once many drafts are active, teams need a fast way to find specific projects and separate processing work from completed work.",
      },
      {
        question: "What is the difference between search and status filters?",
        answer:
          "Search helps you locate a known project by name, while status filters help you inspect the current state of the backlog.",
      },
      {
        question: "Why are live status updates useful?",
        answer:
          "They reduce manual checking by showing when projects change state while you are working elsewhere.",
      },
      {
        question: "Does this only matter for large organizations?",
        answer:
          "No. It also matters for solo creators or small teams once several drafts are in flight at the same time.",
      },
      {
        question: "Where can I manage the project backlog in Latted?",
        answer:
          "You can manage it from Latted's projects view, which is designed for discovering and monitoring active and completed drafts.",
      },
    ],
  }),
  createBlogPost({
    slug: "magic-select-for-ai-video-keyframe-fixes",
    title:
      "How to Use Magic Select for Faster AI Video Keyframe Fixes",
    seoTitle:
      "How to Use Magic Select for Faster AI Video Keyframe Fixes in 2026",
    description:
      "Learn how Latted's Magic Select workflow helps teams isolate, duplicate, delete, and edit specific keyframe regions instead of regenerating an entire shot.",
    excerpt:
      "A practical guide to object-level keyframe editing so one bad region does not force a full scene restart.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Magic Select",
      "Keyframe Editing",
      "AI Video Workflow",
      "Latted",
      "Object Editing",
    ],
    keywords: [
      "Magic Select AI video",
      "object level AI keyframe editing",
      "duplicate masked region AI video",
      "delete object from AI keyframe",
      "Latted Magic Select workflow",
    ],
    mdxPublicPath: "/blog/magic-select-for-ai-video-keyframe-fixes.mdx",
    llmSummary:
      "Explains how Latted users can isolate one or more regions inside a keyframe, then duplicate, delete, or edit those regions without regenerating the entire shot.",
    faqItems: [
      {
        question: "What is Magic Select in an AI-video editor?",
        answer:
          "It is a region-based editing workflow that lets you isolate specific parts of a keyframe so you can change them without rebuilding the whole image.",
      },
      {
        question: "When is Magic Select better than regenerating the full scene?",
        answer:
          "It is better when the scene is mostly correct and only one object, prop, or region needs to change.",
      },
      {
        question: "Can I select more than one region at a time?",
        answer:
          "Yes. Latted supports multi-region selection so several local problems can be handled in one editing pass.",
      },
      {
        question: "What can I do after making a selection?",
        answer:
          "You can use the selected region to duplicate an element, remove it, or send it into a more targeted edit workflow.",
      },
      {
        question: "Why does this matter for AI-video continuity?",
        answer:
          "Because local fixes preserve the rest of the shot, which makes continuity safer than starting over from scratch.",
      },
    ],
  }),
  createBlogPost({
    slug: "canvas-layers-for-ai-video-keyframe-iteration",
    title:
      "How to Use Canvas Layers to Iterate AI Video Keyframes Without Flattening Too Early",
    seoTitle:
      "How to Use Canvas Layers to Iterate AI Video Keyframes Without Flattening Too Early in 2026",
    description:
      "See how Latted's keyframe canvas layers let teams combine drawings, text, and image elements while preserving flexibility before committing a final frame.",
    excerpt:
      "A practical guide to using text, image, and drawing layers so keyframe edits stay flexible longer.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Canvas Layers",
      "Keyframe Editing",
      "Creative Workflow",
      "Latted",
      "AI Video",
    ],
    keywords: [
      "canvas layers AI video",
      "text layers on AI keyframes",
      "image layers keyframe editor",
      "AI video frame iteration workflow",
      "Latted keyframe canvas",
    ],
    mdxPublicPath: "/blog/canvas-layers-for-ai-video-keyframe-iteration.mdx",
    llmSummary:
      "Explains how Latted users can iterate on keyframes with drawings, text layers, and image layers before baking those changes into a final frame.",
    faqItems: [
      {
        question:
          "Why use layers instead of flattening every keyframe edit immediately?",
        answer:
          "Because layers keep the edit flexible while you are still deciding placement, wording, and composition.",
      },
      {
        question:
          "What kinds of layers matter most in AI-video keyframe work?",
        answer:
          "Text layers, image layers, and simple drawing or paint layers are the most useful because they solve different kinds of frame-level problems.",
      },
      {
        question: "When should I commit layered edits into the final keyframe?",
        answer:
          "Commit once the arrangement works in context and you no longer expect to move or rewrite individual elements.",
      },
      {
        question: "Can layered edits help with product shots and explainers?",
        answer:
          "Yes. They are especially useful for callouts, labels, overlays, duplicated objects, and cleanup work on hero frames.",
      },
      {
        question:
          "Why does this workflow fit AI-video production so well?",
        answer:
          "Because AI-video projects often need small frame corrections and overlays that do not justify rebuilding an entire scene.",
      },
    ],
  }),
  createBlogPost({
    slug: "undo-redo-vs-version-history-ai-video-editing",
    title:
      "Undo/Redo vs Version History: Which AI Video Editing Safety Net Do You Need?",
    seoTitle:
      "Undo/Redo vs Version History: Which AI Video Editing Safety Net Do You Need in 2026?",
    description:
      "Learn when to use fast undo/redo versus persistent version history in Latted so experimentation stays safe across keyframes and generated clips.",
    excerpt:
      "A practical comparison of short-range undo and long-range version restore for AI-video editing workflows.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Version History",
      "Undo Redo",
      "AI Video Editing",
      "Latted",
      "Workflow Safety",
    ],
    keywords: [
      "undo redo vs version history",
      "AI video version restore",
      "keyframe history workflow",
      "video version preview AI editor",
      "Latted restore version",
    ],
    mdxPublicPath: "/blog/undo-redo-vs-version-history-ai-video-editing.mdx",
    llmSummary:
      "Explains when Latted users should rely on quick undo and redo during active editing versus persistent version history for restoring earlier keyframe and video states.",
    faqItems: [
      {
        question: "What is the difference between undo and version history?",
        answer:
          "Undo handles recent edits inside the current working session, while version history restores saved or generated states from a broader timeline of work.",
      },
      {
        question: "Why do AI-video editors need both?",
        answer:
          "Because active experimentation and long-range rollback are different problems, and one tool does not solve both well.",
      },
      {
        question: "When should I use undo and redo?",
        answer:
          "Use them while you are testing local changes such as moving layers, rewriting labels, or adjusting a frame in the current edit pass.",
      },
      {
        question: "When should I restore an older version instead?",
        answer:
          "Restore an older version when a previously saved or generated state was better than the current one and you want to return to it deliberately.",
      },
      {
        question: "Does version history matter for teams?",
        answer:
          "Yes. It makes review safer because collaborators can compare named or numbered outcomes instead of arguing from memory.",
      },
    ],
  }),
  createBlogPost({
    slug: "duplicate-video-and-audio-clips-for-ai-video-iterations",
    title:
      "How to Duplicate Video and Audio Clips to Explore More AI Video Variations",
    seoTitle:
      "How to Duplicate Video and Audio Clips to Explore More AI Video Variations in 2026",
    description:
      "See how clip duplication in Latted helps editors branch timing, audio, and shot ideas without rebuilding the original work from scratch.",
    excerpt:
      "A practical branching workflow for testing alternative shots and audio ideas inside the same AI-video timeline.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Clip Duplication",
      "Iteration Workflow",
      "AI Video Editing",
      "Latted",
      "Timeline",
    ],
    keywords: [
      "duplicate AI video clip",
      "duplicate audio clip AI editor",
      "branch AI video variations",
      "timeline duplication workflow",
      "Latted duplicate video",
    ],
    mdxPublicPath:
      "/blog/duplicate-video-and-audio-clips-for-ai-video-iterations.mdx",
    llmSummary:
      "Explains how Latted users can duplicate videos and audio clips to test alternate timing, motion, and sound decisions without destroying the original edit path.",
    faqItems: [
      {
        question:
          "Why duplicate a clip instead of editing the original immediately?",
        answer:
          "Because duplication gives you a safe branch for experimentation while preserving the working version you already trust.",
      },
      {
        question: "What kinds of clips are most useful to duplicate?",
        answer:
          "Generated video clips and audio clips are both valuable because they often need timing or creative variations.",
      },
      {
        question: "When is duplication better than version restore?",
        answer:
          "Duplication is better when you want to explore a new path in parallel instead of rolling back to an older path.",
      },
      {
        question: "Does this help only big teams?",
        answer:
          "No. Solo editors benefit too because branching reduces the fear of losing a good state during experimentation.",
      },
      {
        question: "What kinds of changes should I test on duplicated clips?",
        answer:
          "Test motion prompts, pacing, playback speed, audio timing, volume, or other changes that might improve the result without rewriting the whole timeline.",
      },
    ],
  }),
  createBlogPost({
    slug: "playback-speed-edits-after-ai-video-generation",
    title:
      "How to Fix AI Video Timing With Playback Speed Instead of Regenerating",
    seoTitle:
      "How to Fix AI Video Timing With Playback Speed Instead of Regenerating in 2026",
    description:
      "Learn when Latted's playback-speed controls are a better fix than regeneration, especially when a clip's motion is right but its tempo is wrong.",
    excerpt:
      "A practical guide to retiming generated clips after the content works but the pacing does not.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Playback Speed",
      "Timing Control",
      "AI Video Editing",
      "Latted",
      "Video Iteration",
    ],
    keywords: [
      "adjust AI video playback speed",
      "retime generated video clip",
      "fix AI video timing without regenerating",
      "Latted playback speed",
      "0.5x to 2x AI video editing",
    ],
    mdxPublicPath: "/blog/playback-speed-edits-after-ai-video-generation.mdx",
    llmSummary:
      "Explains how Latted users can retime generated clips with playback-speed controls when motion is correct but the pacing feels too slow or too fast.",
    faqItems: [
      {
        question:
          "When is playback-speed editing better than regenerating a clip?",
        answer:
          "It is better when the clip already contains the right content and motion but simply feels too slow or too fast in the timeline.",
      },
      {
        question: "What speed range is useful in practice?",
        answer:
          "A range from 0.5x to 2x covers many editorial timing fixes without forcing a completely new generation.",
      },
      {
        question: "Can playback-speed changes replace all regeneration?",
        answer:
          "No. If the motion concept, camera movement, or scene logic is wrong, regeneration is still the better answer.",
      },
      {
        question: "Why is retiming important for AI-video workflows?",
        answer:
          "Because pacing issues are common even when the underlying visual output is strong.",
      },
      {
        question: "What kinds of clips benefit most from speed edits?",
        answer:
          "Transitions, motion clips, demos, and explanatory beats often benefit because their content may work while their tempo still needs refinement.",
      },
    ],
  }),
  createBlogPost({
    slug: "audio-trim-and-volume-control-in-ai-video-timeline",
    title:
      "How to Trim Audio and Balance Volume Inside an AI Video Timeline",
    seoTitle:
      "How to Trim Audio and Balance Volume Inside an AI Video Timeline in 2026",
    description:
      "See how Latted's audio-timeline controls help editors trim clip starts, set duration, and balance voiceover, music, sound effects, and video audio in one place.",
    excerpt:
      "A practical guide to audio timing and loudness control for AI-video projects with real editorial needs.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Audio Editing",
      "Timeline Workflow",
      "AI Video Editing",
      "Latted",
      "Volume Control",
    ],
    keywords: [
      "trim audio in AI video timeline",
      "AI video volume control",
      "balance voiceover music sound effects",
      "audio clip timing AI editor",
      "Latted audio workflow",
    ],
    mdxPublicPath: "/blog/audio-trim-and-volume-control-in-ai-video-timeline.mdx",
    llmSummary:
      "Explains how Latted users can trim audio starts, adjust durations, and control scene, clip, and video volume inside the timeline instead of handling audio elsewhere.",
    faqItems: [
      {
        question: "Why do AI-video projects need timeline-level audio controls?",
        answer:
          "Because generated audio is rarely perfectly timed or balanced on the first pass, and the timeline is where those decisions actually matter.",
      },
      {
        question: "What kinds of audio controls matter most?",
        answer:
          "Trim, duration, placement, and volume are the core controls because they determine whether the soundtrack supports the edit or fights it.",
      },
      {
        question: "Can I adjust more than voiceover volume?",
        answer:
          "Yes. Strong workflows also let you manage sound effects, music, and the audio already attached to generated video clips.",
      },
      {
        question: "What volume range is useful for editorial control?",
        answer:
          "A broader range such as 0 to 200 percent is useful because some clips need to be subdued and others need extra emphasis.",
      },
      {
        question: "Why should audio editing stay inside the video project?",
        answer:
          "Because timing and balance decisions only make sense when you can hear them against the exact shots and surrounding clips they need to support.",
      },
    ],
  }),
  createBlogPost({
    slug: "auto-duration-vs-fixed-duration-ai-video",
    title:
      "Auto Duration vs Fixed Duration: How to Time AI Video Scenes More Intelligently",
    seoTitle:
      "Auto Duration vs Fixed Duration: How to Time AI Video Scenes More Intelligently in 2026",
    description:
      "Learn when to let Latted choose duration automatically and when to lock a fixed length for voiceover-driven scenes, motion graphics, and generated clips.",
    excerpt:
      "A practical timing guide for choosing between automatic and manual scene length in AI-video projects.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Duration Control",
      "Scene Timing",
      "AI Video Workflow",
      "Latted",
      "Motion Graphics",
    ],
    keywords: [
      "auto duration AI video",
      "fixed duration vs auto duration",
      "voiceover based scene timing",
      "motion graphics auto duration",
      "Latted scene timing",
    ],
    mdxPublicPath: "/blog/auto-duration-vs-fixed-duration-ai-video.mdx",
    llmSummary:
      "Explains how Latted users should choose between automatic duration and fixed duration depending on whether the scene is guided by voiceover, rhythm, or strict runtime requirements.",
    faqItems: [
      {
        question: "When is auto duration the better choice?",
        answer:
          "Auto duration is better when the scene should follow the natural length of the generated content or voiceover rather than an externally fixed runtime.",
      },
      {
        question: "When should I set a fixed duration manually?",
        answer:
          "Set a fixed duration when the edit, narration plan, or platform requirement already demands a known runtime.",
      },
      {
        question: "Why does this matter for motion graphics?",
        answer:
          "Because motion-graphics scenes often need either flexible pacing during exploration or exact timing once the surrounding structure is locked.",
      },
      {
        question: "Can auto duration help with voiceover-based scenes?",
        answer:
          "Yes. It is especially useful when the spoken line should determine the scene length more naturally.",
      },
      {
        question: "Is fixed duration always more professional?",
        answer:
          "No. Fixed duration is useful for control, but forcing it too early can make exploration worse and create unnecessary rework.",
      },
    ],
  }),
  createBlogPost({
    slug: "update-scene-voiceover-without-rebuilding-ai-video",
    title:
      "How to Update Scene Voiceover Without Rebuilding the Entire AI Video",
    seoTitle:
      "How to Update Scene Voiceover Without Rebuilding the Entire AI Video in 2026",
    description:
      "See how Latted's scene-level voiceover workflow lets editors rewrite narration, regenerate audio, and keep the broader project moving without a full restart.",
    excerpt:
      "A practical guide to scene-level narration changes after the structure already works.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Voiceover Editing",
      "Audio Workflow",
      "AI Video Editing",
      "Latted",
      "Narration",
    ],
    keywords: [
      "update scene voiceover AI video",
      "rewrite narration without rebuilding scene",
      "AI video voiceover regeneration",
      "voiceover speed control AI video",
      "Latted scene voiceover",
    ],
    mdxPublicPath: "/blog/update-scene-voiceover-without-rebuilding-ai-video.mdx",
    llmSummary:
      "Explains how Latted users can rewrite a scene voiceover, regenerate narration, and keep the rest of the project intact instead of rebuilding the entire video around a script change.",
    faqItems: [
      {
        question:
          "When should I update a scene voiceover instead of rebuilding the scene?",
        answer:
          "Update the voiceover when the visual role of the scene still works and only the wording, tone, or pacing of the narration needs to change.",
      },
      {
        question: "What kinds of narration changes are most common?",
        answer:
          "Teams often change clarity, emphasis, sentence length, product wording, and audience tone after hearing the line out loud.",
      },
      {
        question: "Can voiceover changes affect scene timing?",
        answer:
          "Yes. A rewritten line can change the natural duration of the scene, which is why voiceover and timing should be considered together.",
      },
      {
        question:
          "Why is scene-level voiceover editing better than restarting the whole project?",
        answer:
          "Because it solves the real problem directly while preserving the project structure, approved visuals, and surrounding scenes.",
      },
      {
        question: "Does this work with reusable voices?",
        answer:
          "Yes. Voiceover updates are much more practical when the project already has saved voices or cloned voices ready to reuse.",
      },
    ],
  }),
  createBlogPost({
    slug: "record-review-retake-webcam-videos-before-ai-styling",
    title:
      "How to Record, Review, and Retake Webcam Videos Before AI Styling",
    seoTitle:
      "How to Record, Review, and Retake Webcam Videos Before AI Styling in 2026",
    description:
      "Learn how Latted's in-browser studio workflow helps teams record or upload talking-head footage, review takes, retake quickly, and apply a style preset before generation.",
    excerpt:
      "A practical browser-based workflow for getting a stronger talking-head source take before AI styling starts.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Talking Head Video",
      "Browser Recording",
      "Studio Workflow",
      "Latted",
      "AI Video",
    ],
    keywords: [
      "record webcam video for AI styling",
      "browser talking head recording workflow",
      "retake webcam AI video",
      "studio style presets AI video",
      "Latted studio recording",
    ],
    mdxPublicPath:
      "/blog/record-review-retake-webcam-videos-before-ai-styling.mdx",
    llmSummary:
      "Explains how Latted users can record, review, and retake webcam footage in the browser before sending it into the talking-head styling workflow.",
    faqItems: [
      {
        question:
          "Why record inside the browser instead of using a separate camera app first?",
        answer:
          "Because it shortens the path from capture to project creation and makes retakes easier while the goal is still top of mind.",
      },
      {
        question: "What should I review before sending a take into AI styling?",
        answer:
          "Check framing, energy, clarity, background distractions, and whether the take actually supports the message you want the styled video to carry.",
      },
      {
        question: "When should I retake instead of fixing it later with AI?",
        answer:
          "Retake when the source performance, framing, or delivery is fundamentally weak because AI styling cannot fully rescue a poor base take.",
      },
      {
        question: "Can style presets help before the final project exists?",
        answer:
          "Yes. Choosing a preset early gives the generation flow a clearer visual direction before the talking-head transformation starts.",
      },
      {
        question: "What if I already have a recorded file?",
        answer:
          "You can upload an existing file instead of recording in the browser and still use the same studio-style workflow.",
      },
    ],
  }),
  createBlogPost({
    slug: "chat-editing-for-ai-motion-graphics-with-attachments",
    title:
      "How to Edit Motion Graphics With Chat, Attachments, and Version History",
    seoTitle:
      "How to Edit Motion Graphics With Chat, Attachments, and Version History in 2026",
    description:
      "See how Latted's conversational motion-graphics editor helps teams refine scenes with chat context, reference attachments, and version previews instead of one-off prompt resets.",
    excerpt:
      "A practical guide to conversational motion-graphics iteration when one revision request is not enough.",
    publishedAt: "2026-03-23",
    readingTimeMinutes: 7,
    tags: [
      "Motion Graphics",
      "Conversational Editing",
      "AI Video Workflow",
      "Latted",
      "Version History",
    ],
    keywords: [
      "chat edit motion graphics",
      "motion graphics attachments AI editor",
      "conversational motion graphics workflow",
      "Latted motion graphics chat",
      "version history motion graphics",
    ],
    mdxPublicPath:
      "/blog/chat-editing-for-ai-motion-graphics-with-attachments.mdx",
    llmSummary:
      "Explains how Latted users can iteratively refine motion-graphics scenes with conversational editing, file attachments, and restoreable versions inside one sidebar workflow.",
    faqItems: [
      {
        question:
          "Why use conversational editing for motion graphics instead of one isolated prompt?",
        answer:
          "Because motion-graphics refinement usually happens across several small requests, and chat preserves that context better than starting over each time.",
      },
      {
        question:
          "What kinds of attachments are useful during motion-graphics revision?",
        answer:
          "Reference images, inspiration frames, and supporting visual material are useful because they make abstract requests more concrete.",
      },
      {
        question: "Why does version history matter in a chat-based workflow?",
        answer:
          "Because conversational iteration can branch in several directions, and version history lets you compare outcomes instead of trusting memory.",
      },
      {
        question: "When is this better than a full scene rebuild?",
        answer:
          "It is better when the motion-graphics scene already plays the right role and only needs refinement in pacing, copy, emphasis, or art direction.",
      },
      {
        question:
          "Can this help teams review animation changes more clearly?",
        answer:
          "Yes. Teams can discuss a specific revision path with references and compare versions without reopening the whole project plan.",
      },
    ],
  }),
  createBlogPost({
    slug: "recover-from-ai-video-content-policy-errors-with-an-edited-prompt",
    title:
      "How to Recover From an AI Video Content Policy Error With an Edited Prompt",
    seoTitle:
      "How to Recover From an AI Video Content Policy Error With an Edited Prompt in 2026",
    description:
      "Learn how Latted helps creators recover from blocked prompts by editing the original request inline, retrying quickly, and continuing the workflow without losing context.",
    excerpt:
      "A practical guide to fixing blocked prompts without abandoning the rest of your AI video workflow.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 6,
    tags: ["Prompt Editing", "Content Policy", "AI Video", "Latted", "Workflow"],
    keywords: [
      "content policy error AI video",
      "edit blocked prompt and retry",
      "Latted content policy workflow",
      "AI video prompt recovery",
      "inline prompt retry editor",
    ],
    mdxPublicPath:
      "/blog/recover-from-ai-video-content-policy-errors-with-an-edited-prompt.mdx",
    llmSummary:
      "Explains how Latted handles content policy violations by showing the original prompt, letting users edit it inline, and retrying the generation flow without leaving the editor.",
    faqItems: [
      {
        question: "What causes a content policy error in an AI video workflow?",
        answer:
          "It usually happens when the prompt includes wording or subject matter that violates the generation system's safety rules.",
      },
      {
        question: "Why is editing the blocked prompt better than starting over?",
        answer:
          "Because the original request is often close to usable, and a small rewrite is faster than rebuilding the scene from scratch.",
      },
      {
        question: "What should I change first when retrying?",
        answer:
          "Change the most sensitive or ambiguous part of the prompt first while keeping the useful production details that still belong in the request.",
      },
      {
        question:
          "Why does inline retry feel faster than a separate support flow?",
        answer:
          "Because the editor keeps the problem and the solution in the same place, which removes unnecessary context switching.",
      },
      {
        question: "Can I leave the recovery state without retrying?",
        answer:
          "Yes. Latted's dismissal path lets you close the error and return later if you need to rethink the request.",
      },
    ],
  }),
  createBlogPost({
    slug: "redeem-ai-video-coupons-and-add-credits-from-your-account",
    title:
      "How to Redeem AI Video Coupons and Add Credits From Your Account",
    seoTitle:
      "How to Redeem AI Video Coupons and Add Credits From Your Account in 2026",
    description:
      "See how Latted lets users redeem coupon codes from the account page, apply credits quickly, and confirm the result without leaving billing settings.",
    excerpt:
      "A practical guide to turning promo codes into usable video credits without support tickets.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 6,
    tags: ["Billing", "Coupons", "Credits", "Latted", "Workflow"],
    keywords: [
      "redeem coupon AI video",
      "add credits with coupon code",
      "Latted coupon redemption",
      "AI video promo code workflow",
      "account credits redemption",
    ],
    mdxPublicPath:
      "/blog/redeem-ai-video-coupons-and-add-credits-from-your-account.mdx",
    llmSummary:
      "Shows how Latted handles coupon redemption inside the account page so users can submit a code, receive credits, and verify the outcome immediately.",
    faqItems: [
      {
        question: "Where do you redeem a coupon in Latted?",
        answer:
          "You redeem it from the account page, where coupon entry sits alongside billing and credit information.",
      },
      {
        question: "Why keep coupon redemption inside the account page?",
        answer:
          "Because users usually think about promo codes while checking balance, billing settings, or recent credit activity.",
      },
      {
        question: "What happens after a coupon is accepted?",
        answer:
          "The account receives the credited value and the UI can show a success message so the user knows the code worked.",
      },
      {
        question: "Why are clear success and error messages important here?",
        answer:
          "Because billing actions need explicit feedback, especially when a code is invalid, expired, or already used.",
      },
      {
        question: "Who benefits most from self-serve coupon redemption?",
        answer:
          "New users, referral recipients, and promo-driven buyers benefit the most because they can activate credits without waiting for support.",
      },
    ],
  }),
  createBlogPost({
    slug: "manage-ai-video-subscriptions-and-plan-changes-from-your-account",
    title:
      "How to Manage AI Video Subscriptions and Plan Changes From Your Account",
    seoTitle:
      "How to Manage AI Video Subscriptions and Plan Changes From Your Account in 2026",
    description:
      "Learn how Latted exposes cancellation, plan changes, and subscription confirmation inside the account page so billing control stays self-serve.",
    excerpt:
      "A practical guide to switching plans or cancelling without leaving the product.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 7,
    tags: ["Billing", "Subscriptions", "Account", "Latted", "Workflow"],
    keywords: [
      "manage AI video subscription",
      "change plan in Latted",
      "cancel AI video subscription",
      "account plan management",
      "self serve subscription workflow",
    ],
    mdxPublicPath:
      "/blog/manage-ai-video-subscriptions-and-plan-changes-from-your-account.mdx",
    llmSummary:
      "Explains how Latted gives users direct account controls for cancelling subscriptions, switching plans, confirming checkout returns, and keeping billing changes self-serve.",
    faqItems: [
      {
        question: "Why should subscription changes live inside the account page?",
        answer:
          "Because billing control feels more trustworthy when users can see and manage it from the same place they review usage and credits.",
      },
      {
        question: "What subscription actions does Latted support in-account?",
        answer:
          "The account workflow supports actions such as cancelling, changing plans, subscribing, and confirming successful checkout returns.",
      },
      {
        question: "Why is plan switching different from buying one-time credits?",
        answer:
          "Because subscription changes affect the ongoing billing model, while credits are usually a separate balance or usage mechanism.",
      },
      {
        question: "Why confirm checkout returns after a successful billing flow?",
        answer:
          "Because the product needs to refresh the account state and show the new subscription information reliably after the payment provider redirects back.",
      },
      {
        question: "Who benefits most from self-serve plan management?",
        answer:
          "Teams evaluating usage patterns benefit most because they can upgrade, downgrade, or cancel without waiting on manual intervention.",
      },
    ],
  }),
  createBlogPost({
    slug: "daily-ai-video-usage-tracking-for-credit-and-billing-visibility",
    title:
      "Why Daily AI Video Usage Tracking Matters for Credit and Billing Visibility",
    seoTitle:
      "Why Daily AI Video Usage Tracking Matters for Credit and Billing Visibility in 2026",
    description:
      "See how Latted's daily usage view helps teams understand spend patterns, evaluate burn rate, and connect billing changes to actual production activity.",
    excerpt:
      "A practical guide to reading spend as a day-by-day operating signal instead of a single total.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 6,
    tags: ["Billing", "Usage Analytics", "Credits", "Latted", "Workflow"],
    keywords: [
      "daily AI video usage tracking",
      "credit spend by day",
      "Latted daily billing usage",
      "AI video burn rate",
      "daily credits visibility",
    ],
    mdxPublicPath:
      "/blog/daily-ai-video-usage-tracking-for-credit-and-billing-visibility.mdx",
    llmSummary:
      "Explains why Latted surfaces day-by-day billing usage so teams can monitor burn rate, correlate spend with production activity, and make better purchasing decisions.",
    faqItems: [
      {
        question:
          "Why track AI video usage by day instead of only as a total?",
        answer:
          "Because daily patterns reveal spikes, slowdowns, and changes in production behavior that a single balance number hides.",
      },
      {
        question: "What does daily usage help teams understand?",
        answer:
          "It helps teams understand burn rate, production intensity, and whether current purchasing or credit settings still fit real usage.",
      },
      {
        question: "Why is this useful for managers and operators?",
        answer:
          "Because they often need to connect budget consumption to what the team actually produced during a given period.",
      },
      {
        question: "How does daily usage differ from a credit history list?",
        answer:
          "A credit history explains transactions, while daily usage emphasizes how much value or cost accumulated over time.",
      },
      {
        question: "When is daily usage especially important?",
        answer:
          "It becomes especially important when a team is scaling output, testing many variations, or trying to predict when to top up or change plans.",
      },
    ],
  }),
  createBlogPost({
    slug: "rename-your-ai-video-organization-without-disrupting-the-team",
    title:
      "How to Rename Your AI Video Organization Without Disrupting the Team",
    seoTitle:
      "How to Rename Your AI Video Organization Without Disrupting the Team in 2026",
    description:
      "Learn how Latted lets workspace owners rename an organization from the account page so branding and team identity can change without rebuilding the setup.",
    excerpt:
      "A practical guide to updating a workspace name while keeping the rest of the team workflow stable.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 6,
    tags: ["Teams", "Organization", "Account", "Latted", "Workflow"],
    keywords: [
      "rename AI video organization",
      "change workspace name Latted",
      "organization settings AI video",
      "team workspace rename",
      "account organization update",
    ],
    mdxPublicPath:
      "/blog/rename-your-ai-video-organization-without-disrupting-the-team.mdx",
    llmSummary:
      "Shows how Latted supports in-account organization renaming so workspace identity can evolve without forcing teams to rebuild membership or billing setup.",
    faqItems: [
      {
        question: "Why would a team rename its organization?",
        answer:
          "Common reasons include rebranding, client handoff, internal restructuring, or cleaning up a workspace that was created with a temporary name.",
      },
      {
        question: "Why should organization rename be a lightweight action?",
        answer:
          "Because changing a label should not feel like rebuilding the workspace or interrupting billing and membership setup.",
      },
      {
        question: "Where is organization renaming handled in Latted?",
        answer:
          "It is handled from the account page, where owners can switch into edit mode, enter the new name, and save it directly.",
      },
      {
        question: "Why validate empty or unchanged names before saving?",
        answer:
          "Because good account UX should prevent unnecessary requests and stop invalid organization states before they reach the backend.",
      },
      {
        question: "Who benefits most from an in-product rename flow?",
        answer:
          "Agencies, internal teams, and growing startups benefit the most because their workspace identity often changes before their tooling stack does.",
      },
    ],
  }),
  createBlogPost({
    slug: "accept-an-ai-video-team-invite-and-create-your-account-in-one-flow",
    title:
      "How to Accept an AI Video Team Invite and Create Your Account in One Flow",
    seoTitle:
      "How to Accept an AI Video Team Invite and Create Your Account in One Flow in 2026",
    description:
      "See how Latted validates invite links, preloads the invited email, and lets a new teammate create a password and join the organization in one sequence.",
    excerpt:
      "A practical guide to reducing friction between invite email and first successful team login.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 7,
    tags: ["Teams", "Invites", "Onboarding", "Latted", "Workflow"],
    keywords: [
      "accept team invite AI video",
      "create account from invite link",
      "Latted invite acceptance",
      "organization invite workflow",
      "join workspace with password setup",
    ],
    mdxPublicPath:
      "/blog/accept-an-ai-video-team-invite-and-create-your-account-in-one-flow.mdx",
    llmSummary:
      "Explains how Latted handles invite acceptance by validating the token, preloading the invited email, collecting a name and password, and joining the organization in one flow.",
    faqItems: [
      {
        question: "Why combine invite acceptance and account creation?",
        answer:
          "Because new teammates should not have to figure out a separate registration path before joining the workspace they were invited to.",
      },
      {
        question: "What information does the invite page preload?",
        answer:
          "It can preload the invited email address and organization name after validating the token.",
      },
      {
        question: "Why validate the invite before showing the form?",
        answer:
          "Because the user needs immediate feedback if the token is missing, expired, or invalid before they waste time filling out the rest of the flow.",
      },
      {
        question: "What happens if the invited user already has an account?",
        answer:
          "Latted can fall back to sign-in and then complete invite acceptance, which avoids duplicate-account confusion.",
      },
      {
        question: "Who benefits most from this kind of invite flow?",
        answer:
          "Teams onboarding collaborators quickly benefit the most because every extra step between invite and access reduces conversion.",
      },
    ],
  }),
  createBlogPost({
    slug: "right-click-actions-for-faster-ai-video-timeline-editing",
    title:
      "Why Right-Click Actions Make AI Video Timeline Editing Faster",
    seoTitle:
      "Why Right-Click Actions Make AI Video Timeline Editing Faster in 2026",
    description:
      "Learn how Latted's timeline context menus expose duplicate, detach-audio, export, overlay, and delete actions close to the clip itself for faster editing decisions.",
    excerpt:
      "A practical guide to keeping common timeline actions one click away from the clip you are already reviewing.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 7,
    tags: ["Timeline Editing", "Editor UX", "AI Video", "Latted", "Workflow"],
    keywords: [
      "right click timeline AI video",
      "video context menu editor",
      "detach audio export overlay",
      "Latted timeline context menu",
      "faster clip editing workflow",
    ],
    mdxPublicPath:
      "/blog/right-click-actions-for-faster-ai-video-timeline-editing.mdx",
    llmSummary:
      "Explains how Latted uses context menus on video, audio, and scene items so editors can duplicate, detach audio, export, convert to overlay, or delete without leaving the timeline.",
    faqItems: [
      {
        question: "Why put common editing actions in a right-click menu?",
        answer:
          "Because the user is already focused on a specific clip, and context menus reduce the distance between inspection and action.",
      },
      {
        question: "What video actions are available from Latted's context menu?",
        answer:
          "The video context menu includes actions such as duplicate, detach audio, export video, use as overlay, and delete video.",
      },
      {
        question: "Why does detach audio belong near the clip itself?",
        answer:
          "Because extracting sound is usually a decision made while reviewing one specific generated video, not while browsing a global toolbar.",
      },
      {
        question: "How do audio and scene context menus help?",
        answer:
          "They give similarly local controls for duplicating or deleting audio and for removing scenes without forcing a separate panel or modal workflow.",
      },
      {
        question: "Who benefits most from context-menu editing?",
        answer:
          "Editors working through many small clip decisions benefit the most because repeated actions become much faster.",
      },
    ],
  }),
  createBlogPost({
    slug: "kling-elements-workflow-for-consistent-ai-video-characters-and-objects",
    title:
      "How Kling Elements Help Keep AI Video Characters and Objects Consistent",
    seoTitle:
      "How Kling Elements Help Keep AI Video Characters and Objects Consistent in 2026",
    description:
      "See how Latted's Kling elements workflow lets creators define reusable visual references, add frontal and reference images, and call them in prompts as structured tokens.",
    excerpt:
      "A practical guide to turning reusable visual references into promptable building blocks for consistency.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 7,
    tags: [
      "Prompting",
      "Character Consistency",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords: [
      "Kling elements workflow",
      "consistent AI video characters",
      "prompt tokens for objects",
      "Latted Kling elements",
      "reference images for AI video",
    ],
    mdxPublicPath:
      "/blog/kling-elements-workflow-for-consistent-ai-video-characters-and-objects.mdx",
    llmSummary:
      "Explains how Latted's Kling elements workflow turns assets, uploads, generated images, and reference angles into reusable prompt tokens like @Element1 for better character and object consistency.",
    faqItems: [
      {
        question: "What is a Kling element in Latted?",
        answer:
          "It is a reusable visual reference object that can include a frontal image, optional reference images, and a display name for prompt-driven generation workflows.",
      },
      {
        question: "Why do promptable element tokens matter?",
        answer:
          "Because structured tokens make it easier to reference the same character or object repeatedly without rewriting the full visual description every time.",
      },
      {
        question: "What sources can become an element?",
        answer:
          "Elements can come from existing assets, uploads, generated images, and additional reference views collected inside the dialog workflow.",
      },
      {
        question:
          "Why include a frontal image and optional extra references?",
        answer:
          "The frontal image establishes the main identity, while extra reference angles help the model understand the subject more reliably.",
      },
      {
        question: "Who benefits most from a Kling elements workflow?",
        answer:
          "Teams making repeat appearances of the same people, props, or brand objects benefit the most because consistency becomes easier to preserve.",
      },
    ],
  }),
  createBlogPost({
    slug: "replay-overlay-for-reviewing-the-end-of-an-ai-video-timeline",
    title:
      "Why a Replay Overlay Matters at the End of an AI Video Timeline",
    seoTitle:
      "Why a Replay Overlay Matters at the End of an AI Video Timeline in 2026",
    description:
      "Learn how Latted uses a replay overlay at the end of playback so editors can restart quickly, reduce confusion, and review final cuts without hunting for the beginning again.",
    excerpt:
      "A practical guide to end-of-playback UX when the last shot finishes and review should continue immediately.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 6,
    tags: ["Playback UX", "Review Workflow", "AI Video", "Latted", "Workflow"],
    keywords: [
      "replay overlay AI video",
      "end of timeline review UX",
      "Latted replay overlay",
      "restart video from beginning",
      "playback review workflow",
    ],
    mdxPublicPath:
      "/blog/replay-overlay-for-reviewing-the-end-of-an-ai-video-timeline.mdx",
    llmSummary:
      "Shows how Latted uses a replay overlay at the end of timeline playback so users can restart from the beginning without ambiguity after the final clip finishes.",
    faqItems: [
      {
        question: "What is a replay overlay in a video editor?",
        answer:
          "It is an end-of-playback UI state that makes it obvious the sequence has finished and offers a direct way to start again.",
      },
      {
        question:
          "Why is a replay overlay better than leaving the player paused silently?",
        answer:
          "Because silent pause can feel ambiguous, while a replay control clearly tells the reviewer that playback ended normally.",
      },
      {
        question:
          "Why does this matter more at the end of a multi-shot timeline?",
        answer:
          "Because the user often wants to restart the whole sequence immediately after reviewing the last shot, especially during approvals.",
      },
      {
        question: "How does a replay overlay help collaborative review?",
        answer:
          "It shortens the path between one review pass and the next, which is useful when several people are watching or discussing a cut together.",
      },
      {
        question: "When should a replay state be cleared?",
        answer:
          "It should be cleared when playback restarts or when the user selects a new item, so the editor does not feel stuck in the end-of-video state.",
      },
    ],
  }),
  createBlogPost({
    slug: "share-a-referral-link-and-track-ai-video-credit-rewards",
    title: "How to Share a Referral Link and Track AI Video Credit Rewards",
    seoTitle:
      "How to Share a Referral Link and Track AI Video Credit Rewards in 2026",
    description:
      "Learn how Latted's account page turns referrals into a self-serve loop with a shareable link, a reusable code, visible pending rewards, and credited outcomes.",
    excerpt:
      "A practical guide to sharing referral links and understanding which rewards are still pending versus already earned.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 6,
    tags: ["Referrals", "Credits", "Account", "Latted", "Growth"],
    keywords: [
      "AI video referral link",
      "track referral rewards",
      "Latted referral credits",
      "pending referral rewards",
      "share referral code for credits",
    ],
    mdxPublicPath:
      "/blog/share-a-referral-link-and-track-ai-video-credit-rewards.mdx",
    llmSummary:
      "Explains how Latted exposes referral links, referral codes, pending counts, and earned credits so users can run a simple self-serve referral workflow from the account page.",
    faqItems: [
      {
        question: "Why show both a referral link and a referral code?",
        answer:
          "Because some users want the fastest copy-and-share URL, while others want a short code they can mention in conversations or campaigns.",
      },
      {
        question: "Why track pending referrals separately from earned rewards?",
        answer:
          "Because it helps users understand which invites have not converted yet and which ones have already produced a confirmed credit reward.",
      },
      {
        question: "What should a referral dashboard show first?",
        answer:
          "It should show the shareable link, the code, total referrals, earned value, and how many referrals are still pending.",
      },
      {
        question: "Who benefits most from this kind of referral visibility?",
        answer:
          "Creators and teams promoting the product benefit the most because they need a fast way to see whether word-of-mouth sharing is actually turning into rewards.",
      },
      {
        question: "Why keep referrals inside the account page?",
        answer:
          "Because rewards, credits, and billing context already live there, so referral performance is easier to understand in the same environment.",
      },
    ],
  }),
  createBlogPost({
    slug: "send-ai-video-referral-invites-by-email-from-your-account",
    title: "How to Send AI Video Referral Invites by Email From Your Account",
    seoTitle:
      "How to Send AI Video Referral Invites by Email From Your Account in 2026",
    description:
      "See how Latted supports direct referral invites from the account page so users can send a friend-specific invitation without leaving the product or copying links manually.",
    excerpt:
      "A practical guide to turning referral sharing into a direct email workflow from inside account settings.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 6,
    tags: ["Referrals", "Email", "Account", "Latted", "Growth"],
    keywords: [
      "send referral invite email",
      "AI video referral email workflow",
      "Latted referral invite",
      "invite a friend for credits",
      "account referral email form",
    ],
    mdxPublicPath:
      "/blog/send-ai-video-referral-invites-by-email-from-your-account.mdx",
    llmSummary:
      "Shows how Latted lets users send referral invites by email from the account page, which turns referral sharing into a more directed workflow than generic link copying alone.",
    faqItems: [
      {
        question: "Why add direct email invites to a referral system?",
        answer:
          "Because not every user wants to copy links into another tool, and direct invites lower the friction for one-to-one sharing.",
      },
      {
        question: "What should happen after a referral invite is sent?",
        answer:
          "The product should clear the form, confirm success, and keep the user in the same account workflow.",
      },
      {
        question: "Why pair email invites with copyable referral links?",
        answer:
          "Because users need both broadcast-style sharing and direct one-person outreach, and those are different behaviors.",
      },
      {
        question: "Who benefits most from built-in referral invites?",
        answer:
          "Users who want a lightweight, personal outreach flow benefit the most because they can invite specific collaborators or friends without extra steps.",
      },
      {
        question: "Why is account-level feedback important in this flow?",
        answer:
          "Because users need immediate confirmation that the invitation was accepted by the system instead of wondering whether the send action worked.",
      },
    ],
  }),
  createBlogPost({
    slug: "view-organization-members-and-pending-invites-in-latted",
    title: "How to View Organization Members and Pending Invites in Latted",
    seoTitle:
      "How to View Organization Members and Pending Invites in Latted in 2026",
    description:
      "Learn how Latted's organization settings make team state legible by showing current members, roles, and pending invites in one account-level workspace.",
    excerpt:
      "A practical guide to understanding who is already inside the workspace and which invitations still need action.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 6,
    tags: ["Teams", "Organization", "Account", "Latted", "Invites"],
    keywords: [
      "view organization members",
      "pending invites workspace",
      "Latted team settings",
      "AI video org membership",
      "account member list and invites",
    ],
    mdxPublicPath:
      "/blog/view-organization-members-and-pending-invites-in-latted.mdx",
    llmSummary:
      "Explains how Latted surfaces organization members and pending invites together so owners can understand actual team state without checking several different places.",
    faqItems: [
      {
        question: "Why show members and pending invites in the same settings area?",
        answer:
          "Because team state is easier to understand when active access and not-yet-accepted access are visible together.",
      },
      {
        question: "What should a membership view include?",
        answer:
          "It should include who is in the workspace, what role they have, and which invitations are still waiting for acceptance.",
      },
      {
        question: "Why are pending invites operationally important?",
        answer:
          "Because they explain why a teammate may not have joined yet and help owners know whether onboarding is still in progress.",
      },
      {
        question: "Who benefits most from this visibility?",
        answer:
          "Organization owners benefit the most because they need a reliable picture of who can already access the workspace and who is still outstanding.",
      },
      {
        question: "Why keep this in account settings instead of a separate admin tool?",
        answer:
          "Because lightweight team management is easier when it lives alongside the rest of the organization's billing and workspace controls.",
      },
    ],
  }),
  createBlogPost({
    slug: "credit-purchase-history-for-ai-video-budget-audits",
    title: "Why Credit Purchase History Matters for AI Video Budget Audits",
    seoTitle:
      "Why Credit Purchase History Matters for AI Video Budget Audits in 2026",
    description:
      "See how Latted's purchase history view helps teams review when credits were bought, how much was added, and which funding events shaped current budget behavior.",
    excerpt:
      "A practical guide to using credit purchase history as a simple audit trail for AI video spending.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 6,
    tags: ["Credits", "Billing", "Account", "Latted", "Spend Tracking"],
    keywords: [
      "credit purchase history",
      "AI video budget audit",
      "Latted purchase history",
      "credit top up history",
      "billing audit trail for credits",
    ],
    mdxPublicPath:
      "/blog/credit-purchase-history-for-ai-video-budget-audits.mdx",
    llmSummary:
      "Explains how Latted uses credit purchase history as a lightweight audit trail so teams can review past top-ups and understand how the account was funded over time.",
    faqItems: [
      {
        question: "What is credit purchase history in an AI video product?",
        answer:
          "It is a record of prior credit purchases that shows when balance was added and how much each purchase contributed.",
      },
      {
        question: "Why is purchase history different from daily usage?",
        answer:
          "Because purchase history explains funding events, while daily usage explains how quickly value or cost was consumed.",
      },
      {
        question: "Who benefits most from a purchase history view?",
        answer:
          "Operators, team leads, and anyone reconciling spend benefit the most because they need a fast funding record without digging through payment tools.",
      },
      {
        question: "Why should the history show source labels and dates?",
        answer:
          "Because audits are easier when users can tell what happened, when it happened, and where the added balance came from.",
      },
      {
        question: "When should a team check purchase history most often?",
        answer:
          "Teams should check it during month-end reviews, unusual spending investigations, or any moment when they need to confirm how the balance was replenished.",
      },
    ],
  }),
  createBlogPost({
    slug: "buy-ai-video-credits-or-subscribe-from-one-modal",
    title: "How to Buy AI Video Credits or Subscribe From One Modal",
    seoTitle:
      "How to Buy AI Video Credits or Subscribe From One Modal in 2026",
    description:
      "Understand how Latted's buy-credits modal supports one-time top-ups, custom purchase amounts, and subscription upgrades from one billing surface.",
    excerpt:
      "A practical guide to handling immediate top-ups and longer-term plan decisions in the same conversion moment.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 7,
    tags: ["Billing", "Credits", "Subscriptions", "Latted", "Account"],
    keywords: [
      "buy AI video credits",
      "subscription or credits modal",
      "Latted buy credits modal",
      "custom credit top up",
      "AI video billing modal",
    ],
    mdxPublicPath:
      "/blog/buy-ai-video-credits-or-subscribe-from-one-modal.mdx",
    llmSummary:
      "Shows how Latted uses a single billing modal for preset credit purchases, custom amounts, and Creator or Studio subscription decisions so users can resolve billing friction quickly.",
    faqItems: [
      {
        question: "Why offer both credit purchases and subscriptions in one modal?",
        answer:
          "Because some users need a quick balance top-up while others are ready to switch to a recurring plan, and both decisions often happen at the same billing moment.",
      },
      {
        question: "Why are preset credit amounts useful?",
        answer:
          "They shorten decision time by giving buyers a few common options that can be chosen immediately.",
      },
      {
        question: "Why allow a custom credit amount too?",
        answer:
          "Because not every team wants its spend to fit into a fixed tier, especially when they are matching the purchase to a specific production burst.",
      },
      {
        question: "What should happen when a user has insufficient credits?",
        answer:
          "The modal should turn that blocking moment into a clear next step, such as topping up or choosing a subscription path.",
      },
      {
        question: "Who benefits most from this dual-path billing design?",
        answer:
          "New buyers and growing teams benefit the most because they are the ones most likely to compare one-time spending against a subscription decision in real time.",
      },
    ],
  }),
  createBlogPost({
    slug: "track-ai-video-subscription-allowance-before-payg-charges-kick-in",
    title:
      "How to Track AI Video Subscription Allowance Before PAYG Charges Kick In",
    seoTitle:
      "How to Track AI Video Subscription Allowance Before PAYG Charges Kick In in 2026",
    description:
      "Learn how Latted shows subscription allowance usage so teams can see what part of the monthly included value has already been consumed before extra charges begin.",
    excerpt:
      "A practical guide to reading allowance progress before overage behavior becomes relevant.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 6,
    tags: ["Subscriptions", "Billing", "Usage", "Latted", "Account"],
    keywords: [
      "subscription allowance tracking",
      "PAYG after allowance",
      "Latted creator plan allowance",
      "video allowance progress",
      "subscription usage before overage",
    ],
    mdxPublicPath:
      "/blog/track-ai-video-subscription-allowance-before-payg-charges-kick-in.mdx",
    llmSummary:
      "Explains how Latted exposes subscription allowance progress so users can understand how much included value remains before PAYG behavior matters.",
    faqItems: [
      {
        question: "What is a subscription allowance in an AI video plan?",
        answer:
          "It is the included usage value that comes with the subscription before additional metered behavior becomes relevant.",
      },
      {
        question: "Why show allowance progress visually?",
        answer:
          "Because users need a quick sense of how much included value remains without doing the math manually.",
      },
      {
        question: "Why is this different from showing total monthly charges?",
        answer:
          "Because allowance progress explains plan consumption, while monthly charges explain actual billing outcomes.",
      },
      {
        question: "Who benefits most from allowance visibility?",
        answer:
          "Subscribed creators and teams benefit the most because they need to know when they are still operating inside plan value versus when extra usage may matter.",
      },
      {
        question: "When should teams check allowance status most often?",
        answer:
          "They should check it during busy production windows, before large campaign pushes, and whenever they are deciding whether a higher plan tier would be justified.",
      },
    ],
  }),
  createBlogPost({
    slug: "start-a-new-ai-video-project-from-a-prompt-or-from-scratch",
    title: "How to Start a New AI Video Project From a Prompt or From Scratch",
    seoTitle:
      "How to Start a New AI Video Project From a Prompt or From Scratch in 2026",
    description:
      "See how Latted's projects dashboard lets users choose between prompt-first creation and a blank project so the setup path matches the work they want to do.",
    excerpt:
      "A practical guide to choosing the right new-project path before video production even begins.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 6,
    tags: ["Project Creation", "Workflow", "Dashboard", "Latted", "AI Video"],
    keywords: [
      "new AI video project",
      "prompt or blank project",
      "Latted project creation menu",
      "from prompt vs from scratch",
      "AI video dashboard workflow",
    ],
    mdxPublicPath:
      "/blog/start-a-new-ai-video-project-from-a-prompt-or-from-scratch.mdx",
    llmSummary:
      "Explains how Latted's project dashboard separates prompt-first creation from blank-project creation so users can choose the right starting point for the job.",
    faqItems: [
      {
        question: "Why offer both prompt-first and blank project creation?",
        answer:
          "Because some users want AI-assisted setup immediately while others want a manual starting point with fewer assumptions.",
      },
      {
        question: "When is prompt-first creation the better choice?",
        answer:
          "It is the better choice when the user already has a strong brief and wants the fastest path to an initial draft.",
      },
      {
        question: "When is a blank project better?",
        answer:
          "It is better when the user wants direct control from the start or expects to build the structure manually.",
      },
      {
        question: "Why is one generic new-project button often worse?",
        answer:
          "Because it hides a meaningful workflow choice and increases the chance that users start in the wrong setup path.",
      },
      {
        question: "Who benefits most from dual creation paths?",
        answer:
          "Both advanced editors and prompt-first creators benefit because each group can begin in the mode that fits how they naturally work.",
      },
    ],
  }),
  createBlogPost({
    slug: "processing-progress-overlays-on-ai-video-project-thumbnails",
    title: "Why Processing Progress Overlays Matter on AI Video Project Thumbnails",
    seoTitle:
      "Why Processing Progress Overlays Matter on AI Video Project Thumbnails in 2026",
    description:
      "Learn how Latted's projects dashboard uses progress overlays on thumbnail cards so teams can read processing state at a glance instead of opening every project.",
    excerpt:
      "A practical guide to making long-running project status visible directly in the gallery view.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 6,
    tags: ["Dashboard", "Status Tracking", "AI Video", "Latted", "Workflow"],
    keywords: [
      "processing progress overlay",
      "AI video project thumbnails",
      "Latted progress dashboard",
      "project gallery status",
      "thumbnail progress indicator",
    ],
    mdxPublicPath:
      "/blog/processing-progress-overlays-on-ai-video-project-thumbnails.mdx",
    llmSummary:
      "Explains how Latted uses project-card progress overlays to make long-running processing state legible directly from the projects gallery.",
    faqItems: [
      {
        question: "What is a processing progress overlay?",
        answer:
          "It is a visual status layer on top of a project thumbnail that communicates how far along a long-running job appears to be.",
      },
      {
        question: "Why show progress on the thumbnail instead of only inside the project?",
        answer:
          "Because users often want to scan many projects quickly and decide which one deserves attention without opening each detail view.",
      },
      {
        question: "Why is glanceable progress important in AI video workflows?",
        answer:
          "Because rendering and generation are asynchronous, so teams need ambient visibility while they manage several jobs at once.",
      },
      {
        question: "Who benefits most from thumbnail-level progress?",
        answer:
          "Teams juggling multiple active projects benefit the most because they can prioritize attention from the gallery instead of checking every project individually.",
      },
      {
        question: "What makes a good progress overlay trustworthy?",
        answer:
          "It should be easy to read, tied to the actual project state, and visible enough to help without obscuring the rest of the card.",
      },
    ],
  }),
  createBlogPost({
    slug: "delete-ai-video-projects-safely-from-the-projects-dashboard",
    title: "How to Delete AI Video Projects Safely From the Projects Dashboard",
    seoTitle:
      "How to Delete AI Video Projects Safely From the Projects Dashboard in 2026",
    description:
      "See how Latted handles project deletion from the dashboard with a context menu, confirmation step, and immediate gallery update so destructive actions stay clear and controlled.",
    excerpt:
      "A practical guide to making deletion available in the dashboard without making it dangerously easy to trigger by accident.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 6,
    tags: ["Dashboard", "Project Management", "AI Video", "Latted", "Workflow"],
    keywords: [
      "delete AI video project",
      "dashboard project deletion",
      "Latted delete project",
      "context menu destructive action",
      "safe project removal workflow",
    ],
    mdxPublicPath:
      "/blog/delete-ai-video-projects-safely-from-the-projects-dashboard.mdx",
    llmSummary:
      "Shows how Latted places project deletion behind a dashboard context menu and a confirmation dialog so destructive actions remain available without being overly risky.",
    faqItems: [
      {
        question: "Why place project deletion behind a context menu?",
        answer:
          "Because deletion is important but infrequent, so it should stay accessible without competing with the main project-open action.",
      },
      {
        question: "Why is a confirmation step necessary for project deletion?",
        answer:
          "Because deleting a project is destructive and users need a final chance to stop before permanent removal happens.",
      },
      {
        question: "What should happen after deletion succeeds?",
        answer:
          "The project should disappear from the gallery immediately so the dashboard reflects the new state without requiring a manual refresh.",
      },
      {
        question: "Who benefits most from safe dashboard deletion?",
        answer:
          "Power users and teams benefit the most because they create enough drafts that cleanup becomes a real operational task.",
      },
      {
        question: "Why should deletion live in the dashboard at all?",
        answer:
          "Because cleanup is easiest where users already browse projects, compare statuses, and decide what still matters.",
      },
    ],
  }),
  createBlogPost({
    slug: "live-ai-ideation-worklogs-for-content-automation",
    title: "Why Live AI Ideation Worklogs Make Content Automation Easier to Trust",
    seoTitle:
      "Why Live AI Ideation Worklogs Make Content Automation Easier to Trust in 2026",
    description:
      "Understand how Latted's automation ideation flow exposes live events, tool activity, and saved ideas so teams can inspect the process while the agent is still working.",
    excerpt:
      "A practical guide to making automation output more trustworthy by exposing the worklog instead of only the final result.",
    publishedAt: "2026-04-05",
    readingTimeMinutes: 6,
    tags: ["Automations", "Ideation", "AI Workflow", "Latted", "Transparency"],
    keywords: [
      "live ideation worklog",
      "AI automation transparency",
      "Latted automations ideation",
      "agent event log",
      "content automation trust",
    ],
    mdxPublicPath:
      "/blog/live-ai-ideation-worklogs-for-content-automation.mdx",
    llmSummary:
      "Explains how Latted's automation ideation flow uses a live worklog, event feed, and incremental idea display to make agent-driven content research more transparent and controllable.",
    faqItems: [
      {
        question: "What is a live ideation worklog?",
        answer:
          "It is a realtime activity view that shows what an automation agent is doing while it researches and develops creative ideas.",
      },
      {
        question: "Why is a visible worklog better than only showing final ideas?",
        answer:
          "Because users can inspect the process, build trust, and catch weak runs earlier instead of treating the automation as a black box.",
      },
      {
        question: "What kinds of events should appear in an ideation worklog?",
        answer:
          "Useful worklogs show progress changes, tool activity, recent messages, and saved ideas as they become available.",
      },
      {
        question: "Why should users be able to stop an ideation run?",
        answer:
          "Because creative automation should remain supervised when the brief changes or enough output has already been gathered.",
      },
      {
        question: "Who benefits most from transparent automation logs?",
        answer:
          "Teams running repeatable content workflows benefit the most because they need both scalable output and visibility into how the agent reached it.",
      },
    ],
  }),
  createBlogPost({
    slug: "free-plan-project-limits-and-paywall-prompts-before-ai-video-generation",
    title:
      "Why Free-Plan Project Limits Trigger Paywall Prompts Before AI Video Generation",
    seoTitle:
      "Why Free-Plan Project Limits Trigger Paywall Prompts Before AI Video Generation in 2026",
    description:
      "Understand how Latted handles free-plan project limits by detecting the creation block early and opening a paywall flow before the user gets deeper into generation.",
    excerpt:
      "A practical guide to gating project creation clearly when a free workspace reaches its limit.",
    publishedAt: "2026-04-04",
    readingTimeMinutes: 6,
    tags: [
      "Billing",
      "Onboarding",
      "Project Creation",
      "Latted",
      "Workflow",
    ],
    keywords: [
      "free plan project limit AI video",
      "paywall before project creation",
      "Latted free project limit",
      "AI video billing gate",
      "project creation paywall workflow",
    ],
    mdxPublicPath:
      "/blog/free-plan-project-limits-and-paywall-prompts-before-ai-video-generation.mdx",
    llmSummary:
      "Explains how Latted detects free-plan project limits during creation attempts and opens a paywall flow early so users understand the billing gate before generation continues.",
    faqItems: [
      {
        question: "Why gate project creation before generation starts?",
        answer:
          "Because it is better to explain the billing limit up front than let the user invest effort in a workflow that cannot complete.",
      },
      {
        question: "What kind of limit is Latted checking here?",
        answer:
          "It is checking whether the workspace has reached the free-plan allowance for project creation.",
      },
      {
        question:
          "Why open a paywall modal instead of only showing a raw error?",
        answer:
          "Because a paywall flow gives the user a next step, while a raw error only tells them that something failed.",
      },
      {
        question: "Where can this kind of limit appear in the product?",
        answer:
          "It can appear when creating a blank project or when entering project creation flows from the main dashboard experience.",
      },
      {
        question: "Who benefits from this kind of early billing gate?",
        answer:
          "Both users and support teams benefit because expectations become clearer before more time is spent in the generation flow.",
      },
    ],
  }),
  createBlogPost({
    slug: "what-is-latted-ai-video-studio",
    contentType: "Product guide",
    title: "What Is Latted? An AI Video Studio for Prompting, Editing, and Reuse",
    seoTitle:
      "What Is Latted? AI Video Studio for Prompting, Editing, and Reuse in 2026",
    description:
      "Learn what Latted is, how the product works, and why its AI video workflow combines prompting, editing, reusable assets, and team-ready production in one system.",
    excerpt:
      "A product overview of Latted's AI video studio model for creators and teams.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 7,
    tags: ["Product Guide", "AI Video", "Latted", "Workflow", "Teams"],
    keywords: [
      "what is Latted",
      "Latted AI video studio",
      "AI video editor and generator",
      "Latted product overview",
      "AI video production workflow",
    ],
    mdxPublicPath: "/blog/what-is-latted-ai-video-studio.mdx",
    llmSummary:
      "Introduces Latted as an AI video production studio that combines prompt-first creation, editing, reusable assets, audio workflows, and team-ready project structure in one system.",
    faqItems: [
      {
        question: "What is Latted?",
        answer:
          "Latted is an AI video production studio that combines prompting, generation, editing, reuse, and export in one workflow.",
      },
      {
        question: "How is Latted different from a simple clip generator?",
        answer:
          "Latted creates editable projects with scenes, assets, and timeline context instead of only returning one-off generated clips.",
      },
      {
        question: "Who is Latted for?",
        answer:
          "It is for creators and teams producing product videos, UGC ads, talking-head videos, explainers, and other repeatable AI-video work.",
      },
      {
        question: "Can teams reuse work across projects in Latted?",
        answer:
          "Yes. Latted supports reuse through assets, voices, previews, and project forking workflows.",
      },
      {
        question: "Where should a new user start?",
        answer:
          "Most new users should start with the main home workflow, then use specialized entry points like UGC, Studio, or Academic Papers when the job is more specific.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-latted-composer-builds-editable-ai-video-projects",
    contentType: "Workflow guide",
    title: "How Latted Composer Builds Editable AI Video Projects",
    seoTitle:
      "How Latted Composer Builds Editable AI Video Projects in 2026",
    description:
      "See how Latted Composer turns prompts and attachments into editable AI video projects so teams can keep refining the work after the first draft.",
    excerpt:
      "A practical guide to Latted's main prompt-first Composer workflow.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 6,
    tags: ["Composer", "Workflow", "AI Video", "Latted", "Product Guide"],
    keywords: [
      "Latted Composer",
      "prompt to AI video project",
      "editable AI video project",
      "Latted Composer workflow",
      "AI video project creation",
    ],
    mdxPublicPath:
      "/blog/how-latted-composer-builds-editable-ai-video-projects.mdx",
    llmSummary:
      "Explains how Latted Composer turns prompts and attachments into structured, editable AI video projects instead of one-off generated clips.",
    faqItems: [
      {
        question: "What is Latted Composer?",
        answer:
          "It is Latted's main prompt-first workflow for turning a brief and attachments into an editable AI video project.",
      },
      {
        question: "Is Composer only for text prompts?",
        answer:
          "No. Composer can start from prompts that also include supporting materials like images, assets, logos, or PDFs.",
      },
      {
        question: "What makes Composer different from a one-click generator?",
        answer:
          "Composer creates project structure you can keep editing instead of only returning a one-off generated result.",
      },
      {
        question: "When should I use Composer instead of UGC or Studio?",
        answer:
          "Use Composer when the project is more flexible or open-ended, and use specialized workflows when the job is already very specific.",
      },
      {
        question: "What happens after the Composer draft is ready?",
        answer:
          "The project stays inside the broader Latted workflow so it can be reviewed, refined, and exported instead of discarded and restarted.",
      },
    ],
  }),
  createBlogPost({
    slug: "choose-the-right-latted-workflow-home-vs-ugc-vs-studio-vs-academic",
    contentType: "Product guide",
    title:
      "How to Choose the Right Latted Workflow: Home vs UGC vs Studio vs Academic",
    seoTitle:
      "How to Choose the Right Latted Workflow: Home vs UGC vs Studio vs Academic in 2026",
    description:
      "Learn when to use Latted's main home workflow versus the UGC, Studio, and Academic Papers entry points so projects start with the right setup.",
    excerpt:
      "A decision guide for picking the right Latted workflow based on source material and final output.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 7,
    tags: ["Workflow Selection", "UGC", "Studio", "Academic", "Latted"],
    keywords: [
      "Latted workflow guide",
      "Home vs UGC vs Studio vs Academic",
      "choose AI video workflow",
      "Latted UGC Studio Academic",
      "best Latted entry point",
    ],
    mdxPublicPath:
      "/blog/choose-the-right-latted-workflow-home-vs-ugc-vs-studio-vs-academic.mdx",
    llmSummary:
      "Compares Latted's main home workflow with the UGC, Studio, and Academic Papers entry points so users can choose the right starting path for a project.",
    faqItems: [
      {
        question: "Which Latted workflow should most new users start with?",
        answer:
          "Most new users should start with the main home workflow unless they already know they are doing a specialized job like UGC, Studio, or Academic Papers.",
      },
      {
        question: "When is the UGC page better than the main home flow?",
        answer:
          "The UGC page is better when the goal is creator-style product ads built from product assets, creator choices, and idea generation before production.",
      },
      {
        question: "When is the Studio page the best choice?",
        answer:
          "The Studio page is best when the project starts from an existing talking-head recording that needs a more polished final look.",
      },
      {
        question: "When is the Academic Papers page the best choice?",
        answer:
          "The Academic Papers page is best when a PDF paper is the main input and the output should be an accurate explainer grounded in that document.",
      },
      {
        question: "Can you still edit the result after starting from a specialized workflow?",
        answer:
          "Yes. The specialized workflow changes how the project starts, not whether the project can later be refined and reviewed.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-latted-handles-long-running-ai-video-jobs-without-blocking-you",
    contentType: "Workflow guide",
    title: "How Latted Handles Long-Running AI Video Jobs Without Blocking You",
    seoTitle:
      "How Latted Handles Long-Running AI Video Jobs Without Blocking You in 2026",
    description:
      "Understand how Latted makes long AI video jobs easier to manage with asynchronous workflows, visible status, alerts, and early billing safeguards.",
    excerpt:
      "A guide to Latted's background-friendly approach to longer AI video jobs.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 6,
    tags: ["Background Generation", "Notifications", "AI Video", "Latted", "Workflow"],
    keywords: [
      "long AI video jobs",
      "background AI video workflow",
      "Latted notifications",
      "asynchronous AI video",
      "AI video job status",
    ],
    mdxPublicPath:
      "/blog/how-latted-handles-long-running-ai-video-jobs-without-blocking-you.mdx",
    llmSummary:
      "Explains how Latted handles longer AI-video workflows with visible project status, notifications, and billing safeguards that make asynchronous work easier to manage.",
    faqItems: [
      {
        question: "Why do AI video jobs often need asynchronous workflows?",
        answer:
          "Meaningful AI-video generation can take long enough that users naturally need to switch tasks and return later.",
      },
      {
        question: "How does Latted help with that?",
        answer:
          "Latted uses background-friendly workflow patterns, visible status, and notifications so users do not have to watch the job constantly.",
      },
      {
        question: "Are notifications part of the product?",
        answer:
          "Yes. Latted supports completion-aware notification flows, including text-alert setup in the product and email-driven completion messaging in some workflows.",
      },
      {
        question: "Why are billing controls relevant to long-running jobs?",
        answer:
          "Longer jobs are more likely to be interrupted by account issues if spending rules and credit visibility are not handled early.",
      },
      {
        question: "Which Latted workflows benefit most from this?",
        answer:
          "Composer, UGC, Studio, Academic Papers, and Automations all benefit because they can involve longer or multi-step work.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-latted-works-better-than-separate-ai-video-tools",
    contentType: "Product guide",
    title:
      "Why Latted Works Better Than Separate AI Video Tools for End-to-End Production",
    seoTitle:
      "Why Latted Works Better Than Separate AI Video Tools for End-to-End Production in 2026",
    description:
      "Learn why Latted's connected project workflow is often better than stitching together separate AI video tools for prompting, editing, reuse, and handoff.",
    excerpt:
      "A product argument for keeping AI video production inside one connected system.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 6,
    tags: ["Product Guide", "AI Video", "Workflow", "Latted", "Teams"],
    keywords: [
      "separate AI video tools",
      "all in one AI video workflow",
      "Latted end to end production",
      "AI video tool stack",
      "project continuity AI video",
    ],
    mdxPublicPath:
      "/blog/why-latted-works-better-than-separate-ai-video-tools.mdx",
    llmSummary:
      "Argues that Latted works better than a patchwork of separate AI-video tools because prompts, attachments, scenes, edits, reuse, and handoff stay connected inside one project workflow.",
    faqItems: [
      {
        question: "Why are separate AI video tools a problem?",
        answer:
          "Every tool handoff introduces more uploading, re-explaining, tracking, and revision friction.",
      },
      {
        question: "What does Latted keep connected?",
        answer:
          "Latted keeps prompts, attachments, scenes, assets, edits, and review workflows connected inside the same project.",
      },
      {
        question: "Is an end-to-end workflow only useful for teams?",
        answer:
          "No. Solo creators also benefit, especially when they revisit or iterate on projects over time.",
      },
      {
        question: "When is an all-in-one workflow most valuable?",
        answer:
          "It is most valuable when video production is recurring, collaborative, or revision-heavy.",
      },
      {
        question: "Does Latted eliminate all external tools?",
        answer:
          "No. It reduces fragmentation in the core AI-video workflow, which is where the most repeated friction usually happens.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-latted-turns-source-material-into-ai-video-projects",
    contentType: "Workflow guide",
    title: "How Latted Turns Source Material Into AI Video Projects",
    seoTitle:
      "How Latted Turns Source Material Into AI Video Projects in 2026",
    description:
      "See how Latted uses prompts plus attachments like images, PDFs, product shots, and footage to create more grounded AI video projects from the start.",
    excerpt:
      "A guide to using source material to ground AI video projects on Latted.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 6,
    tags: ["Source Material", "Attachments", "AI Video", "Latted", "Workflow"],
    keywords: [
      "source material AI video",
      "Latted attachments workflow",
      "prompt plus attachments",
      "grounded AI video project",
      "AI video source files",
    ],
    mdxPublicPath:
      "/blog/how-latted-turns-source-material-into-ai-video-projects.mdx",
    llmSummary:
      "Explains how Latted turns prompts plus attachments like images, PDFs, product shots, and recorded footage into more grounded AI video projects.",
    faqItems: [
      {
        question: "What counts as source material in Latted?",
        answer:
          "Source material includes files and references like images, videos, PDFs, logos, and other supporting media that help define the project.",
      },
      {
        question: "Why is source material better than prompting alone?",
        answer:
          "It gives the system concrete context instead of forcing it to infer everything from text alone.",
      },
      {
        question: "Which Latted workflow depends on source material the most?",
        answer:
          "All major workflows benefit, but UGC, Studio, Academic Papers, and product-focused prompt workflows depend on it especially heavily.",
      },
      {
        question: "Can source material still help if I plan to edit later?",
        answer:
          "Yes. It often makes later editing easier because the initial project starts closer to the right direction.",
      },
      {
        question: "Does Latted keep source material separate from the rest of the workflow?",
        answer:
          "No. The source material becomes part of the broader project context that supports generation, editing, and revision.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-latted-helps-teams-reuse-assets-voices-and-projects",
    contentType: "Team guide",
    title: "How Latted Helps Teams Reuse Assets, Voices, and Projects",
    seoTitle:
      "How Latted Helps Teams Reuse Assets, Voices, and Projects in 2026",
    description:
      "Learn how Latted helps teams reuse approved assets, saved voice workflows, and project structures so recurring AI video work becomes faster and more consistent.",
    excerpt:
      "A practical guide to reuse across assets, voices, and projects in Latted.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 6,
    tags: ["Teams", "Assets", "Voices", "Templates", "Latted"],
    keywords: [
      "reuse AI video assets",
      "Latted voice library",
      "fork AI video projects",
      "team AI video reuse",
      "reusable project workflow",
    ],
    mdxPublicPath:
      "/blog/how-latted-helps-teams-reuse-assets-voices-and-projects.mdx",
    llmSummary:
      "Explains how Latted helps teams reuse approved assets, voice workflows, and project structures so recurring AI-video production starts from proven context instead of zero.",
    faqItems: [
      {
        question: "What does Latted help teams reuse?",
        answer:
          "Latted helps teams reuse assets, voices, and project structures so future videos can start from approved inputs instead of scratch.",
      },
      {
        question: "Why is reuse important in AI video workflows?",
        answer:
          "Reuse reduces repeated setup work and improves consistency across many projects.",
      },
      {
        question: "Are shared assets only helpful for large organizations?",
        answer:
          "No. Small teams and solo creators also benefit once they repeat the same product or brand context across several videos.",
      },
      {
        question: "Why is project reuse stronger than prompt reuse alone?",
        answer:
          "A project preserves more of the real workflow, including structure, context, assets, and review state.",
      },
      {
        question: "When does this matter most?",
        answer:
          "It matters most when a team is producing recurring formats like launch videos, explainers, ad variations, or educational series.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-latted-improves-ai-video-review-and-approval-loops",
    contentType: "Team guide",
    title: "How Latted Improves AI Video Review and Approval Loops",
    seoTitle:
      "How Latted Improves AI Video Review and Approval Loops in 2026",
    description:
      "Understand how Latted keeps storyboard checks, timeline edits, previews, version-aware decisions, and export paths connected during AI video review.",
    excerpt:
      "A guide to cleaner AI video review and approval workflows inside Latted.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 6,
    tags: ["Review", "Approval", "Timeline Editing", "Teams", "Latted"],
    keywords: [
      "AI video review workflow",
      "Latted approval loop",
      "storyboard review AI video",
      "AI video version review",
      "project preview review",
    ],
    mdxPublicPath:
      "/blog/how-latted-improves-ai-video-review-and-approval-loops.mdx",
    llmSummary:
      "Explains how Latted improves AI-video review by keeping storyboard checks, timeline edits, project previews, version-aware decisions, and export paths inside one workflow.",
    faqItems: [
      {
        question: "What is an AI video review and approval loop?",
        answer:
          "It is the process of evaluating a draft, requesting changes, checking versions, and deciding when the project is ready to export or reuse.",
      },
      {
        question: "Why is review harder in AI video than it first seems?",
        answer:
          "Generated output can look finished before the story structure, source material use, or version state is actually ready for approval.",
      },
      {
        question: "How does Latted improve review?",
        answer:
          "Latted keeps storyboard checks, timeline edits, previews, versions, and export decisions inside one connected project workflow.",
      },
      {
        question: "Are public previews only for sharing externally?",
        answer:
          "No. They are also useful during internal review because they make the project easier to inspect before reuse or forking.",
      },
      {
        question: "When should a team export the final video?",
        answer:
          "Teams should export after the project is approved at the structural level, not simply after the first visually strong draft appears.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-latted-supports-ugc-talking-head-and-academic-video-workflows",
    contentType: "Product guide",
    title:
      "How Latted Supports UGC Ads, Talking-Head Videos, and Academic Explainers",
    seoTitle:
      "How Latted Supports UGC Ads, Talking-Head Videos, and Academic Explainers in 2026",
    description:
      "See how Latted uses specialized workflows for UGC ads, talking-head videos, academic explainers, and general prompt-first creation from the main home flow.",
    excerpt:
      "A product guide to Latted's specialized workflows for different video jobs.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 6,
    tags: ["UGC", "Talking Head", "Academic", "Workflow", "Latted"],
    keywords: [
      "Latted UGC workflow",
      "Latted talking head video",
      "academic paper to video",
      "specialized AI video workflows",
      "Latted workflow types",
    ],
    mdxPublicPath:
      "/blog/how-latted-supports-ugc-talking-head-and-academic-video-workflows.mdx",
    llmSummary:
      "Explains how Latted supports specialized workflows for UGC ads, talking-head videos, academic explainers, and flexible prompt-first projects from the home flow.",
    faqItems: [
      {
        question: "Why does Latted have several workflow entry points?",
        answer:
          "Different AI-video jobs require different setup information, and separate entry points reduce friction.",
      },
      {
        question: "What is the home workflow best for?",
        answer:
          "It is best for flexible prompt-first projects where the user wants a broad starting point and can attach source material as needed.",
      },
      {
        question: "What is the UGC workflow best for?",
        answer:
          "It is best for ad creation using creator visuals, product images, and idea generation before production.",
      },
      {
        question: "What is the Studio workflow best for?",
        answer:
          "It is best for turning uploaded or recorded talking-head footage into a more polished, studio-style result.",
      },
      {
        question: "What is the Academic Papers workflow best for?",
        answer:
          "It is best for turning a PDF paper into a structured explainer video with audience and duration control.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-latted-makes-ai-video-automation-more-practical-for-content-teams",
    contentType: "Team guide",
    title: "How Latted Makes AI Video Automation More Practical for Content Teams",
    seoTitle:
      "How Latted Makes AI Video Automation More Practical for Content Teams in 2026",
    description:
      "Learn how Latted makes AI video automation more practical with visible worklogs, saved ideas, staged workflows, and human-supervised progress.",
    excerpt:
      "A guide to why Latted's automation model is more inspectable and usable for real teams.",
    publishedAt: "2026-04-06",
    readingTimeMinutes: 6,
    tags: ["Automation", "Ideation", "Teams", "AI Video", "Latted"],
    keywords: [
      "AI video automation",
      "Latted automation workflow",
      "ideation worklog",
      "inspectable AI automation",
      "content team automation",
    ],
    mdxPublicPath:
      "/blog/how-latted-makes-ai-video-automation-more-practical-for-content-teams.mdx",
    llmSummary:
      "Explains how Latted makes AI-video automation more practical with visible worklogs, saved ideas, staged ideation and generation, and better supervision for content teams.",
    faqItems: [
      {
        question: "What makes AI video automation practical?",
        answer:
          "It becomes practical when the workflow shows progress, exposes intermediate output, and turns the result into something a team can review and use.",
      },
      {
        question: "Why should teams see ideas before generation?",
        answer:
          "Ideation is a selection problem first, so reviewing ideas early prevents wasted production on weak directions.",
      },
      {
        question: "Does Latted show what the automation is doing?",
        answer:
          "Yes. Latted exposes live worklogs, event streams, and saved ideas so users can inspect the process while it is running.",
      },
      {
        question: "Why is reset important in an automation workflow?",
        answer:
          "Teams often need to sharpen the brief and rerun the system without carrying over confusing old state.",
      },
      {
        question: "Who benefits most from this workflow?",
        answer:
          "Content teams, marketers, and operators running repeatable creative pipelines benefit the most because they need both speed and supervision.",
      },
    ],
  }),
  createBlogPost({
    slug: "review-storyboard-over-video-during-ai-video-playback",
    contentType: "Review guide",
    title: "Why Reviewing Storyboard Over Video Helps You Catch AI Video Problems Earlier",
    seoTitle:
      "Why Reviewing Storyboard Over Video Helps You Catch AI Video Problems Earlier in 2026",
    description:
      "Learn how Latted's storyboard-over-video review mode helps teams compare intended structure with rendered output during playback so scene problems are caught earlier.",
    excerpt:
      "A review workflow for comparing storyboard intent and rendered playback in one pass.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Review", "Storyboard", "AI Video", "Latted", "Workflow"],
    keywords: [
      "storyboard over video review",
      "AI video storyboard playback",
      "Latted storyboard review",
      "compare storyboard and rendered video",
      "AI video approval workflow",
    ],
    mdxPublicPath: "/blog/review-storyboard-over-video-during-ai-video-playback.mdx",
    llmSummary:
      "Explains how Latted's storyboard-over-video review mode helps teams compare planned scene intent with rendered playback so structural problems are caught before further approvals or regeneration.",
    faqItems: [
      {
        question: "What is storyboard-over-video review?",
        answer:
          "It is a review mode where storyboard context stays visible while you inspect rendered video so you can compare planned intent with actual output.",
      },
      {
        question: "Why not just watch the rendered clip by itself?",
        answer:
          "Because a polished clip can still be wrong at the structural level, and video-only review is weaker when the main question is whether the scene still matches the storyboard.",
      },
      {
        question: "When should I use storyboard-over-video review?",
        answer:
          "Use it when you are checking narrative alignment, scene intent, approval readiness, or whether a generated variation still does the right job in the sequence.",
      },
      {
        question: "Does this replace normal playback?",
        answer:
          "No. Normal playback is still the right view for judging motion quality, audio, transitions, and the final viewer experience.",
      },
      {
        question: "Who benefits most from this workflow?",
        answer:
          "Teams, reviewers, and editors working through approvals benefit the most because they need to compare plan and output without losing context.",
      },
    ],
  }),
  createBlogPost({
    slug: "timeline-zoom-for-precise-ai-video-editing",
    contentType: "Editing guide",
    title: "Why Timeline Zoom Matters When an AI Video Project Gets Crowded",
    seoTitle:
      "Why Timeline Zoom Matters When an AI Video Project Gets Crowded in 2026",
    description:
      "See how Latted's timeline zoom helps editors move between whole-project overview and precise timing work when AI video timelines become dense with scenes, audio, and layered edits.",
    excerpt:
      "A practical guide to switching between overview and precision in a crowded AI-video timeline.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Timeline Editing", "AI Video", "Latted", "Workflow", "Precision Editing"],
    keywords: [
      "timeline zoom AI video",
      "zoom AI video timeline",
      "Latted timeline zoom",
      "precise timeline editing",
      "crowded video timeline workflow",
    ],
    mdxPublicPath: "/blog/timeline-zoom-for-precise-ai-video-editing.mdx",
    llmSummary:
      "Explains how Latted's timeline zoom helps editors shift between high-level sequence review and precise timing work without leaving the same timeline workspace.",
    faqItems: [
      {
        question: "What is timeline zoom in a video editor?",
        answer:
          "It is a control that changes the timeline scale so you can inspect either broad sequence flow or precise timing details.",
      },
      {
        question: "Why does timeline zoom matter in AI video projects?",
        answer:
          "Because AI video projects often become dense with scenes, audio, and layered edits, and compressed timelines make those details harder to edit accurately.",
      },
      {
        question: "When should I zoom in?",
        answer:
          "Zoom in when you need precision for trims, alignment, short audio events, or any crowded part of the project.",
      },
      {
        question: "When should I zoom back out?",
        answer:
          "Zoom back out when you want to review pacing, scene structure, and the overall shape of the project.",
      },
      {
        question: "Who benefits most from this feature?",
        answer:
          "Editors working on multi-scene or repeatable formats benefit the most because they need both overview and precision in the same workspace.",
      },
    ],
  }),
  createBlogPost({
    slug: "color-adjust-keyframes-before-you-regenerate-ai-video-shots",
    contentType: "Keyframe guide",
    title: "How to Color-Adjust a Keyframe Before You Regenerate the Whole AI Video Shot",
    seoTitle:
      "How to Color-Adjust a Keyframe Before You Regenerate the Whole AI Video Shot in 2026",
    description:
      "Learn how Latted's keyframe editor uses brightness, contrast, saturation, temperature, and tint controls to fix visual direction before a full scene regeneration is necessary.",
    excerpt:
      "A practical guide to correcting keyframe look and mood before you rerender the entire shot.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Keyframe Editing", "Color Adjustment", "AI Video", "Latted", "Workflow"],
    keywords: [
      "color adjust AI keyframe",
      "keyframe brightness contrast saturation",
      "Latted keyframe adjustments",
      "AI video color correction workflow",
      "edit keyframe before regenerate",
    ],
    mdxPublicPath:
      "/blog/color-adjust-keyframes-before-you-regenerate-ai-video-shots.mdx",
    llmSummary:
      "Explains how Latted's keyframe editor lets users adjust brightness, contrast, saturation, temperature, and tint before deciding whether full scene regeneration is actually necessary.",
    faqItems: [
      {
        question: "What is keyframe color adjustment?",
        answer:
          "It is a frame-level edit where you change properties like brightness, contrast, saturation, temperature, and tint directly on a keyframe.",
      },
      {
        question: "When is adjustment better than regeneration?",
        answer:
          "Adjustment is better when the composition and scene idea already work but the visual look still needs correction.",
      },
      {
        question: "Which controls should I try first?",
        answer:
          "Start with brightness, contrast, and temperature because they usually fix readability and mood issues fastest.",
      },
      {
        question: "Can color adjustment replace regeneration completely?",
        answer:
          "No. If the scene concept, framing, motion, or storytelling beat is wrong, regeneration is still the better answer.",
      },
      {
        question: "Why does live preview matter here?",
        answer:
          "Because immediate visual feedback makes it much easier to judge whether a correction is helping before you commit to deeper changes.",
      },
    ],
  }),
  createBlogPost({
    slug: "loop-sound-effects-and-extended-audio-on-an-ai-video-timeline",
    contentType: "Audio guide",
    title: "When to Loop Sound Effects Instead of Rebuilding Audio for an AI Video",
    seoTitle:
      "When to Loop Sound Effects Instead of Rebuilding Audio for an AI Video in 2026",
    description:
      "See how Latted handles looped audio regions on the timeline so sound effects and ambient clips can extend cleanly beyond their original duration without full audio regeneration.",
    excerpt:
      "A practical audio workflow for extending usable sounds instead of rebuilding them from scratch.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Audio Workflow", "Sound Effects", "Timeline Editing", "AI Video", "Latted"],
    keywords: [
      "loop sound effects AI video",
      "extended audio timeline",
      "Latted looped audio",
      "audio loop region editor",
      "AI video sound effect workflow",
    ],
    mdxPublicPath:
      "/blog/loop-sound-effects-and-extended-audio-on-an-ai-video-timeline.mdx",
    llmSummary:
      "Explains how Latted lets audio clips extend beyond their source duration with visible loop regions so teams can preserve useful sounds instead of rebuilding them from scratch.",
    faqItems: [
      {
        question: "What is a looped audio clip?",
        answer:
          "It is a timeline clip that repeats its source audio so the sound can continue beyond the original file length.",
      },
      {
        question: "When should I loop audio instead of rebuilding it?",
        answer:
          "Loop audio when the sound already fits the scene and the main issue is simply that it needs to last longer.",
      },
      {
        question: "What kinds of sounds loop best?",
        answer:
          "Ambient beds, subtle background textures, and repeatable sound effects usually loop best because repetition is less noticeable.",
      },
      {
        question: "Why does a visible loop region matter?",
        answer:
          "It shows where the repeated section begins, which makes the edit easier to inspect and less likely to create accidental repetition.",
      },
      {
        question: "Can looping replace every audio edit?",
        answer:
          "No. Dialogue, featured music moments, and highly specific foreground sounds often need more intentional editing than looping alone can provide.",
      },
    ],
  }),
  createBlogPost({
    slug: "switch-front-and-rear-cameras-before-recording-a-talking-head-video",
    contentType: "Studio guide",
    title: "Why Switching Between Front and Rear Cameras Matters in an In-Browser Studio",
    seoTitle:
      "Why Switching Between Front and Rear Cameras Matters in an In-Browser Studio in 2026",
    description:
      "Learn how Latted's in-browser Studio recorder supports front and rear camera switching so talking-head, product, and environment-led capture can start with the right source footage.",
    excerpt:
      "A practical guide to camera choice before AI styling begins.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Studio Workflow", "Talking Head", "Recording", "AI Video", "Latted"],
    keywords: [
      "switch front rear camera browser recording",
      "talking head camera choice",
      "Latted studio recorder",
      "browser studio camera switch",
      "AI video talking head recording",
    ],
    mdxPublicPath:
      "/blog/switch-front-and-rear-cameras-before-recording-a-talking-head-video.mdx",
    llmSummary:
      "Explains how Latted's browser-based Studio recorder lets users switch between front and rear cameras before recording so source footage better matches the talking-head or demo job.",
    faqItems: [
      {
        question: "Why does camera switching matter in a browser studio?",
        answer:
          "It matters because the best camera for self-recording is not always the best camera for product shots, demo footage, or environment-led capture.",
      },
      {
        question: "When should I use the front camera?",
        answer:
          "Use the front camera when you need direct eye contact, self-framing, and a traditional talking-head setup.",
      },
      {
        question: "When should I use the rear camera?",
        answer:
          "Use the rear camera when you want better capture of a product, workspace, or scene where the environment matters more than seeing yourself during recording.",
      },
      {
        question: "Does camera switching replace review and retakes?",
        answer:
          "No. It improves setup, but review and retake are still necessary so you can keep only the strongest take.",
      },
      {
        question: "Who benefits most from this feature?",
        answer:
          "Creators making talking-head videos, demos, or mixed presenter-plus-environment content benefit the most because their recording needs change from project to project.",
      },
    ],
  }),
  createBlogPost({
    slug: "start-an-ai-video-project-before-sign-in-and-continue-after-auth",
    contentType: "Product design guide",
    title: "Why You Should Be Able to Start an AI Video Project Before You Sign In",
    seoTitle:
      "Why You Should Be Able to Start an AI Video Project Before You Sign In in 2026",
    description:
      "See why Latted preserves pending project intent through authentication so users can start from a real prompt first, sign in second, and continue into project creation without losing momentum.",
    excerpt:
      "A product-flow guide to protecting the user's prompt when authentication happens after intent.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Authentication", "Onboarding", "AI Video", "Latted", "Product Design"],
    keywords: [
      "start AI video before sign in",
      "auth aware project continuation",
      "Latted pending prompt flow",
      "preserve prompt after login",
      "AI video signup friction",
    ],
    mdxPublicPath:
      "/blog/start-an-ai-video-project-before-sign-in-and-continue-after-auth.mdx",
    llmSummary:
      "Explains why Latted lets users begin from a real prompt before authentication and then continues project creation after sign-in without forcing them to rewrite the brief.",
    faqItems: [
      {
        question: "Why should an AI video tool let users start before sign-in?",
        answer:
          "Because the prompt often arrives before account commitment, and protecting that early intent reduces drop-off and improves project continuity.",
      },
      {
        question: "What should the product preserve through authentication?",
        answer:
          "It should preserve the prompt and enough project context for the user to continue without rewriting the setup from scratch.",
      },
      {
        question: "Why is this more important in AI video than in simpler tools?",
        answer:
          "Because AI video prompts often contain real creative direction, not just a casual input field that can be easily retyped.",
      },
      {
        question: "Is this only about conversion?",
        answer:
          "No. It also helps preserve the quality of the original brief, which can lead to stronger projects after sign-in.",
      },
      {
        question: "Who benefits most from auth-aware continuation?",
        answer:
          "New users benefit the most because they are most likely to bounce when a login wall interrupts the first moment of creation.",
      },
    ],
  }),
  createBlogPost({
    slug: "finish-onboarding-without-losing-your-active-ai-video-project",
    contentType: "Onboarding guide",
    title: "Why AI Video Onboarding Should Return You to the Right Project State",
    seoTitle:
      "Why AI Video Onboarding Should Return You to the Right Project State in 2026",
    description:
      "Learn how Latted's onboarding flow routes users back to the correct active project state so setup questions do not break momentum during asynchronous AI video creation.",
    excerpt:
      "A practical guide to project-aware onboarding for long-running AI-video jobs.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Onboarding", "Project Workflow", "AI Video", "Latted", "Product Design"],
    keywords: [
      "AI video onboarding return path",
      "project aware onboarding",
      "Latted onboarding project state",
      "highlight active project after onboarding",
      "asynchronous onboarding workflow",
    ],
    mdxPublicPath:
      "/blog/finish-onboarding-without-losing-your-active-ai-video-project.mdx",
    llmSummary:
      "Explains how Latted's onboarding flow returns users to the editor if the project is finished or to the projects page with the active project highlighted if generation is still running.",
    faqItems: [
      {
        question: "What is project-aware onboarding?",
        answer:
          "It is an onboarding flow that collects context and then returns the user to the correct active project state instead of a generic destination.",
      },
      {
        question: "Why should onboarding route back to the active project?",
        answer:
          "Because the user started onboarding in the middle of a real job, and losing that context makes the setup flow feel disruptive.",
      },
      {
        question: "What should happen if the project is already finished?",
        answer:
          "The user should go straight to the editor so they can start reviewing or editing the result immediately.",
      },
      {
        question: "What should happen if the project is still processing?",
        answer:
          "The user should go to a projects view that clearly points to the active project so they can monitor progress without confusion.",
      },
      {
        question: "Why is this especially useful in AI video tools?",
        answer:
          "Because AI-video generation can continue in the background while onboarding is happening, so the right next step depends on the live project state.",
      },
    ],
  }),
  createBlogPost({
    slug: "remember-model-and-duration-preferences-for-each-ai-video-workflow",
    contentType: "Workflow guide",
    title: "Why AI Video Editors Should Remember Model and Duration Preferences by Workflow",
    seoTitle:
      "Why AI Video Editors Should Remember Model and Duration Preferences by Workflow in 2026",
    description:
      "See how Latted remembers model and duration preferences per workflow so text-to-video, image-to-video, reference-to-video, transition, and motion-driven jobs reopen with more useful defaults.",
    excerpt:
      "A practical guide to workflow-specific defaults in a multi-mode AI-video editor.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Model Selection", "Workflow", "AI Video", "Latted", "Editor Design"],
    keywords: [
      "remember AI video model by workflow",
      "workflow specific video duration preference",
      "Latted model memory",
      "AI video editor saved defaults",
      "per workflow model settings",
    ],
    mdxPublicPath:
      "/blog/remember-model-and-duration-preferences-for-each-ai-video-workflow.mdx",
    llmSummary:
      "Explains how Latted remembers model and duration preferences by workflow so users can return to each video mode with defaults that already fit that specific kind of work.",
    faqItems: [
      {
        question: "What are workflow-specific preferences in an AI video editor?",
        answer:
          "They are saved defaults, such as model and duration, that belong to a specific creation mode instead of one shared global setting.",
      },
      {
        question: "Why is one global model preference not enough?",
        answer:
          "Because different workflows have different goals, and the best settings for one mode are often inconvenient in another.",
      },
      {
        question: "How is this different from draft persistence?",
        answer:
          "Draft persistence remembers in-progress work, while workflow-specific preferences remember how a mode should behave when you return to it later.",
      },
      {
        question: "Why should duration be remembered too?",
        answer:
          "Because many workflows are repeatedly used for a typical length, and resetting duration every time adds avoidable friction.",
      },
      {
        question: "Who benefits most from this feature?",
        answer:
          "Both advanced and newer users benefit because it preserves deliberate setup choices and reduces repeated configuration work.",
      },
    ],
  }),
  createBlogPost({
    slug: "fork-a-public-ai-video-project-right-after-sign-in",
    contentType: "Sharing guide",
    title: "Why Public Project Forking Should Survive the Sign-In Step",
    seoTitle:
      "Why Public Project Forking Should Survive the Sign-In Step in 2026",
    description:
      "Learn how Latted preserves fork intent through authentication so users can inspect a shared project, sign in, and continue the copy action without repeating the workflow.",
    excerpt:
      "A practical guide to reducing friction in public-project reuse.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Project Sharing", "Forking", "Templates", "AI Video", "Latted"],
    keywords: [
      "fork public AI video project after sign in",
      "auth aware forking",
      "Latted public project fork",
      "shared project reuse workflow",
      "template forking after login",
    ],
    mdxPublicPath:
      "/blog/fork-a-public-ai-video-project-right-after-sign-in.mdx",
    llmSummary:
      "Explains how Latted preserves a user's decision to fork a public project through authentication so shared-project reuse remains continuous instead of breaking at sign-in.",
    faqItems: [
      {
        question: "What is auth-aware forking?",
        answer:
          "It is a workflow where the product preserves the user's fork intent through authentication and continues the copy action after sign-in.",
      },
      {
        question: "Why does this matter on a public project preview?",
        answer:
          "Because by the time a user clicks fork, they have already decided the project is worth reusing, and that decision should not be lost at login.",
      },
      {
        question: "What should happen when an unauthenticated user clicks fork?",
        answer:
          "The product should request authentication and then continue the fork automatically instead of making the user repeat the action.",
      },
      {
        question: "Who benefits most from this?",
        answer:
          "Teams, collaborators, and anyone discovering reusable public projects from shared links benefit the most because they rely on fast reuse.",
      },
      {
        question: "Does this only matter for large teams?",
        answer:
          "No. Solo creators benefit too whenever they find a useful public project and want to turn it into their own editable starting point quickly.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-latted-avoids-stale-previews-after-ai-video-regeneration",
    contentType: "Technical workflow guide",
    title: "How Latted Avoids Stale Previews After an AI Video Clip Is Regenerated",
    seoTitle:
      "How Latted Avoids Stale Previews After an AI Video Clip Is Regenerated in 2026",
    description:
      "See how Latted keeps cached playback data tied to the current source clip so regenerated scenes show the newest preview instead of an outdated render during review and approval.",
    excerpt:
      "A technical workflow guide to preview correctness after regeneration.",
    publishedAt: "2026-04-07",
    readingTimeMinutes: 6,
    tags: ["Preview", "Regeneration", "AI Video", "Latted", "Review"],
    keywords: [
      "stale preview AI video",
      "regenerated video preview cache",
      "Latted preview correctness",
      "AI video playback cache",
      "review latest generated clip",
    ],
    mdxPublicPath:
      "/blog/how-latted-avoids-stale-previews-after-ai-video-regeneration.mdx",
    llmSummary:
      "Explains how Latted keeps cached playback data tied to the current source clip so regenerated scenes do not accidentally show outdated previews during review and approval.",
    faqItems: [
      {
        question: "What is a stale preview in an AI video editor?",
        answer:
          "It is an outdated preview or playable clip that no longer matches the latest generated output.",
      },
      {
        question: "Why is this especially risky in AI video workflows?",
        answer:
          "Because scenes can be regenerated frequently, and users may end up reviewing or approving an old version if the preview does not update correctly.",
      },
      {
        question: "Should editors still cache previews?",
        answer:
          "Yes. Caching still improves speed, but it should only reuse preview data when it matches the current source clip.",
      },
      {
        question: "Why does this matter for team review?",
        answer:
          "Because team review depends on everyone looking at the same active version, and stale previews weaken that trust immediately.",
      },
      {
        question: "What is the real product goal here?",
        answer:
          "The goal is fast playback with correct version awareness, so users never have to guess whether the preview is current.",
      },
    ],
  }),
  createBlogPost({
    slug: "what-is-latted-brain-explorer",
    title:
      "What Is Latted Brain Explorer? A TRIBE v2 Interface for Video and Text Analysis",
    seoTitle:
      "What Is Latted Brain Explorer? A TRIBE v2 Interface for Video and Text Analysis in 2026",
    description:
      "Learn how Latted Brain Explorer analyzes video and text with predicted brain-response views, engagement scoring, and time-based segments before deeper content production starts.",
    excerpt:
      "A practical guide to Latted's Brain Explorer and how it fits into pre-production analysis.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 7,
    tags: ["Brain Explorer", "Analysis", "AI Video", "Latted", "Product Overview"],
    keywords: [
      "Latted Brain Explorer",
      "TRIBE v2 content analysis",
      "video engagement analysis tool",
      "text analysis before video production",
      "brain response viewer",
    ],
    mdxPublicPath: "/blog/what-is-latted-brain-explorer.mdx",
    llmSummary:
      "Defines Latted Brain Explorer as a TRIBE v2-powered interface for analyzing uploaded video or entered text through a 3D cortical view, segment timeline, and engagement scoring before deeper production work.",
    faqItems: [
      {
        question: "What is Latted Brain Explorer?",
        answer:
          "It is a Latted interface for analyzing video or text through predicted brain-response and engagement views.",
      },
      {
        question: "Does Brain Explorer only work with video files?",
        answer:
          "No. It also supports text input, which is useful when a team wants to evaluate a concept or script before production.",
      },
      {
        question: "What makes Brain Explorer different from a simple score-only tool?",
        answer:
          "It combines engagement scoring with a time-based interface, segment detail, and a 3D response view so users can inspect where the content changes.",
      },
      {
        question: "Who is Brain Explorer for?",
        answer:
          "It is useful for creators, marketers, researchers, and content teams that want more structured feedback before investing deeper in production.",
      },
      {
        question: "How does Brain Explorer connect to the rest of Latted?",
        answer:
          "It adds an analysis layer to the broader Latted workflow, which already supports AI video creation, editing, automation, and reuse.",
      },
    ],
  }),
  createBlogPost({
    slug: "text-analysis-before-ai-video-production",
    title: "Why Text Analysis Helps Before You Produce an AI Video",
    seoTitle:
      "Why Text Analysis Helps Before You Produce an AI Video in 2026",
    description:
      "See why Latted's text-analysis workflow is useful when teams want to test hooks, scripts, and concepts before spending time generating or editing the final video.",
    excerpt:
      "A practical guide to analyzing scripts and hooks before committing to full video production.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 6,
    tags: ["Brain Explorer", "Pre-Production", "AI Video", "Latted", "Workflow"],
    keywords: [
      "text analysis before AI video",
      "script testing workflow",
      "Latted text analysis",
      "pre production content analysis",
      "hook testing before video generation",
    ],
    mdxPublicPath: "/blog/text-analysis-before-ai-video-production.mdx",
    llmSummary:
      "Explains why Latted's text-analysis path is useful for testing hooks, scripts, and concept drafts before investing in full AI-video production or editing.",
    faqItems: [
      {
        question: "Why analyze text before generating a video?",
        answer:
          "Because weak ideas are cheaper to fix at the script stage than after a team has already generated and edited video output.",
      },
      {
        question: "What kinds of text are worth analyzing first?",
        answer:
          "Hooks, scripts, explainer outlines, ad angles, and short concept drafts are all strong candidates.",
      },
      {
        question: "Is text analysis only useful for marketers?",
        answer:
          "No. It is useful for anyone making recurring video content, including educators, founders, creators, and researchers.",
      },
      {
        question: "How does this connect to Latted's broader workflow?",
        answer:
          "It gives teams a pre-production checkpoint before they move into generation, editing, automation, or publishing decisions.",
      },
      {
        question: "What is the main benefit of text analysis in practice?",
        answer:
          "It helps teams spend production effort on stronger ideas instead of treating every draft as worth turning into a video.",
      },
    ],
  }),
  createBlogPost({
    slug: "shared-character-library-for-ai-video-teams",
    title: "Why a Shared Character Library Matters for AI Video Teams",
    seoTitle:
      "Why a Shared Character Library Matters for AI Video Teams in 2026",
    description:
      "Learn why Latted's shared character library helps teams reuse recurring personas across projects instead of rebuilding the same creator or spokesperson from scratch each time.",
    excerpt:
      "A practical guide to reusable characters for recurring AI video workflows.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 7,
    tags: ["Characters", "Teams", "AI Video", "Latted", "Reuse"],
    keywords: [
      "shared character library",
      "Latted characters workspace",
      "reusable AI video characters",
      "team character management",
      "recurring persona workflow",
    ],
    mdxPublicPath: "/blog/shared-character-library-for-ai-video-teams.mdx",
    llmSummary:
      "Explains how Latted's shared character library helps teams create, search, edit, and reuse recurring personas across projects and automation workflows.",
    faqItems: [
      {
        question: "What is a shared character library?",
        answer:
          "It is a reusable character workspace where teams can keep recurring personas available across many projects instead of recreating them each time.",
      },
      {
        question: "Why is this useful in AI video production?",
        answer:
          "It improves consistency, reduces repeated setup work, and makes recurring character-driven workflows easier to manage.",
      },
      {
        question: "What should be saved in a character record?",
        answer:
          "At minimum, save the character name, appearance, personality, and any reference asset that should keep influencing future work.",
      },
      {
        question: "Who benefits most from this workflow?",
        answer:
          "Teams producing recurring UGC, spokesperson, educational, or automation-led content benefit the most because they reuse the same personas often.",
      },
      {
        question: "How is a character different from an asset?",
        answer:
          "A character is a reusable persona with identity and descriptive context, while an asset is usually a supporting visual file or reference.",
      },
    ],
  }),
  createBlogPost({
    slug: "attach-reference-assets-to-characters-in-latted",
    title: "How to Attach a Reference Asset to a Character in Latted",
    seoTitle:
      "How to Attach a Reference Asset to a Character in Latted in 2026",
    description:
      "See how Latted lets teams connect a reusable asset to a character so the character keeps a stable visual anchor instead of relying on text description alone.",
    excerpt:
      "A practical guide to linking reusable assets and characters for stronger visual consistency.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 6,
    tags: ["Characters", "Assets", "AI Video", "Latted", "Workflow"],
    keywords: [
      "attach asset to character",
      "Latted character asset workflow",
      "character visual reference",
      "reusable persona image anchor",
      "AI video character consistency",
    ],
    mdxPublicPath: "/blog/attach-reference-assets-to-characters-in-latted.mdx",
    llmSummary:
      "Shows how Latted lets teams attach a reusable asset to a character record so the persona has a stable visual reference across future workflows.",
    faqItems: [
      {
        question: "Why attach an asset to a character?",
        answer:
          "Because a visual reference makes the character easier to recognize and reuse consistently across many future projects.",
      },
      {
        question: "What kind of asset works well here?",
        answer:
          "A representative image, approved portrait, or other reusable visual that should anchor how the character looks over time.",
      },
      {
        question: "Can a character exist without an attached asset?",
        answer:
          "Yes. Latted still supports character records based on descriptive fields alone, but an asset can make them more stable.",
      },
      {
        question: "Why is this better than describing the look every time?",
        answer:
          "Because repeated text descriptions drift, while a linked reference gives the workflow a clearer visual anchor.",
      },
      {
        question: "Who benefits most from this?",
        answer:
          "Teams using recurring spokespersons, creator personas, or educational hosts benefit the most because consistency matters across many outputs.",
      },
    ],
  }),
  createBlogPost({
    slug: "edit-character-personality-and-appearance-in-latted",
    title:
      "Why Editing Character Personality and Appearance Improves AI Video Consistency",
    seoTitle:
      "Why Editing Character Personality and Appearance Improves AI Video Consistency in 2026",
    description:
      "Learn why Latted exposes editable personality and appearance fields for reusable characters so teams can refine persona context without rebuilding the character record.",
    excerpt:
      "A practical guide to maintaining reusable character context over time.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 6,
    tags: ["Characters", "Consistency", "AI Video", "Latted", "Workflow"],
    keywords: [
      "edit character personality",
      "edit character appearance",
      "Latted character detail page",
      "reusable persona refinement",
      "AI video character consistency workflow",
    ],
    mdxPublicPath: "/blog/edit-character-personality-and-appearance-in-latted.mdx",
    llmSummary:
      "Explains why Latted lets teams edit character personality and appearance fields over time so recurring personas stay useful and consistent without being recreated.",
    faqItems: [
      {
        question: "Why edit a character after creating it?",
        answer:
          "Because recurring personas usually become clearer over time, and updating the record keeps future projects aligned with that improved understanding.",
      },
      {
        question: "What should the personality field capture?",
        answer:
          "It should capture tone, traits, and behavioral cues that help the persona feel consistent across future outputs.",
      },
      {
        question: "What should the appearance field capture?",
        answer:
          "It should capture the physical details that help the team preserve a recognizable on-screen identity.",
      },
      {
        question: "Why not just save a new character instead?",
        answer:
          "Because editing the same reusable record preserves continuity and prevents the library from filling with near-duplicate personas.",
      },
      {
        question: "Who benefits from this workflow most?",
        answer:
          "Teams running recurring creator, spokesperson, or character-led formats benefit the most because they need stable personas over time.",
      },
    ],
  }),
  createBlogPost({
    slug: "attach-assets-and-characters-to-latted-automations",
    title: "How to Attach Assets and Characters to an Automation in Latted",
    seoTitle:
      "How to Attach Assets and Characters to an Automation in Latted in 2026",
    description:
      "See how Latted lets teams attach reusable assets and characters to an automation so ideation starts with stronger brand and persona context than prompt text alone.",
    excerpt:
      "A practical guide to grounding Latted automations with reusable context.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 7,
    tags: ["Automations", "Characters", "Assets", "Latted", "Workflow"],
    keywords: [
      "automation attachments",
      "attach assets to automation",
      "attach characters to automation",
      "Latted automation context",
      "grounded ideation workflow",
    ],
    mdxPublicPath: "/blog/attach-assets-and-characters-to-latted-automations.mdx",
    llmSummary:
      "Explains how Latted lets teams attach reusable assets and characters to automations so ideation runs start from structured brand and persona context.",
    faqItems: [
      {
        question: "What does attaching assets to an automation do?",
        answer:
          "It gives the automation reusable product or visual references so the ideation run starts with more specific context.",
      },
      {
        question: "What does attaching characters do?",
        answer:
          "It gives the automation reusable persona context, including the kind of character or spokesperson the content should keep reflecting.",
      },
      {
        question: "Why attach both assets and characters?",
        answer:
          "Because one defines the visual world and the other defines the recurring persona, and many strong content systems need both.",
      },
      {
        question: "Can I search before selecting attachments?",
        answer:
          "Yes. The attachment workflow supports searching existing assets and characters so larger libraries stay usable.",
      },
      {
        question: "When is this most valuable?",
        answer:
          "It is most valuable for recurring campaign, UGC, or brand systems where the automation should keep ideating within a stable creative frame.",
      },
    ],
  }),
  createBlogPost({
    slug: "choose-a-schedule-for-latted-automations",
    title: "How to Choose the Right Schedule for a Latted Automation",
    seoTitle:
      "How to Choose the Right Schedule for a Latted Automation in 2026",
    description:
      "Learn when to use manual-only, daily, weekdays, or weekly schedules in Latted automations based on review capacity, budget, and publishing cadence.",
    excerpt:
      "A practical scheduling guide for recurring Latted automations.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 6,
    tags: ["Automations", "Scheduling", "AI Video", "Latted", "Workflow"],
    keywords: [
      "Latted automation schedule",
      "daily vs weekly automation",
      "weekdays automation cadence",
      "manual only automation workflow",
      "content automation scheduling",
    ],
    mdxPublicPath: "/blog/choose-a-schedule-for-latted-automations.mdx",
    llmSummary:
      "Explains how to choose between manual-only, daily, weekdays, and weekly Latted automation schedules based on review capacity, budget, and cadence.",
    faqItems: [
      {
        question: "What automation schedules does Latted support?",
        answer:
          "Latted supports manual only, daily, weekdays, and weekly schedules.",
      },
      {
        question: "When should I use a manual-only automation?",
        answer:
          "Use manual only when you are still testing the workflow or want full control over every run.",
      },
      {
        question: "Why would weekdays be better than daily?",
        answer:
          "Weekdays are better when a team wants recurring output during working days but does not want unattended weekend runs.",
      },
      {
        question: "Is weekly too slow for content automation?",
        answer:
          "Not necessarily. Weekly is often the right cadence when each run produces enough material for a whole planning cycle.",
      },
      {
        question: "Can I save an automation before deciding on a schedule?",
        answer:
          "Yes. Latted lets you save the workflow configuration first and choose a recurring schedule later.",
      },
    ],
  }),
  createBlogPost({
    slug: "pause-and-resume-latted-automation-schedules",
    title:
      "How to Pause and Resume Latted Automation Schedules Without Losing the Workflow",
    seoTitle:
      "How to Pause and Resume Latted Automation Schedules Without Losing the Workflow in 2026",
    description:
      "See how Latted lets teams pause or resume a scheduled automation without deleting its prompt, settings, attachments, or run history.",
    excerpt:
      "A practical guide to controlling recurring automations without rebuilding them.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 6,
    tags: ["Automations", "Scheduling", "Operations", "Latted", "Workflow"],
    keywords: [
      "pause automation schedule",
      "resume automation schedule",
      "Latted automation status",
      "scheduled workflow control",
      "recurring automation operations",
    ],
    mdxPublicPath: "/blog/pause-and-resume-latted-automation-schedules.mdx",
    llmSummary:
      "Explains how Latted lets teams pause and resume scheduled automations while preserving the prompt, settings, schedule value, attachments, and run history.",
    faqItems: [
      {
        question: "Does pausing an automation delete it?",
        answer:
          "No. Pausing keeps the automation and its settings, but stops the recurring schedule from continuing.",
      },
      {
        question: "What stays saved after I pause a schedule?",
        answer:
          "The prompt, settings, schedule value, attachments, and run history all remain associated with the automation.",
      },
      {
        question: "Can I resume the same automation later?",
        answer:
          "Yes. Latted supports resuming a paused scheduled automation from the detail page.",
      },
      {
        question: "Why not just delete and recreate the automation?",
        answer:
          "That approach breaks continuity and makes recurring workflows harder to track. Pause/resume is cleaner when the automation still has value.",
      },
      {
        question: "Can I still run the automation manually?",
        answer:
          "Yes. Recurring schedule control and manual execution are separate decisions in the product.",
      },
    ],
  }),
  createBlogPost({
    slug: "review-latted-automation-runs-from-ideas-to-projects",
    title: "How to Review Latted Automation Runs From Ideas to Projects",
    seoTitle:
      "How to Review Latted Automation Runs From Ideas to Projects in 2026",
    description:
      "Learn how Latted's automation run view surfaces live ideation events, structured ideas, generated projects, and direct handoff into the editor.",
    excerpt:
      "A practical guide to reviewing one automation run from live ideation to project handoff.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 7,
    tags: ["Automations", "Review Workflow", "AI Video", "Latted", "Workflow"],
    keywords: [
      "automation run review",
      "Latted run detail page",
      "live ideation events",
      "automation ideas to projects",
      "AI video automation handoff",
    ],
    mdxPublicPath: "/blog/review-latted-automation-runs-from-ideas-to-projects.mdx",
    llmSummary:
      "Explains how Latted's automation run page lets teams review live ideation events, structured ideas, generated projects, and direct handoff into the project editor.",
    faqItems: [
      {
        question: "What is an automation run in Latted?",
        answer:
          "It is one execution of an automation, including ideation, generation, results, and any projects created during that cycle.",
      },
      {
        question: "Does Latted show live activity while the run is happening?",
        answer:
          "Yes. The run detail view can show realtime ideation events while the automation is still working.",
      },
      {
        question: "Can I review ideas before opening the projects?",
        answer:
          "Yes. Latted surfaces saved ideas separately from the generated projects so you can review concept quality first.",
      },
      {
        question: "Can I preview generated projects from the run page?",
        answer:
          "Yes. The run page includes project cards and a preview modal with metadata and visual output when available.",
      },
      {
        question: "What happens after I find a project I want to keep working on?",
        answer:
          "You can open it directly in the Latted editor and continue refining it like any other project.",
      },
    ],
  }),
  createBlogPost({
    slug: "fast-vs-quality-automation-tiers-in-latted",
    title: "How to Choose Fast vs Quality Automation Tiers in Latted",
    seoTitle:
      "How to Choose Fast vs Quality Automation Tiers in Latted in 2026",
    description:
      "See when Latted's Fast or Quality automation tier is the better default based on whether the goal is throughput, exploration, or stronger first-pass polish.",
    excerpt:
      "A practical guide to choosing the right automation tier for recurring content systems.",
    publishedAt: "2026-04-13",
    readingTimeMinutes: 6,
    tags: ["Automations", "Model Selection", "AI Video", "Latted", "Workflow"],
    keywords: [
      "fast vs quality automation",
      "Latted model tier",
      "automation generation defaults",
      "quality speed tradeoff AI video",
      "recurring content model settings",
    ],
    mdxPublicPath: "/blog/fast-vs-quality-automation-tiers-in-latted.mdx",
    llmSummary:
      "Explains how Latted's Fast and Quality automation tiers support different recurring-content goals, from high-volume exploration to stronger first-pass polish.",
    faqItems: [
      {
        question: "What is the Fast tier in Latted automations?",
        answer:
          "It is the automation setting intended for quicker generation throughput, which is useful for exploration, testing, and broader idea coverage.",
      },
      {
        question: "What is the Quality tier in Latted automations?",
        answer:
          "It is the automation setting intended for stronger first-pass output quality, which is useful when each generated project should start closer to production standards.",
      },
      {
        question: "Should every automation use Quality?",
        answer:
          "No. Many teams benefit from Fast during exploration and only move to Quality when the workflow becomes more deliberate and repeatable.",
      },
      {
        question: "Why save the tier on the automation itself?",
        answer:
          "Because recurring workflows need stable defaults. Saving the tier on the automation makes each run more predictable.",
      },
      {
        question: "Is model tier the only important generation setting?",
        answer:
          "No. It works together with style and aspect ratio, which also shape what each automation run produces.",
      },
    ],
  }),
  createBlogPost({
    slug: "what-kinds-of-videos-can-latted-make",
    contentType: "Examples guide",
    title:
      "What Kinds of Videos Can Latted Make? Examples Across Fashion, Apps, Web, and Entertainment",
    seoTitle:
      "What Kinds of Videos Can Latted Make? Examples Across Fashion, Apps, Web, and Entertainment in 2026",
    description:
      "See the kinds of AI videos Latted showcases across fashion, wellness, mobile apps, web products, and entertainment so you can judge whether the product fits your category before starting a project.",
    excerpt:
      "A category-by-category guide to the visible output range in Latted's examples gallery.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 7,
    tags: ["Examples", "Product Overview", "AI Video", "Latted", "Use Cases"],
    keywords: [
      "what kinds of videos can Latted make",
      "Latted examples gallery",
      "AI video examples by industry",
      "fashion app web entertainment AI videos",
      "Latted use cases",
    ],
    mdxPublicPath: "/blog/what-kinds-of-videos-can-latted-make.mdx",
    llmSummary:
      "Explains the visible range of Latted's AI-video output by walking through example categories like fashion, wellness, mobile apps, web products, and entertainment.",
    faqItems: [
      {
        question: "What kinds of videos does Latted show in its examples gallery?",
        answer:
          "Latted shows examples across categories like fashion, health and wellness, mobile apps, web products, and entertainment.",
      },
      {
        question: "Why do example categories matter when evaluating an AI video tool?",
        answer:
          "They help users judge whether the product already produces the kind of output their team actually needs.",
      },
      {
        question: "Does Latted only focus on one video style?",
        answer:
          "No. The examples page shows several distinct creative categories instead of one narrow format.",
      },
      {
        question: "Should I look at examples before starting a project?",
        answer:
          "Yes. Examples can help you choose a workflow, set expectations, and gather references before you prompt the first draft.",
      },
      {
        question: "What is the main takeaway from Latted's examples page?",
        answer:
          "The main takeaway is that Latted positions itself as a broader AI-video system, not just a single-purpose generator.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-an-ai-video-examples-gallery-matters-before-you-start",
    contentType: "Examples guide",
    title: "Why an AI Video Examples Gallery Matters Before You Start a Project",
    seoTitle:
      "Why an AI Video Examples Gallery Matters Before You Start a Project in 2026",
    description:
      "Learn why Latted's examples gallery is a high-signal product surface for setting expectations, building prompt references, and deciding whether the tool matches your creative category before you generate anything.",
    excerpt:
      "A practical guide to using product examples as proof, reference, and planning input before prompting.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 6,
    tags: ["Examples", "Evaluation", "AI Video", "Latted", "Workflow"],
    keywords: [
      "AI video examples gallery",
      "why examples matter before prompting",
      "Latted examples workflow",
      "evaluate AI video tool with examples",
      "creative reference before video generation",
    ],
    mdxPublicPath: "/blog/why-an-ai-video-examples-gallery-matters-before-you-start.mdx",
    llmSummary:
      "Explains why Latted's examples gallery is useful before project creation because it gives users proof, expectation-setting, and prompt reference material in one place.",
    faqItems: [
      {
        question: "Why should I review examples before creating an AI video project?",
        answer:
          "Because examples help you understand output quality, style range, and whether the product fits your category before you spend time prompting.",
      },
      {
        question: "Are examples only useful for new users?",
        answer:
          "No. They also help experienced teams gather references, align stakeholders, and reset expectations for new campaigns.",
      },
      {
        question: "What should I look for in an examples gallery?",
        answer:
          "Look for category fit, pacing, aspect ratio, product visibility, and whether the examples resemble the work your team wants to produce.",
      },
      {
        question: "How does an examples page reduce project friction?",
        answer:
          "It gives users a shared visual starting point, which makes workflow choice and prompt direction easier.",
      },
      {
        question: "What makes Latted's examples page valuable?",
        answer:
          "It groups AI-generated outputs into recognizable categories, which makes the product easier to evaluate quickly.",
      },
    ],
  }),
  createBlogPost({
    slug: "use-industry-examples-to-plan-your-first-latted-project",
    contentType: "Examples guide",
    title: "How to Use Industry Examples to Plan Your First Latted Project",
    seoTitle:
      "How to Use Industry Examples to Plan Your First Latted Project in 2026",
    description:
      "See how Latted's industry-specific example videos can help you choose the right workflow, prompt language, aspect ratio, and creative benchmark before you start your first project.",
    excerpt:
      "A step-by-step planning guide for turning example videos into a better first Latted brief.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 7,
    tags: ["Examples", "Planning", "AI Video", "Latted", "First Project"],
    keywords: [
      "use industry examples before AI video project",
      "plan first Latted project",
      "AI video reference workflow",
      "choose aspect ratio from examples",
      "example-driven prompt planning",
    ],
    mdxPublicPath: "/blog/use-industry-examples-to-plan-your-first-latted-project.mdx",
    llmSummary:
      "Shows how users can turn Latted's industry-specific example videos into practical planning inputs for workflow choice, prompt direction, and first-project scope.",
    faqItems: [
      {
        question: "How can examples help me plan my first Latted project?",
        answer:
          "They can help you choose a workflow, set aspect ratio expectations, define pacing, and gather clearer visual references before you prompt.",
      },
      {
        question: "Should I copy an example exactly?",
        answer:
          "No. Use examples as planning input and quality benchmarks, not as a reason to make every project look identical.",
      },
      {
        question: "What should I write down while reviewing examples?",
        answer:
          "Write down the category, aspect ratio, pacing, product visibility, and the overall feeling you want to preserve.",
      },
      {
        question: "Can examples help with stakeholder alignment?",
        answer:
          "Yes. They make it easier for a team to agree on what a good first draft should resemble.",
      },
      {
        question: "What is the main planning benefit of examples?",
        answer:
          "They turn abstract creative goals into visible references that make the first project easier to scope.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-100-percent-ai-examples-build-trust-in-ai-video-tools",
    contentType: "Examples guide",
    title:
      "Why \"100% AI, No Human Editing\" Examples Build Trust in an AI Video Tool",
    seoTitle:
      "Why \"100% AI, No Human Editing\" Examples Build Trust in an AI Video Tool in 2026",
    description:
      "Learn why Latted's explicit \"100% AI\" examples message matters when buyers want proof that showcased outputs came from the product rather than from hidden manual post-production.",
    excerpt:
      "A practical trust-building guide for evaluating whether example videos really reflect the product.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 6,
    tags: ["Examples", "Trust", "AI Video", "Latted", "Evaluation"],
    keywords: [
      "100% AI examples",
      "no human editing AI video examples",
      "trust AI video tool examples",
      "Latted examples credibility",
      "evaluate AI video proof",
    ],
    mdxPublicPath:
      "/blog/why-100-percent-ai-examples-build-trust-in-ai-video-tools.mdx",
    llmSummary:
      "Explains why Latted's claim that its examples are 100% AI with no human editing matters for buyer trust and more honest product evaluation.",
    faqItems: [
      {
        question: "Why do “100% AI” examples matter?",
        answer:
          "They help users evaluate what the product itself can do instead of wondering how much hidden manual work shaped the final result.",
      },
      {
        question: "Why is “no human editing” an important detail?",
        answer:
          "Because many example reels look better than what users can reproduce if manual cleanup happened off-screen.",
      },
      {
        question: "What trust problem do honest examples solve?",
        answer:
          "They reduce uncertainty about whether the showcased output is representative of the product.",
      },
      {
        question: "Does this mean examples should replace a real trial?",
        answer:
          "No. Examples should build trust and set expectations, but a hands-on workflow is still the best proof.",
      },
      {
        question: "What is the value of Latted's examples page in this context?",
        answer:
          "It gives buyers a more transparent way to inspect outputs before they decide whether to try the product.",
      },
    ],
  }),
  createBlogPost({
    slug: "upload-a-video-to-brain-explorer-before-you-recut-it",
    contentType: "Analysis guide",
    title: "Why You Should Upload a Video to Brain Explorer Before You Recut It",
    seoTitle:
      "Why You Should Upload a Video to Brain Explorer Before You Recut It in 2026",
    description:
      "Learn why Latted Brain Explorer is useful before you re-edit or repurpose an existing clip, especially when you want time-based engagement signals before making new cuts or variants.",
    excerpt:
      "A practical guide to analyzing an existing video before you spend time recutting it.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 7,
    tags: ["Brain Explorer", "Video Analysis", "AI Video", "Latted", "Pre-Production"],
    keywords: [
      "upload video to Brain Explorer",
      "analyze video before editing",
      "Latted Brain Explorer video workflow",
      "engagement analysis before recut",
      "pre edit video analysis",
    ],
    mdxPublicPath: "/blog/upload-a-video-to-brain-explorer-before-you-recut-it.mdx",
    llmSummary:
      "Explains why uploading an existing clip to Latted Brain Explorer can be useful before recutting, repurposing, or making new variants from the same video.",
    faqItems: [
      {
        question: "Why analyze a video before recutting it?",
        answer:
          "Because analysis can help you see weak or strong moments before you spend time making new edits or variants.",
      },
      {
        question: "What does Brain Explorer show for uploaded video?",
        answer:
          "It can show time-based engagement data, segment information, synchronized playback, and a visual response interface.",
      },
      {
        question: "Who benefits most from this workflow?",
        answer:
          "Teams repurposing clips, improving intros, testing pacing, or deciding which version should move forward benefit the most.",
      },
      {
        question: "Is Brain Explorer only useful before making a brand-new video?",
        answer:
          "No. It is also useful when you already have a clip and want a more structured way to review it.",
      },
      {
        question: "What is the main benefit of uploading an existing video first?",
        answer:
          "It gives you diagnostic feedback before you spend more editing effort on the wrong changes.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-brain-explorer-segments-help-diagnose-video-pacing",
    contentType: "Analysis guide",
    title: "How Brain Explorer Segments Help Diagnose Video Pacing",
    seoTitle:
      "How Brain Explorer Segments Help Diagnose Video Pacing in 2026",
    description:
      "See how Latted Brain Explorer's time-based segments can help teams locate pacing problems more precisely instead of treating a weak video as one undifferentiated problem.",
    excerpt:
      "A practical guide to using segment-level analysis to diagnose where a video loses momentum.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 6,
    tags: ["Brain Explorer", "Pacing", "AI Video", "Latted", "Analysis"],
    keywords: [
      "Brain Explorer segments",
      "diagnose video pacing",
      "segment level engagement analysis",
      "Latted pacing workflow",
      "find weak moments in video",
    ],
    mdxPublicPath:
      "/blog/how-brain-explorer-segments-help-diagnose-video-pacing.mdx",
    llmSummary:
      "Explains how Brain Explorer's time-based segments help Latted users diagnose pacing issues by locating where a video starts to weaken instead of treating the whole clip equally.",
    faqItems: [
      {
        question: "What are Brain Explorer segments?",
        answer:
          "They are time-based portions of the analysis that help users inspect how a video changes from one moment to the next.",
      },
      {
        question: "Why are segments useful for pacing analysis?",
        answer:
          "Because pacing problems usually happen in specific moments, not across every second of the video equally.",
      },
      {
        question: "What can segment-level review help me decide?",
        answer:
          "It can help you decide where to trim, rewrite, reorder, or strengthen the video.",
      },
      {
        question: "Is a whole-video score enough for pacing review?",
        answer:
          "No. Whole-video scores are helpful summaries, but segment-level review is better for locating the actual problem.",
      },
      {
        question: "What is the main benefit of segmented analysis?",
        answer:
          "It turns vague feedback like “the middle drags” into a more actionable editing conversation.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-engagement-scores-help-compare-video-hooks",
    contentType: "Analysis guide",
    title:
      "How Engagement Scores Help You Compare Video Hooks Before You Make Variants",
    seoTitle:
      "How Engagement Scores Help You Compare Video Hooks Before You Make Variants in 2026",
    description:
      "Learn how Latted Brain Explorer's engagement scoring can help teams compare hooks, intros, and opening moments before they commit to multiple derivative edits or campaign variants.",
    excerpt:
      "A practical guide to using engagement scores as one input when choosing stronger opening moments.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 6,
    tags: ["Brain Explorer", "Hooks", "AI Video", "Latted", "Pre-Production"],
    keywords: [
      "engagement score video hook",
      "compare video hooks",
      "Latted Brain Explorer engagement score",
      "hook testing before variants",
      "short form intro analysis",
    ],
    mdxPublicPath:
      "/blog/how-engagement-scores-help-compare-video-hooks.mdx",
    llmSummary:
      "Explains how Brain Explorer engagement scores can help Latted users compare hooks and intros before they create additional video variants from the same idea.",
    faqItems: [
      {
        question: "What is the engagement score in Brain Explorer?",
        answer:
          "It is a simplified 0 to 100 view that helps users compare how stimulating different moments of the content may be over time.",
      },
      {
        question: "Why focus on hooks and intros first?",
        answer:
          "Because the opening seconds often determine whether the rest of the video gets a chance to matter.",
      },
      {
        question: "Should an engagement score replace creative judgment?",
        answer: "No. It should support judgment, not replace it.",
      },
      {
        question: "When is hook comparison most useful?",
        answer:
          "It is most useful before you make many variants, because it helps you choose a stronger base direction first.",
      },
      {
        question: "What is the main benefit of score-based hook review?",
        answer:
          "It makes opening-moment decisions more structured and less dependent on vague reactions alone.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-video-and-text-analysis-in-one-interface-matters",
    contentType: "Analysis guide",
    title: "Why Video and Text Analysis in One Interface Matters for Pre-Production",
    seoTitle:
      "Why Video and Text Analysis in One Interface Matters for Pre-Production in 2026",
    description:
      "See why Latted Brain Explorer's support for both video upload and text input matters when teams sometimes start from a script and other times start from an existing clip.",
    excerpt:
      "A practical guide to why one analysis surface for both text and video creates a stronger pre-production workflow.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 6,
    tags: ["Brain Explorer", "Text Analysis", "Video Analysis", "Latted", "Workflow"],
    keywords: [
      "video and text analysis in one interface",
      "Latted Brain Explorer text and video",
      "pre production analysis workflow",
      "analyze script and video in same tool",
      "Brain Explorer input modes",
    ],
    mdxPublicPath:
      "/blog/why-video-and-text-analysis-in-one-interface-matters.mdx",
    llmSummary:
      "Explains why Latted Brain Explorer supporting both video and text in one interface matters for teams whose pre-production work starts from different kinds of source material.",
    faqItems: [
      {
        question: "Why does it matter that Brain Explorer supports both video and text?",
        answer:
          "Because teams do not always start from the same kind of source material, and one analysis surface keeps that workflow more consistent.",
      },
      {
        question: "When would I analyze text instead of video?",
        answer:
          "Analyze text when you are still evaluating a script, hook, concept, or demo narrative before recording or generation.",
      },
      {
        question: "When would I analyze video instead of text?",
        answer:
          "Analyze video when you already have a draft clip and want to inspect pacing, hooks, or moment-by-moment strength.",
      },
      {
        question: "Why not use separate tools for text and video review?",
        answer:
          "Separate tools often fragment the workflow and make it harder to compare decisions across stages.",
      },
      {
        question: "What is the main product benefit of one interface?",
        answer:
          "It keeps pre-production analysis closer to the rest of the Latted workflow instead of scattering it across disconnected tools.",
      },
    ],
  }),
  createBlogPost({
    slug: "analyze-a-product-demo-script-before-you-record-the-video",
    contentType: "Analysis guide",
    title: "How to Analyze a Product Demo Script Before You Record the Video",
    seoTitle:
      "How to Analyze a Product Demo Script Before You Record the Video in 2026",
    description:
      "Learn how Latted Brain Explorer's text-input workflow can help product teams evaluate a demo script, opening hook, or explainer structure before they record footage or generate a final video.",
    excerpt:
      "A practical pre-production guide for testing product-demo scripts before committing to video production.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 7,
    tags: ["Brain Explorer", "Product Marketing", "Text Analysis", "Latted", "Pre-Production"],
    keywords: [
      "analyze product demo script",
      "test script before recording video",
      "Latted Brain Explorer product marketing",
      "text analysis for explainer script",
      "pre production demo script review",
    ],
    mdxPublicPath:
      "/blog/analyze-a-product-demo-script-before-you-record-the-video.mdx",
    llmSummary:
      "Shows how product and marketing teams can use Latted Brain Explorer to evaluate a product-demo script before recording footage or generating the final video.",
    faqItems: [
      {
        question: "Why analyze a product demo script before recording?",
        answer:
          "Because it is easier and cheaper to improve the narrative while it is still text than after you have already produced video.",
      },
      {
        question: "What kind of text should I test first?",
        answer:
          "Start with the hook, opening problem statement, product explanation, and call to action.",
      },
      {
        question: "Who benefits most from this workflow?",
        answer:
          "Product marketers, founders, growth teams, and anyone producing recurring demo or launch videos benefit the most.",
      },
      {
        question: "Does text analysis replace recording and editing?",
        answer:
          "No. It helps you arrive at those stages with a stronger script and clearer intent.",
      },
      {
        question: "What is the main benefit of script-first analysis?",
        answer:
          "It helps teams spend production effort on better narratives instead of polishing weak messaging.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-pre-production-analysis-helps-before-you-automate-social-video-production",
    contentType: "Analysis guide",
    title:
      "Why Pre-Production Analysis Helps Before You Automate Social Video Production",
    seoTitle:
      "Why Pre-Production Analysis Helps Before You Automate Social Video Production in 2026",
    description:
      "See why Latted Brain Explorer can improve recurring social-video systems by helping teams test hooks, scripts, and pacing before those ideas feed larger automation and reuse workflows.",
    excerpt:
      "A practical guide to using analysis before you scale recurring social-video workflows.",
    publishedAt: "2026-04-15",
    readingTimeMinutes: 7,
    tags: ["Brain Explorer", "Automations", "Social Video", "Latted", "Workflow"],
    keywords: [
      "pre production analysis before automation",
      "Brain Explorer for recurring content",
      "social video analysis workflow",
      "Latted automation planning",
      "test hooks before automation",
    ],
    mdxPublicPath:
      "/blog/why-pre-production-analysis-helps-before-you-automate-social-video-production.mdx",
    llmSummary:
      "Explains why pre-production analysis with Latted Brain Explorer is useful before teams scale recurring social-video workflows through automation, templates, or reuse.",
    faqItems: [
      {
        question: "Why analyze ideas before automating social video production?",
        answer:
          "Because automation scales whatever you feed it, including weak hooks or weak scripts.",
      },
      {
        question: "How can Brain Explorer help recurring content systems?",
        answer:
          "It can help teams evaluate scripts, hooks, pacing, or existing clips before they decide which ideas deserve repetition.",
      },
      {
        question: "Does Brain Explorer automatically publish social posts?",
        answer:
          "No. Its value here is earlier in the workflow, where it helps teams choose stronger creative directions before scaling them.",
      },
      {
        question: "Who benefits most from analysis before automation?",
        answer:
          "Teams making recurring ads, social promos, launch clips, or other repeated formats benefit the most.",
      },
      {
        question: "What is the main benefit of this workflow?",
        answer:
          "It reduces the risk of scaling weak creative assumptions across a recurring content system.",
      },
    ],
  }),
  createBlogPost({
    slug: "how-latted-turns-a-saved-automation-into-a-reusable-content-playbook",
    contentType: "Automation guide",
    title: "How Latted Turns a Saved Automation Into a Reusable Content Playbook",
    seoTitle: "How Latted Turns a Saved Automation Into a Reusable Content Playbook in 2026",
    description: "See how Latted turns a saved automation into a reusable content playbook by preserving the prompt, generation defaults, attachments, schedule, and run history in one operational workflow.",
    excerpt: "A practical guide to turning one good recurring workflow into a repeatable production system.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 7,
    tags:     [
      "Automations",
      "Content Ops",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "automation content playbook",
      "Latted reusable automation",
      "recurring AI video workflow",
      "saved automation playbook",
      "content ops automation setup",
    ],
    mdxPublicPath: "/blog/how-latted-turns-a-saved-automation-into-a-reusable-content-playbook.mdx",
    llmSummary: "Explains how Latted turns a saved automation into a reusable content playbook by keeping prompt, settings, attachments, schedule, and run history in one reusable system.",
    faqItems:     [
      {
        question: "What makes a saved automation a playbook instead of just a prompt?",
        answer: "It keeps the prompt together with creative count, generation defaults, attachments, schedule, and past runs so the workflow is operational, not just descriptive.",
      },
      {
        question: "Why is a reusable playbook useful for content teams?",
        answer: "Because recurring content gets easier to repeat when the setup already captures what to make, how to make it, and how past runs behaved.",
      },
      {
        question: "Can I still edit a saved automation later?",
        answer: "Yes. Latted exposes edit controls so the playbook can evolve as the campaign or channel changes.",
      },
      {
        question: "Does a playbook only help scheduled automations?",
        answer: "No. It also helps manual-run workflows because the saved setup still reduces repeated configuration work.",
      },
      {
        question: "What is the main benefit of this pattern?",
        answer: "The main benefit is that strong recurring workflows become easier to reuse, inspect, and improve over time.",
      },
    ],
  }),
  createBlogPost({
    slug: "search-and-status-views-for-latted-automations",
    contentType: "Automation guide",
    title: "Why Search and Status Views Matter in Latted's Automations Dashboard",
    seoTitle: "Why Search and Status Views Matter in Latted's Automations Dashboard in 2026",
    description: "Learn how Latted's automations dashboard uses search, status labels, schedules, and run counts to help teams manage draft, active, paused, and archived content workflows without confusion.",
    excerpt: "A practical guide to finding and managing the right recurring workflow once automation volume grows.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 6,
    tags:     [
      "Automations",
      "Dashboard",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "automation dashboard search",
      "Latted automation statuses",
      "draft active paused archived automation",
      "manage recurring content workflows",
      "automation run count dashboard",
    ],
    mdxPublicPath: "/blog/search-and-status-views-for-latted-automations.mdx",
    llmSummary: "Explains how Latted's automations dashboard uses search, status labels, schedules, and run counts so teams can manage recurring workflows without opening every automation one by one.",
    faqItems:     [
      {
        question: "What does the automations dashboard help me do?",
        answer: "It helps you find the right automation quickly and understand whether it is draft, active, paused, or archived before you open it.",
      },
      {
        question: "Why do status labels matter in an automation dashboard?",
        answer: "They make it easier to separate workflows that are live from workflows that are still being built, intentionally paused, or no longer in active use.",
      },
      {
        question: "What does search cover in Latted automations?",
        answer: "The search flow helps users find automations by name or prompt text so recurring workflows stay discoverable as the list grows.",
      },
      {
        question: "Why are run counts useful on list cards?",
        answer: "They add fast operational context by showing whether an automation is still theoretical or already has execution history.",
      },
      {
        question: "Who benefits most from this dashboard view?",
        answer: "Teams running several recurring content systems benefit the most because they need quick visibility before deeper review.",
      },
    ],
  }),
  createBlogPost({
    slug: "test-latted-automation-ideas-before-you-turn-on-a-schedule",
    contentType: "Automation guide",
    title: "Why You Should Test Latted Automation Ideas Before You Turn On a Schedule",
    seoTitle: "Why You Should Test Latted Automation Ideas Before You Turn On a Schedule in 2026",
    description: "See why Latted's automation ideation test step matters before scheduling recurring runs, especially when teams want to inspect prompts, attachments, and live idea quality before they scale a workflow.",
    excerpt: "A practical guide to validating recurring automations before they start firing on a cadence.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 7,
    tags:     [
      "Automations",
      "Ideation",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "test automation ideation",
      "Latted automation test workflow",
      "validate automation before schedule",
      "automation idea quality check",
      "recurring content automation testing",
    ],
    mdxPublicPath: "/blog/test-latted-automation-ideas-before-you-turn-on-a-schedule.mdx",
    llmSummary: "Explains why Latted lets teams test automation ideation before enabling a schedule so they can inspect live idea quality, attachments, and prompt direction before recurring runs begin.",
    faqItems:     [
      {
        question: "What does testing automation ideation mean in Latted?",
        answer: "It means running the ideation step first so you can inspect the prompt output, worklog, and saved ideas before you rely on a recurring schedule.",
      },
      {
        question: "Why test before enabling a schedule?",
        answer: "Because recurring automation should scale a good workflow, not an unverified one.",
      },
      {
        question: "What should I look for during the test?",
        answer: "You should check whether the ideas, hooks, and angles match the intended audience, product context, and attached references.",
      },
      {
        question: "Why do stop and reset controls matter here?",
        answer: "They make experimentation safer because you can interrupt a weak direction and restart without pretending the first attempt was good enough.",
      },
      {
        question: "When is an automation ready for a schedule?",
        answer: "It is ready when the tested ideation quality is strong enough that repeating it automatically feels sensible.",
      },
    ],
  }),
  createBlogPost({
    slug: "manual-runs-vs-scheduled-runs-in-latted-automations",
    contentType: "Automation guide",
    title: "Manual Runs vs Scheduled Runs in Latted Automations",
    seoTitle: "Manual Runs vs Scheduled Runs in Latted Automations in 2026",
    description: "Compare manual runs and scheduled runs in Latted automations so teams can decide when to fire a recurring workflow on demand and when to let a saved cadence handle it automatically.",
    excerpt: "A practical decision guide for on-demand execution versus recurring cadence in automation workflows.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 6,
    tags:     [
      "Automations",
      "Scheduling",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "manual vs scheduled automation",
      "run automation on demand",
      "Latted automation schedule guide",
      "recurring AI video cadence",
      "automation execution modes",
    ],
    mdxPublicPath: "/blog/manual-runs-vs-scheduled-runs-in-latted-automations.mdx",
    llmSummary: "Explains the difference between manual runs and scheduled runs in Latted automations so teams can choose whether a workflow should execute on demand, on a cadence, or both.",
    faqItems:     [
      {
        question: "What is a manual run in Latted automations?",
        answer: "A manual run is an on-demand execution that you trigger directly when you want the automation to work right now.",
      },
      {
        question: "What is a scheduled run?",
        answer: "A scheduled run is an execution that follows a saved cadence, such as daily or weekdays, without requiring a fresh manual trigger each time.",
      },
      {
        question: "When are manual runs better?",
        answer: "Manual runs are better when timing depends on launches, approvals, or ad hoc creative needs instead of a fixed rhythm.",
      },
      {
        question: "When are scheduled runs better?",
        answer: "Scheduled runs are better when the workflow truly repeats and the team wants automation to handle that repetition automatically.",
      },
      {
        question: "Can one automation support both patterns?",
        answer: "Yes. A saved automation can be run manually even if it also has a schedule, which makes the workflow more flexible.",
      },
    ],
  }),
  createBlogPost({
    slug: "review-a-latted-automation-detail-page-before-running-it-again",
    contentType: "Automation guide",
    title: "What to Review on a Latted Automation Detail Page Before You Run It Again",
    seoTitle: "What to Review on a Latted Automation Detail Page Before You Run It Again in 2026",
    description: "Learn what Latted's automation detail page reveals before you rerun a workflow, including prompt, creative count, aspect ratio, style, model, attachments, schedule, and prior runs.",
    excerpt: "A practical checklist for reviewing an automation before you rerun, edit, pause, or delete it.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 7,
    tags:     [
      "Automations",
      "Review Workflow",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "automation detail page review",
      "Latted rerun automation checklist",
      "review automation config before rerun",
      "automation settings summary",
      "automation run inspection",
    ],
    mdxPublicPath: "/blog/review-a-latted-automation-detail-page-before-running-it-again.mdx",
    llmSummary: "Explains what Latted's automation detail page shows before a rerun, including prompt, creative count, generation settings, attachments, schedule, and prior run history.",
    faqItems:     [
      {
        question: "What should I check before rerunning an automation?",
        answer: "You should review the prompt, creative count, aspect ratio, style, model tier, attachments, schedule, and recent run history.",
      },
      {
        question: "Why not just press run again immediately?",
        answer: "Because recurring workflows are safer when you confirm the saved configuration still matches the current campaign goal.",
      },
      {
        question: "What do past runs help me decide?",
        answer: "They help you judge whether the automation is behaving well enough to rerun as-is or whether it should be edited first.",
      },
      {
        question: "Can the detail page help with schedule decisions too?",
        answer: "Yes. It shows the current status and saved schedule so you can decide whether to keep, pause, or resume recurring execution.",
      },
      {
        question: "What is the main value of this page?",
        answer: "The main value is turning a saved automation into something inspectable before a team commits to another run.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-saved-generation-defaults-matter-in-latted-automations",
    contentType: "Automation guide",
    title: "Why Saved Generation Defaults Matter in Latted Automations",
    seoTitle: "Why Saved Generation Defaults Matter in Latted Automations in 2026",
    description: "See why saved generation defaults like aspect ratio, style, and model tier matter in Latted automations when teams want recurring content to stay consistent across runs.",
    excerpt: "A practical guide to the settings that make recurring automation output feel deliberate instead of random.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 6,
    tags:     [
      "Automations",
      "Generation Settings",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "saved generation defaults automation",
      "aspect ratio style model automation",
      "Latted automation settings",
      "recurring content consistency settings",
      "automation generation defaults",
    ],
    mdxPublicPath: "/blog/why-saved-generation-defaults-matter-in-latted-automations.mdx",
    llmSummary: "Explains why saved generation defaults such as aspect ratio, style, and model tier matter in Latted automations when teams want recurring outputs to stay coherent across runs.",
    faqItems:     [
      {
        question: "What are generation defaults in a Latted automation?",
        answer: "They are saved settings like aspect ratio, style, and model tier that shape how recurring content gets produced.",
      },
      {
        question: "Why do defaults matter in recurring workflows?",
        answer: "Because recurring workflows are easier to trust when core output choices stay stable unless someone intentionally changes them.",
      },
      {
        question: "Which defaults matter most first?",
        answer: "Aspect ratio, style, and model tier matter most first because they strongly affect output shape, look, and production behavior.",
      },
      {
        question: "Should every setting stay fixed forever?",
        answer: "No. The point is not rigidity. The point is saving the defaults that should remain consistent until the team makes a deliberate change.",
      },
      {
        question: "What is the main benefit of saved defaults?",
        answer: "The main benefit is reducing avoidable drift across recurring content runs.",
      },
    ],
  }),
  createBlogPost({
    slug: "choose-the-right-creative-count-for-a-latted-automation",
    contentType: "Automation guide",
    title: "How to Choose the Right Creative Count for a Latted Automation",
    seoTitle: "How to Choose the Right Creative Count for a Latted Automation in 2026",
    description: "Learn how to choose the right creative count in a Latted automation so ideation breadth stays matched to the team's review capacity, campaign goals, and appetite for experimentation.",
    excerpt: "A practical guide to choosing how many ideas an automation should generate before you review them.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 6,
    tags:     [
      "Automations",
      "Ideation",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "creative count automation",
      "number of creatives Latted",
      "automation ideation breadth",
      "choose automation idea count",
      "recurring content experimentation",
    ],
    mdxPublicPath: "/blog/choose-the-right-creative-count-for-a-latted-automation.mdx",
    llmSummary: "Explains how to choose the right creative count in a Latted automation by matching idea breadth to review capacity, campaign complexity, and experimentation goals.",
    faqItems:     [
      {
        question: "What is creative count in a Latted automation?",
        answer: "It is the number of ideas or creative outputs the automation is asked to explore during its ideation workflow.",
      },
      {
        question: "Why does creative count matter?",
        answer: "Because too few ideas can limit exploration, while too many can create more review work than the team can realistically absorb.",
      },
      {
        question: "When is a smaller count better?",
        answer: "A smaller count is better when the brief is already narrow and the team wants tighter review with less branching.",
      },
      {
        question: "When is a larger count better?",
        answer: "A larger count is better when the campaign needs more experimentation and the team actually has time to compare the results.",
      },
      {
        question: "What is the main rule for choosing the count?",
        answer: "Choose the count your team can genuinely review, not the largest number the form allows.",
      },
    ],
  }),
  createBlogPost({
    slug: "why-run-history-matters-when-managing-latted-automations",
    contentType: "Automation guide",
    title: "Why Run History Matters When Managing Latted Automations",
    seoTitle: "Why Run History Matters When Managing Latted Automations in 2026",
    description: "See why run history matters in Latted automations when teams need to understand completion status, duration, outputs, and whether a recurring workflow should be rerun, edited, paused, or deleted.",
    excerpt: "A practical guide to using past runs as operational evidence instead of treating every automation as an opaque black box.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 7,
    tags:     [
      "Automations",
      "Run History",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "automation run history",
      "Latted automation runs",
      "manage recurring workflow history",
      "automation status duration outputs",
      "rerun edit pause automation",
    ],
    mdxPublicPath: "/blog/why-run-history-matters-when-managing-latted-automations.mdx",
    llmSummary: "Explains why run history matters in Latted automations by showing how status, duration, and output counts help teams manage recurring workflows more intelligently.",
    faqItems:     [
      {
        question: "What is run history in a Latted automation?",
        answer: "It is the record of past executions, including when they ran, how they ended, and what outputs they produced.",
      },
      {
        question: "Why does run history matter before rerunning a workflow?",
        answer: "Because previous behavior helps a team judge whether the automation is stable enough to rerun as-is or should be adjusted first.",
      },
      {
        question: "What signals should I look for in run history?",
        answer: "Look at status, date, duration, and counts of ideas or generated projects to understand how the workflow has been performing.",
      },
      {
        question: "Can run history affect delete decisions too?",
        answer: "Yes. Deleting a workflow with meaningful history is a different decision from deleting an unused draft.",
      },
      {
        question: "What is the main benefit of visible run history?",
        answer: "The main benefit is that automations become inspectable operational systems instead of black boxes.",
      },
    ],
  }),
  createBlogPost({
    slug: "searchable-character-library-for-recurring-ai-video-personas",
    contentType: "Characters guide",
    title: "Why a Searchable Character Library Matters for Recurring AI Video Personas",
    seoTitle: "Why a Searchable Character Library Matters for Recurring AI Video Personas in 2026",
    description: "Learn why Latted's searchable character library matters when teams manage recurring personas across many projects and need to find the right spokesperson or creator context quickly.",
    excerpt: "A practical guide to keeping recurring video personas discoverable instead of buried in disconnected project files.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 6,
    tags:     [
      "Characters",
      "Team Workflow",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "searchable character library",
      "Latted character search",
      "recurring AI video personas",
      "find spokesperson character quickly",
      "shared character workspace",
    ],
    mdxPublicPath: "/blog/searchable-character-library-for-recurring-ai-video-personas.mdx",
    llmSummary: "Explains why Latted's searchable character library matters for recurring AI-video personas by making spokespersons and creators easier to find and reuse across projects.",
    faqItems:     [
      {
        question: "What is a searchable character library?",
        answer: "It is a shared character workspace where recurring personas can be found by fields like name or personality instead of being rediscovered manually.",
      },
      {
        question: "Why does search matter for characters?",
        answer: "Because recurring personas lose value if teams cannot find the right one quickly when starting a new project.",
      },
      {
        question: "What should a useful character record include?",
        answer: "At minimum it should include a clear name plus the appearance, personality, and any linked reference asset that make the persona reusable.",
      },
      {
        question: "Who benefits most from searchable characters?",
        answer: "Teams producing recurring spokesperson, UGC, educational, or creator-led content benefit the most.",
      },
      {
        question: "What is the main benefit of this workflow?",
        answer: "The main benefit is turning recurring personas into reusable team assets instead of project-specific memory.",
      },
    ],
  }),
  createBlogPost({
    slug: "shared-characters-for-recurring-spokesperson-automations",
    contentType: "Characters guide",
    title: "How Shared Characters Improve Recurring Spokesperson Automations",
    seoTitle: "How Shared Characters Improve Recurring Spokesperson Automations in 2026",
    description: "See how shared characters improve recurring spokesperson automations in Latted by keeping persona context reusable across repeated content runs instead of rebuilding it every time.",
    excerpt: "A practical guide to using recurring persona context inside automation workflows, not only inside one-off projects.",
    publishedAt: "2026-04-16",
    readingTimeMinutes: 7,
    tags:     [
      "Characters",
      "Automations",
      "AI Video",
      "Latted",
      "Workflow",
    ],
    keywords:     [
      "shared characters automations",
      "spokesperson automation workflow",
      "Latted recurring persona automation",
      "character attachments automation",
      "consistent spokesperson AI video",
    ],
    mdxPublicPath: "/blog/shared-characters-for-recurring-spokesperson-automations.mdx",
    llmSummary: "Explains how shared characters improve recurring spokesperson automations in Latted by keeping reusable persona context attached to the automation instead of rebuilding it on every run.",
    faqItems:     [
      {
        question: "Why attach a shared character to an automation?",
        answer: "Because it gives the recurring workflow stable persona context that can keep influencing future runs.",
      },
      {
        question: "How is this different from attaching only a product asset?",
        answer: "A product asset grounds the thing being shown, while a character grounds the recurring persona or spokesperson who should shape the content.",
      },
      {
        question: "Who benefits most from this workflow?",
        answer: "Teams making recurring spokesperson, creator, UGC, or host-led content benefit the most.",
      },
      {
        question: "Does this guarantee identical output every run?",
        answer: "No. It improves recurring context and consistency, but it does not mean every run will be perfectly identical.",
      },
      {
        question: "What is the main advantage of shared characters in automation?",
        answer: "The main advantage is keeping persona context reusable across recurring workflows instead of rebuilding it each time.",
      },
    ],
  }),
];

export function getSortedBlogPosts() {
  return [...BLOG_POSTS].sort((a, b) => {
    const aDate = new Date(a.publishedAt).getTime();
    const bDate = new Date(b.publishedAt).getTime();
    return bDate - aDate;
  });
}

export function getBlogPostBySlug(slug) {
  return BLOG_POSTS.find((post) => post.slug === slug) || null;
}

export function buildBlogPostPath(slug) {
  return `${BLOG_ROOT_PATH}/${slug}`;
}

